import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Phone, 
  Mail, 
  MapPin,
  Briefcase,
  BarChart3,
  Star,
  Sparkles,
  MessageCircle,
  MessageCirclePlus,
  ClipboardList,
  Clock,
  FileText,
  CheckCircle2,
  Circle,
  XCircle,
  Copy,
  Check,
  Send,
  Loader2,
  CalendarPlus,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Bell,
  RotateCcw,
  ExternalLink,
  Flame,
  Users,
  ChevronDown,
  Edit2,
  Search,
  MoreHorizontal,
  AlertCircle,
  RefreshCcw,
  History,
  Tag,
  Plus,
  Trophy,
  Presentation,
  Play
} from 'lucide-react';
import { getCountryFlag } from '../../../utils/countryFlags';
import { supabase } from '../../../supabaseClient';
import { actualizarFaseDesdePortal } from '../../../utils/actualizarFaseLead';
import CrearRespondModal from './CrearRespondModal';
import WhatsAppWebButton from './WhatsAppWebButton';
import { OPCIONES_LLAMADA, OPCIONES_MENSAJE, SIGLA_LLAMADA_DEFAULT, SIGLA_MENSAJE_DEFAULT, camposCanal, enriquecerComentarioConCanal, tieneSiglaCanal as haySiglaCanal, textoDetalleConCanal } from '../canalSiglas';
import { listPresentationRuns } from '../../../lib/presentation/runs';

/**
 * Calcula las horas restantes de la ventana de 24h de WhatsApp
 * @param {string} timestamp - timestamp_ultimo_mensaje_whatsapp
 * @returns {object} { horasRestantes: number, activo: boolean }
 */
const calcularTiempoWhatsApp = (timestamp) => {
  if (!timestamp) return { horasRestantes: null, activo: false };
  
  const ultimoMensaje = new Date(timestamp);
  const ahora = new Date();
  const diffMs = ahora - ultimoMensaje;
  const diffHoras = diffMs / (1000 * 60 * 60);
  
  if (diffHoras >= 24) {
    return { horasRestantes: 0, activo: false };
  }
  
  const horasRestantes = Math.ceil(24 - diffHoras);
  return { horasRestantes, activo: true };
};

/**
 * Determina si un usuario está conectado basado en su última conexión
 * @param {string} ultimaConexion - Timestamp de última conexión
 * @returns {boolean} true si conectado (menos de 2 minutos)
 */
const isUserOnline = (ultimaConexion) => {
  if (!ultimaConexion) return false;
  const lastConnection = new Date(ultimaConexion);
  const now = new Date();
  const diffMinutes = (now - lastConnection) / (1000 * 60);
  return diffMinutes < 2;
};

/**
 * Formatea la última conexión de manera legible
 * @param {string} ultimaConexion - Timestamp de última conexión
 * @returns {string} Texto formateado
 */
const formatLastConnection = (ultimaConexion) => {
  if (!ultimaConexion) return 'Sin datos';
  
  const lastConnection = new Date(ultimaConexion);
  const now = new Date();
  const diffMs = now - lastConnection;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  const remainingHours = diffHours % 24;
  const remainingMinutes = diffMinutes % 60;
  
  if (diffMinutes < 2) {
    return 'Conectado ahora';
  }
  
  // Formatear tiempo transcurrido
  let tiempoTranscurrido = '';
  if (diffDays >= 1) {
    tiempoTranscurrido = `${diffDays} día${diffDays > 1 ? 's' : ''}, ${remainingHours}h y ${remainingMinutes}min`;
  } else if (diffHours >= 1) {
    tiempoTranscurrido = `${diffHours}h y ${remainingMinutes}min`;
  } else {
    tiempoTranscurrido = `${diffMinutes}min`;
  }
  
  return `Inactivo. Última conexión hace ${tiempoTranscurrido}`;
};

/**
 * Mapa de colores para el stepper dinámico (por nombre o hex)
 */
const STEPPER_COLORS = {
  // Por nombre
  azul: { hex: '#1717AF', bgLight: 'bg-blue-100' },
  verde: { hex: '#22C55E', bgLight: 'bg-emerald-100' },
  verde_oscuro: { hex: '#15803D', bgLight: 'bg-green-100' },
  amarillo: { hex: '#EAB308', bgLight: 'bg-yellow-100' },
  naranja: { hex: '#F97316', bgLight: 'bg-orange-100' },
  rojo: { hex: '#EF4444', bgLight: 'bg-red-100' },
  gris: { hex: '#CBD5E1', bgLight: 'bg-slate-100' },
  // Por hex directo
  '#1717AF': { hex: '#1717AF', bgLight: 'bg-blue-100' },
  '#22C55E': { hex: '#22C55E', bgLight: 'bg-emerald-100' },
  '#15803D': { hex: '#15803D', bgLight: 'bg-green-100' },
  '#EAB308': { hex: '#EAB308', bgLight: 'bg-yellow-100' },
  '#F97316': { hex: '#F97316', bgLight: 'bg-orange-100' },
  '#EF4444': { hex: '#EF4444', bgLight: 'bg-red-100' },
  '#CBD5E1': { hex: '#CBD5E1', bgLight: 'bg-slate-100' }
};

/**
 * Obtiene los colores del stepper (soporta nombre o hex)
 */
const getStepperColors = (color) => {
  // Si está en el mapa, usar ese
  if (STEPPER_COLORS[color]) {
    return STEPPER_COLORS[color];
  }
  // Si es un hex que no está mapeado, usar directamente
  if (color?.startsWith('#')) {
    return { hex: color, bgLight: 'bg-slate-100' };
  }
  // Default: verde
  return STEPPER_COLORS.verde;
};

/**
 * Parsea el string de fases (ej: "339756287.P1, 340566951.P2")
 * y retorna un array de objetos con fase_id y label
 */
const parsearFases = (fasesString) => {
  if (!fasesString || fasesString.trim() === '') return [];
  
  return fasesString.split(',').map(item => {
    const [faseId, label] = item.trim().split('.');
    return { faseId: faseId.trim(), label: label?.trim() || '' };
  }).filter(item => item.faseId);
};

/**
 * Componente botón de WhatsApp con contador de ventana 24h
 * @param {object} lead - Datos del lead
 * @param {number} size - Tamaño del ícono
 * @param {function} onCrearRespond - Callback para abrir modal de crear en Respond
 */
const WhatsAppButtonSidebar = ({ lead, size = 20, onCrearRespond }) => {
  const [tiempoWhatsApp, setTiempoWhatsApp] = useState(() => 
    calcularTiempoWhatsApp(lead.timestamp_ultimo_mensaje_whatsapp)
  );

  useEffect(() => {
    // Recalcular cuando cambie el timestamp
    setTiempoWhatsApp(calcularTiempoWhatsApp(lead.timestamp_ultimo_mensaje_whatsapp));
    
    // Actualizar cada minuto
    const interval = setInterval(() => {
      setTiempoWhatsApp(calcularTiempoWhatsApp(lead.timestamp_ultimo_mensaje_whatsapp));
    }, 60000);
    
    return () => clearInterval(interval);
  }, [lead.timestamp_ultimo_mensaje_whatsapp]);

  const tieneUrl = !!lead.respond_io_url;
  const { horasRestantes, activo } = tiempoWhatsApp;
  const mostrarContador = horasRestantes !== null;

  // Si no tiene URL, mostrar botón para crear en Respond
  if (!tieneUrl) {
    return (
      <button
        onClick={() => onCrearRespond?.(lead)}
        className="relative p-2.5 rounded-xl transition-all duration-200 text-[#1717AF] bg-[#1717AF]/10 hover:bg-[#1717AF]/20"
        title="Crear en Respond.io"
      >
        <MessageCirclePlus size={size} />
      </button>
    );
  }

  return (
    <button
      onClick={() => window.open(lead.respond_io_url, '_blank')}
      className={`relative p-2.5 rounded-xl transition-all duration-200 ${
        activo
          ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
          : 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
      }`}
      title={
        activo 
          ? `Ventana activa: ${horasRestantes}h restantes` 
          : "Ventana de 24h expirada"
      }
    >
      <MessageCircle size={size} />
      
      {/* Badge contador */}
      {mostrarContador && (
        <span 
          className={`absolute -top-1 -right-1 min-w-[20px] h-[20px] flex items-center justify-center text-[10px] font-bold rounded-full px-1 ${
            activo 
              ? 'bg-emerald-500 text-white' 
              : 'bg-amber-200 text-amber-700'
          }`}
        >
          {horasRestantes}h
        </span>
      )}
    </button>
  );
};

// Configuración del stepper del funnel (fallback si no se cargan de BD)
const funnelStepsDefault = [
  { id: 'Validación de contacto', label: 'Validación de contacto', shortLabel: 'Validación' },
  { id: 'Perfilamiento', label: 'Perfilamiento', shortLabel: 'Perfil' },
  { id: 'Pitch agendado', label: 'Pitch agendado', shortLabel: 'Agendado' },
  { id: 'Pitch', label: 'Pitch', shortLabel: 'Pitch' },
  { id: 'Posible matrícula', label: 'Posible matrícula', shortLabel: 'Posible' },
  { id: 'Pendiente de pago', label: 'Pendiente de pago', shortLabel: 'Pago' },
  { id: '¡Nueva matrícula!', label: '¡Nueva matrícula!', shortLabel: '¡Matrícula!' },
  { id: 'Matrícula caída', label: 'Matrícula caída', shortLabel: 'Caída' },
];

// Función para determinar el estado de gestión del lead
const getGestionStatus = (lead) => {
  const now = new Date();
  
  if (lead.fase_nombre_pipefy === "¡Nueva matrícula!" || lead.etapa_funnel === "¡Nueva matrícula!") {
    return 'matriculado';
  }
  if (lead.fase_nombre_pipefy === "Matrícula caída" || lead.etapa_funnel === "Matrícula caída") {
    return 'caido';
  }

  const recordatorios = lead.recordatorios || [];
  if (recordatorios.length === 0) return 'sin-gestionar';
  
  const recordatoriosConFecha = recordatorios.filter(r => r.fecha_programada);
  if (recordatoriosConFecha.length === 0) return 'sin-gestionar';

  const tieneVigente = recordatoriosConFecha.some(r => new Date(r.fecha_programada) >= now);
  if (tieneVigente) return 'gestionado';

  const masReciente = recordatoriosConFecha
    .map(r => new Date(r.fecha_programada))
    .sort((a, b) => b - a)[0];
  
  const horasDiferencia = (now - masReciente) / (1000 * 60 * 60);
  return horasDiferencia > 48 ? 'atrasado' : 'sin-gestionar';
};

// Badges de estado
const statusBadges = {
  'gestionado': { label: 'Gestionado', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'atrasado': { label: 'Atrasado', bg: 'bg-rose-100', text: 'text-rose-700', dot: 'bg-rose-500' },
  'sin-gestionar': { label: 'Sin gestionar', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  'matriculado': { label: 'Matriculado', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'caido': { label: 'Matrícula caída', bg: 'bg-rose-100', text: 'text-rose-700', dot: 'bg-rose-500' },
};

// Tabs disponibles
const tabs = [
  { id: 'info', label: 'Info general', icon: ClipboardList },
  { id: 'seguimiento', label: 'Seguimiento', icon: BarChart3 },
  { id: 'recordatorio', label: 'Recordatorio', icon: Clock },
  { id: 'historial', label: 'Historial', icon: History },
  { id: 'tici', label: 'Emdi', icon: Sparkles },
];

// Fases que aplican para recordatorios automáticos TICI
const FASES_RECORDATORIO_AUTOMATICO = ['340832804', '339756097', '341769991'];

// Fases donde se muestra el botón de agendar
const FASES_LISTO_AGENDAR = ['339756287', '340855086'];

// Fases donde se muestra el banner de "Pitch agendado"
const FASES_PITCH_AGENDADO = ['339756098', '340566951', '340859031'];

const RECORDATORIOS_PER_PAGE = 50;

function WhatsAppGlyph({ size = 16, className = '' }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function CanalSiglaSelect({ icon, value, options, placeholder, open, onToggle, onChange, accentClass }) {
  return (
    <div className="flex-1 relative sigla-dropdown min-w-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF] transition-all duration-200 flex items-center gap-2"
      >
        <span className={`flex-shrink-0 ${value ? accentClass : 'text-slate-400'}`}>{icon}</span>
        <span className={`flex-1 text-left truncate ${value ? 'text-slate-700 font-medium' : 'text-slate-400'}`}>
          {value || placeholder}
        </span>
        {value ? (
          <X
            size={14}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          />
        ) : (
          <ChevronDown size={14} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${
                opt === value ? 'bg-[#1717AF]/5 text-[#1717AF] font-medium' : 'text-slate-700'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Opciones para los campos editables con select
const OPCIONES_OCUPACION = ['Estudiante', 'Médico General', 'Residente', 'Especialista'];

const OPCIONES_MOTIVACION = [
  'Quiero hacer una especialización en el extranjero',
  'Quiero entender artículos cientificos',
  'Quiero entender las guías de práctica clínica',
  'Tengo una ponencia en un simposio internacional',
  'Otro'
];

const OPCIONES_NIVEL_INGLES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const OPCIONES_ANO_RESIDENCIA = ['1', '2', '3', '4', '5', '6', '7'];

const OPCIONES_COMO_ADQUIRIO_INGLES = [
  'En un curso de inglés (instituto o academia)',
  'En un colegio bilingüe',
  'Viví o estudié en el extranjero',
  'Aprendizaje autodidacta (por mi cuenta, apps, internet, etc.)',
  'A través de mi trabajo o práctica profesional',
  'Combinación de varias fuentes',
  'Mi pareja habla inglés',
  'Otro'
];

const OPCIONES_CUANDO_EMPEZAR = ['En este momento', 'El próximo mes', 'El próximo semestre'];

const OPCIONES_ESPECIALIDAD = [
  'Anestesiología y Reanimación', 'Alergología', 'Anatomía Patológica', 'Cardiología',
  'Cirugía General', 'Cirugía Cardiotorácica', 'Cirugía Colorrectal', 'Cirugía de Tórax',
  'Cirugía Oncológica', 'Cirugía Pediátrica', 'Cirugía Plástica', 'Cirugía Robótica',
  'Cirugía Vascular', 'Coloproctología', 'Dermatología', 'Endocrinología y Nutrición',
  'Epidemiología', 'Gastroenterología', 'Genética Médica', 'Geriatría', 'Gerontología Médica',
  'Gestión de Salud (Administrativo)', 'Ginecología y Obstetricia', 'Hematología', 'Imagenología',
  'Infectología', 'Inmunología Clínica', 'Laboratorio Clínico', 'Medicina de Emergencias',
  'Medicina Familiar', 'Medicina Física y Rehabilitación', 'Medicina Hiperbárica',
  'Medicina Intensiva', 'Medicina Interna', 'Medicina Legal y Forense', 'Medicina Nuclear',
  'Medicina Paliativa', 'Medicina Preventiva y Salud Pública', 'Medicina del Trabajo',
  'Nefrología', 'Neumología', 'Neurocirugía', 'Neurología', 'Oftalmología', 'Oncología Médica',
  'Otorrinolaringología', 'Pediatría', 'Psiquiatría', 'Radiología', 'Reumatología',
  'Terapia del Dolor', 'Traumatología y Ortopedia', 'Urología'
];

const OPCIONES_PLAN_PAGO = ['1', '2', '4', '6', '8', '10', '12'];

const OPCIONES_CONSULTA_DECISION = [
  'Si, debe consultar con alguien más',
  'No consulta con nadie más'
];

/**
 * Parsea las secciones del formulario
 * Formato: "Contacto (1) | Ocupación (2) | Motivación (3)"
 * Retorna: [{ nombre: "Contacto", orden: 1 }, ...]
 */
const parseSecciones = (seccionesStr) => {
  if (!seccionesStr) return [];
  
  // Usar | como separador para evitar conflictos con comas en el texto
  return seccionesStr.split('|').map(sec => {
    const match = sec.trim().match(/^(.+?)\s*\((\d+)\)$/);
    if (match) {
      return { nombre: match[1].trim(), orden: parseInt(match[2]) };
    }
    return { nombre: sec.trim(), orden: 999 };
  }).sort((a, b) => a.orden - b.orden);
};

/**
 * Parsea el orden del campo (incluye sección si tiene_secciones = true)
 * Formato: "SECCIÓN (orden)" o solo "orden"
 * Retorna: { seccion: "SECCIÓN" | null, orden: number }
 */
const parseOrdenField = (ordenStr) => {
  if (!ordenStr) return { seccion: null, orden: 999 };
  
  // Intentar parsear formato "SECCIÓN (orden)"
  const match = ordenStr.toString().trim().match(/^(.+?)\s*\((\d+)\)$/);
  if (match) {
    return { seccion: match[1].trim(), orden: parseInt(match[2]) };
  }
  
  // Si es solo número
  const numMatch = ordenStr.toString().match(/^\d+$/);
  if (numMatch) {
    return { seccion: null, orden: parseInt(ordenStr) };
  }
  
  return { seccion: null, orden: 999 };
};

/**
 * Parsea las opciones del campo select
 * Formato: "Opción A (1) | Opción B (2) | Opción C (3)"
 * Usa | como separador para evitar conflictos con comas en el texto
 * Retorna: [{ value: "Opción A", orden: 1 }, ...]
 */
const parseOpcionesField = (opcionesStr) => {
  if (!opcionesStr) return [];
  
  return opcionesStr.split('|').map(opt => {
    const matchGrupo = opt.trim().match(/^(.+?)\s*\((.+?)-(\d+)\)$/);
    if (matchGrupo) {
      return { value: matchGrupo[1].trim(), grupo: matchGrupo[2].trim(), orden: parseInt(matchGrupo[3]) };
    }
    const match = opt.trim().match(/^(.+?)\s*\((\d+)\)$/);
    if (match) {
      return { value: match[1].trim(), grupo: null, orden: parseInt(match[2]) };
    }
    return { value: opt.trim(), grupo: null, orden: 999 };
  }).sort((a, b) => a.orden - b.orden);
};

/**
 * Parsea la condición de dependencia
 * Formato: "nombre_campo | valor"
 * Usa | como separador para evitar conflictos con comas
 */
const parseDependeDeField = (dependeDeStr) => {
  if (!dependeDeStr) return null;
  const parts = dependeDeStr.split('|').map(s => s.trim());
  if (parts.length >= 2) {
    return { fieldName: parts[0], valor: parts[1] };
  }
  if (parts.length === 1 && parts[0]) {
    return { fieldName: parts[0], valor: null };
  }
  return null;
};

// =============================================================================
// PITCH (sub-tab)
// =============================================================================
// Id del formulario "Pitch" en formularios_creacion_leads
const PITCH_FORMULARIO_ID = '88c92ad1-899e-4c7d-8e2e-42d523fe55c7';

// Normaliza nombres de campo para hacer matching robusto sin acentos ni signos
const normalizePitchFieldName = (name) =>
  (name || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[¿?¡!]/g, '')
    .trim();

// Mapeo nombre del campo (normalizado) → columna en pitches_resultados
const PITCH_FIELD_NAME_TO_COLUMN = {
  'reprogramo': 'rescheduled',
  'en que etapa se quedo': 'pitch_stage',
  'resultado del pitch': 'pitch_result',
  'asistio al pitch': 'attended',
  'como agendaste el pitch': 'cierre_pitch',
};

// Columnas adicionales de pitches_resultados que pueden venir de campos
// dinámicos agregados desde la config (fields_formulario_creacion_leads) y que
// se resuelven por campo_origen ("tabla,columna"). Se limita a columnas que
// existen en pitches_resultados para no romper el insert/update.
const PITCH_EXTRA_COLUMNS = ['motivo_matricula', 'calificacion_setter', 'cierre_pitch'];

// Columnas que, además de guardarse en pitches_resultados, se reflejan en la
// tabla leads (el lead conserva el último valor registrado en el pitch).
const PITCH_COLUMNS_SYNC_LEADS = ['motivo_matricula'];

const getPitchColumnForField = (field) => {
  const mapped = PITCH_FIELD_NAME_TO_COLUMN[normalizePitchFieldName(field?.nombre)];
  if (mapped) return mapped;
  // Campo "¿El lead estuvo bien calificado por el Setter?": se persiste en la
  // columna calificacion_setter (independiente de su campo_origen en config).
  if (isSetterCalificadoPitchField(field)) return 'calificacion_setter';
  // Campo "¿Cómo agendaste el Pitch?": se persiste en cierre_pitch aunque
  // campo_origen no esté configurado en la fila de config.
  if (isCierrePitchField(field)) return 'cierre_pitch';
  // Campo dinámico agregado por config: la columna sale de campo_origen.
  if (field?.campo_origen) {
    const parts = String(field.campo_origen).split(',').map(s => s.trim());
    const col = parts[1] || parts[0];
    if (col && PITCH_EXTRA_COLUMNS.includes(col)) return col;
  }
  return null;
};

// Campo "¿El lead estuvo bien calificado por el Setter?" — visible solo si
// leads.setter === TRUE (fallback si depende_de no está configurado en BD).
const isSetterCalificadoPitchField = (field) => {
  const n = normalizePitchFieldName(field?.nombre);
  return n.includes('calificado por el setter') || n.includes('bien calificado por el setter');
};

const isCierrePitchField = (field) => {
  const n = normalizePitchFieldName(field?.nombre);
  return n.includes('como agendaste') || n.includes('cierre del pitch') || n === 'cierre pitch';
};

const getLeadPropertyValue = (prop, leadRow, localRow) => {
  const raw = localRow?.[prop] ?? leadRow?.[prop];
  if (raw === null || raw === undefined) return '';
  return String(raw).trim();
};

const isTruthyLeadFlag = (value) => String(value).trim().toUpperCase() === 'TRUE';

// Campo especial (hardcodeado, no vive en fields_formulario_creacion_leads):
// "Especifica por qué no se matriculó". Sus opciones salen de config_categorias
// y su valor se guarda dentro de los values del form bajo esta clave sintética.
const MOTIVO_FIELD_KEY = '__motivo_no_matricula';
// Claves sintéticas adicionales del bloque de motivo (viven en los values del
// form para resetearse junto con todo lo demás).
const MOTIVO_DETALLE_KEY = '__motivo_detalle';   // detalle (Seguimiento) / observación (Recordatorio)
const MOTIVO_TIPO_KEY = '__motivo_tipo';         // 'seguimiento' | 'recordatorio'
const MOTIVO_FECHA_KEY = '__motivo_fecha';       // 'YYYY-MM-DD' (solo Recordatorio)
const MOTIVO_HORA_KEY = '__motivo_hora';         // '01'..'12'
const MOTIVO_MINUTO_KEY = '__motivo_minuto';     // '00'..'59'
const MOTIVO_PERIODO_KEY = '__motivo_periodo';   // 'AM' | 'PM'

// Limpia (mutando) todas las claves sintéticas del motivo en un objeto values.
const clearMotivoKeys = (obj) => {
  obj[MOTIVO_FIELD_KEY] = '';
  obj[MOTIVO_DETALLE_KEY] = '';
  obj[MOTIVO_TIPO_KEY] = 'seguimiento';
  obj[MOTIVO_FECHA_KEY] = '';
  obj[MOTIVO_HORA_KEY] = '';
  obj[MOTIVO_MINUTO_KEY] = '';
  obj[MOTIVO_PERIODO_KEY] = '';
};
// Valores de pitch_result que, por sí solos, muestran el campo de motivo.
// "Reprobado" NO está aquí a propósito: se muestra vía pitch_stage (el usuario
// primero elige la etapa y recién ahí aparece el motivo). "Matrícula" nunca.
const PITCH_RESULT_REQUIERE_MOTIVO = [
  'Pago pendiente',
  'Posible matrícula',
  'Interés futuro',
  'No matrícula',
];

// Ordena categorías agrupándolas por categoria_padre (alfabético) y, dentro
// de cada grupo, por categoria (alfabético). Así quedan contiguas por grupo.
const sortCategorias = (arr) =>
  [...(arr || [])].sort((a, b) => {
    const padre = (a.categoria_padre || '').localeCompare(
      b.categoria_padre || '', 'es', { sensitivity: 'base' }
    );
    if (padre !== 0) return padre;
    return (a.categoria || '').localeCompare(
      b.categoria || '', 'es', { sensitivity: 'base' }
    );
  });

const LeadSidebar = ({ lead: leadProp, isOpen, onClose, initialTab = 'info', etapasFunnel = { etapas: [], grupos: [] }, onMarcarNoRevisado, onRefreshData, comerciales = [], puedeVerTodos = false, configTags = {} }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [subActiveTab, setSubActiveTab] = useState('informacion'); // 'informacion' | 'pitch' | 'resumen-ia'
  const [pitchSectionTab, setPitchSectionTab] = useState('meet'); // 'meet' | 'presentacion'
  // Historial de presentaciones generadas.
  const [presentationRuns, setPresentationRuns] = useState([]);
  const [loadingPresentationRuns, setLoadingPresentationRuns] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  // Estado local del lead para actualizaciones en tiempo real
  const [leadLocal, setLeadLocal] = useState(null);
  const lead = leadLocal || leadProp;
  
  // Sincronizar lead local cuando cambia el prop
  useEffect(() => {
    setLeadLocal(leadProp);
  }, [leadProp?.card_id]);
  
  // Función para refrescar datos del lead desde la base de datos
  const refreshLeadData = useCallback(async () => {
    if (!leadProp?.card_id) return;
    
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('card_id', leadProp.card_id)
        .single();
      
      if (!error && data) {
        setLeadLocal(data);
      }
    } catch (err) {
      console.error('Error refrescando lead:', err);
    }
  }, [leadProp?.card_id]);
  
  // Usar etapas de la BD o fallback al default
  const etapasFromProp = etapasFunnel?.etapas || [];
  const funnelSteps = etapasFromProp.length > 0 ? etapasFromProp : funnelStepsDefault;
  
  // Estados para recordatorios
  const [recordatorios, setRecordatorios] = useState([]);
  const [loadingRecordatorios, setLoadingRecordatorios] = useState(false);
  const [hasMoreRecordatorios, setHasMoreRecordatorios] = useState(true);
  const [recordatoriosPage, setRecordatoriosPage] = useState(0);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  
  // Estados para categorías de seguimiento
  const [categorias, setCategorias] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('Otro');
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [categoriaDropdownOpen, setCategoriaDropdownOpen] = useState(false);
  const [categoriaBusqueda, setCategoriaBusqueda] = useState('');
  const [siglaLlamada, setSiglaLlamada] = useState(SIGLA_LLAMADA_DEFAULT);
  const [siglaMensaje, setSiglaMensaje] = useState(SIGLA_MENSAJE_DEFAULT);
  const [siglaDropdownOpen, setSiglaDropdownOpen] = useState(null);
  
  // Filtros de comentarios
  const [filtroEtapa, setFiltroEtapa] = useState(null);
  const [filtroFase, setFiltroFase] = useState(null);
  
  // Estados para el tab Recordatorios
  const [mesActual, setMesActual] = useState(new Date());
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [horaInput, setHoraInput] = useState('09');
  const [minutoInput, setMinutoInput] = useState('00');
  const [periodoInput, setPeriodoInput] = useState('AM');
  const [textoRecordatorio, setTextoRecordatorio] = useState('');
  const [programandoRecordatorio, setProgramandoRecordatorio] = useState(false);
  const [recordatoriosDelLead, setRecordatoriosDelLead] = useState([]);
  const [loadingRecordatoriosCalendario, setLoadingRecordatoriosCalendario] = useState(false);
  const [errorRecordatorio, setErrorRecordatorio] = useState(null);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [fechaRecordatorioCreado, setFechaRecordatorioCreado] = useState(null);
  const [subTabRecordatorio, setSubTabRecordatorio] = useState('programar'); // 'programar' | 'historial'
  
  // Estados para historial de recordatorios
  const [historialRecordatorios, setHistorialRecordatorios] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [historialPage, setHistorialPage] = useState(0);
  const [totalHistorial, setTotalHistorial] = useState(0);
  const [cancelandoRecordatorio, setCancelandoRecordatorio] = useState(null);
  const [confirmarCancelacion, setConfirmarCancelacion] = useState(null); // Guarda el recordatorio a cancelar
  const [recordatorioActivo, setRecordatorioActivo] = useState(null); // Recordatorio activo actual
  const [loadingRecordatorioActivo, setLoadingRecordatorioActivo] = useState(false);
  
  // Estados para booking
  const [urlBooking, setUrlBooking] = useState(null);
  const [loadingUrls, setLoadingUrls] = useState(false);
  
  // Estados para lead HOT 🔥
  const [isHot, setIsHot] = useState(false);
  
  // Estados para modal de Crear en Respond
  const [crearRespondModalOpen, setCrearRespondModalOpen] = useState(false);
  
  // Handler para abrir modal de Crear en Respond
  const handleCrearRespond = () => {
    setCrearRespondModalOpen(true);
  };
  
  // Handler para éxito al crear en Respond
  const handleRespondSuccess = (respondIoUrl, telefono) => {
    setCrearRespondModalOpen(false);
    // Refrescar datos para actualizar el UI
    onRefreshData?.();
  };
  const [togglingHot, setTogglingHot] = useState(false);
  
  // Estados para reasignación de comercial 👥
  const [comercialDropdownOpen, setComercialDropdownOpen] = useState(false);
  const [comercialSeleccionado, setComercialSeleccionado] = useState(null);
  const [mostrarModalReasignar, setMostrarModalReasignar] = useState(false);
  const [reasignando, setReasignando] = useState(false);
  
  // Estados para edición de campos
  const [editingField, setEditingField] = useState(null); // Campo actualmente en edición
  const [editValue, setEditValue] = useState(''); // Valor temporal durante edición
  const [savingField, setSavingField] = useState(null); // Campo que se está guardando
  const [searchSelectQuery, setSearchSelectQuery] = useState(''); // Búsqueda en selects
  const [modalTextoCompleto, setModalTextoCompleto] = useState(null); // Modal para ver texto completo
  const [localLeadData, setLocalLeadData] = useState({}); // Datos locales para actualización inmediata
  
  // Estados para resumen IA
  const [generandoResumen, setGenerandoResumen] = useState(false);
  const [resumenIA, setResumenIA] = useState(null);
  const [resumenIAFecha, setResumenIAFecha] = useState(null);
  
  // Estados para cambio de fase
  const [faseDropdownOpen, setFaseDropdownOpen] = useState(false);
  const [cambiandoFase, setCambiandoFase] = useState(false);
  const [faseLocal, setFaseLocal] = useState(null); // Fase local para actualización inmediata
  const [toastMessage, setToastMessage] = useState(null); // Toast de confirmación
  
  // Estados para stepper dinámico
  const [configStepper, setConfigStepper] = useState(null);
  const [stepperSteps, setStepperSteps] = useState([]);
  const [stepperColor, setStepperColor] = useState('verde');
  
  // Estados para historial de fases
  const [historialFases, setHistorialFases] = useState([]);
  const [loadingHistorialFases, setLoadingHistorialFases] = useState(false);
  const [historialFasesTab, setHistorialFasesTab] = useState('Gestión'); // 'Gestión' | 'Finalizado'
  const [historialFasesExpanded, setHistorialFasesExpanded] = useState({}); // {nombreFase: boolean}
  const [historialFasesPaginacion, setHistorialFasesPaginacion] = useState({}); // {nombreFase: indice}
  
  // Estados para formulario dinámico de Info General
  const [infoGeneralFormulario, setInfoGeneralFormulario] = useState(null);
  const [infoGeneralFields, setInfoGeneralFields] = useState([]);
  const [infoGeneralSecciones, setInfoGeneralSecciones] = useState([]);
  const [loadingInfoGeneral, setLoadingInfoGeneral] = useState(false);

  // Estados para sub-tab Pitch (formulario nuevo + lista de resultados)
  const [pitchFormulario, setPitchFormulario] = useState(null);
  const [pitchFields, setPitchFields] = useState([]);
  const [loadingPitchForm, setLoadingPitchForm] = useState(false);
  const [pitchFormValues, setPitchFormValues] = useState({}); // { fieldId: value } para nueva entrada
  const [pitchSelectOpenId, setPitchSelectOpenId] = useState(null); // dropdown abierto en form nuevo
  const [pitchResultados, setPitchResultados] = useState([]);
  const [loadingPitchResultados, setLoadingPitchResultados] = useState(false);
  const [savingPitch, setSavingPitch] = useState(false);
  // Info post-creación (mensaje extendido tras registrar resultado y mover
  // de fase). Se limpia al cambiar de lead. Solo se setea cuando todo el
  // flujo termina con éxito (insert + webhook + polling).
  const [pitchCompletionInfo, setPitchCompletionInfo] = useState(null);
  const [editingPitchId, setEditingPitchId] = useState(null);
  const [editingPitchValues, setEditingPitchValues] = useState({}); // { fieldId: value }
  const [editingPitchSelectOpenId, setEditingPitchSelectOpenId] = useState(null);
  const [savingEditPitchId, setSavingEditPitchId] = useState(null);
  // UI del dropdown del campo "Especifica por qué no se matriculó" (motivo).
  // El valor seleccionado vive dentro de pitchFormValues/editingPitchValues
  // bajo MOTIVO_FIELD_KEY; estos estados controlan apertura y búsqueda.
  const [motivoNewOpen, setMotivoNewOpen] = useState(false);
  const [motivoNewBusqueda, setMotivoNewBusqueda] = useState('');
  const [motivoEditOpen, setMotivoEditOpen] = useState(false);
  const [motivoEditBusqueda, setMotivoEditBusqueda] = useState('');
  
  // Estados para TICI (Recordatorios automáticos)
  const [subTabTici, setSubTabTici] = useState('programado'); // 'programado' | 'historial'
  const [configRecordatorioAuto, setConfigRecordatorioAuto] = useState(null);
  const [loadingConfigTici, setLoadingConfigTici] = useState(false);
  const [posponiendo, setPosponiendo] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [tiempoRestante, setTiempoRestante] = useState({ dias: 0, horas: 0, minutos: 0, segundos: 0 });
  
  // Estados para historial TICI
  const [historialTici, setHistorialTici] = useState({}); // { fase_id: [records] }
  const [loadingHistorialTici, setLoadingHistorialTici] = useState(false);
  const [acordeonTiciAbierto, setAcordeonTiciAbierto] = useState(null); // fase_id abierta
  const [indiceTiciPorFase, setIndiceTiciPorFase] = useState({}); // { fase_id: indice_actual }
  const [previewHistorialTici, setPreviewHistorialTici] = useState(null); // registro para previsualizar
  const [infoGeneralDropdownOpen, setInfoGeneralDropdownOpen] = useState(null);
  const [infoGeneralSearchQuery, setInfoGeneralSearchQuery] = useState('');
  const infoGeneralSearchRef = useRef(null);
  
  // Determinar si mostrar botón de agendar basado en la fase del lead
  const mostrarBotonAgendar = lead?.fase_id_pipefy && FASES_LISTO_AGENDAR.includes(String(lead.fase_id_pipefy));
  
  // Determinar si mostrar banner de pitch agendado
  const mostrarPitchAgendado = lead?.fase_id_pipefy && FASES_PITCH_AGENDADO.includes(String(lead.fase_id_pipefy));
  
  // Parsear fecha_pitch sin conversión de zona horaria (hora Colombia en el string).
  const parseFechaPitchParts = (fechaPitch) => {
    if (!fechaPitch) return null;
    const match = fechaPitch.match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return null;
    const [, year, month, day, hour, minute] = match;
    return {
      year,
      month: parseInt(month, 10),
      day: parseInt(day, 10),
      hour: parseInt(hour, 10),
      minute,
    };
  };

  const formatHoraPitchAmPm = (hour, minute) => {
    const ampm = hour >= 12 ? 'p. m.' : 'a. m.';
    const h = hour % 12 || 12;
    return `${h}:${minute} ${ampm}`;
  };

  const MESES_PITCH = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  // Banner superior: incluye día de la semana.
  const formatFechaPitch = (fechaPitch) => {
    const parts = parseFechaPitchParts(fechaPitch);
    if (!parts) return 'fecha por confirmar';

    const fecha = new Date(parts.year, parts.month - 1, parts.day);
    const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

    return `${dias[fecha.getDay()]}, ${parts.day} de ${MESES_PITCH[parts.month - 1]}, ${formatHoraPitchAmPm(parts.hour, parts.minute)}`;
  };

  // Tarjeta del tab Pitch: "29 de junio de 2026 a las 3:00 p. m."
  const formatFechaPitchTarjeta = (fechaPitch) => {
    const parts = parseFechaPitchParts(fechaPitch);
    if (!parts) return null;
    return `${parts.day} de ${MESES_PITCH[parts.month - 1]} de ${parts.year} a las ${formatHoraPitchAmPm(parts.hour, parts.minute)}`;
  };
  
  const recordatoriosContainerRef = useRef(null);
  const seguimientoInputRef = useRef(null);
  const sidebarContentRef = useRef(null);
  
  // Refs para inputs de búsqueda en dropdowns
  const categoriaSearchRef = useRef(null);
  const categoriaRecordatorioSearchRef = useRef(null);
  
  const userEmail = localStorage.getItem('user_email');
  const userName = localStorage.getItem('user_name') || 'Usuario';

  // Función para copiar al portapapeles
  const handleCopy = async (text, field) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  // Cargar resumen IA guardado cuando cambia el lead
  useEffect(() => {
    if (lead?.resumen_ia) {
      setResumenIA(lead.resumen_ia);
      setResumenIAFecha(lead.resumen_ia_fecha || null);
    } else {
      setResumenIA(null);
      setResumenIAFecha(null);
    }
  }, [lead?.card_id, lead?.resumen_ia, lead?.resumen_ia_fecha]);

  // Inicializar estado HOT cuando cambia el lead
  useEffect(() => {
    setIsHot(lead?.is_hot || false);
  }, [lead?.card_id, lead?.is_hot]);

  // Inicializar fase local cuando cambia el lead
  useEffect(() => {
    setFaseLocal(lead?.fase_nombre_pipefy || null);
  }, [lead?.card_id, lead?.fase_nombre_pipefy]);

  // Sincronizar datos locales cuando cambia el lead
  useEffect(() => {
    if (lead) {
      setLocalLeadData({});
      // Cancelar cualquier edición pendiente al cambiar de lead
      setEditingField(null);
      setEditValue('');
      setSavingField(null);
      setSearchSelectQuery('');
    }
  }, [lead?.card_id]);

  // Cargar formulario dinámico de Info General
  useEffect(() => {
    const fetchInfoGeneralFormulario = async () => {
      if (!isOpen) return;
      
      setLoadingInfoGeneral(true);
      try {
        // Buscar el formulario "Info General" activo
        const { data: formData, error: formError } = await supabase
          .from('formularios_creacion_leads')
          .select('*')
          .eq('nombre', 'Info General')
          .eq('estado', 'activo')
          .single();
        
        if (formError || !formData) {
          console.log('No se encontró formulario Info General dinámico, usando versión estática');
          setInfoGeneralFormulario(null);
          setLoadingInfoGeneral(false);
          return;
        }
        
        setInfoGeneralFormulario(formData);
        
        // Parsear secciones si tiene_secciones es true
        if (formData.tiene_secciones && formData.secciones) {
          setInfoGeneralSecciones(parseSecciones(formData.secciones));
        } else {
          setInfoGeneralSecciones([]);
        }
        
        // Cargar campos del formulario
        const { data: fieldsData, error: fieldsError } = await supabase
          .from('fields_formularios_relacion')
          .select(`
            orden,
            field:fields_formulario_creacion_leads (
              id,
              nombre,
              tipo,
              opciones,
              obligatorio,
              tooltip,
              dinamico,
              depende_de,
              estado,
              campo_origen
            )
          `)
          .eq('formulario_id', formData.id);
        
        if (fieldsError) throw fieldsError;
        
        // Transformar y ordenar campos
        const fields = (fieldsData || [])
          .filter(item => item.field && item.field.estado === 'activo')
          .map(item => {
            const ordenInfo = parseOrdenField(item.orden);
            return {
              ...item.field,
              ordenRaw: item.orden,
              seccion: ordenInfo.seccion,
              orden: ordenInfo.orden
            };
          })
          .sort((a, b) => a.orden - b.orden);
        
        setInfoGeneralFields(fields);
        
      } catch (error) {
        console.error('Error cargando formulario Info General:', error);
        setInfoGeneralFormulario(null);
      } finally {
        setLoadingInfoGeneral(false);
      }
    };
    
    fetchInfoGeneralFormulario();
  }, [isOpen, lead?.card_id]);

  // ===========================================================================
  // PITCH - sub-tab
  // ===========================================================================

  // Cargar el formulario "Pitch" y sus campos (una sola vez al abrir el sidebar)
  useEffect(() => {
    const fetchPitchFormulario = async () => {
      if (!isOpen) return;

      setLoadingPitchForm(true);
      try {
        const { data: formData, error: formError } = await supabase
          .from('formularios_creacion_leads')
          .select('*')
          .eq('id', PITCH_FORMULARIO_ID)
          .single();

        if (formError || !formData) {
          console.warn('[Pitch] No se encontró formulario Pitch:', formError);
          setPitchFormulario(null);
          setPitchFields([]);
          return;
        }

        setPitchFormulario(formData);

        const { data: fieldsData, error: fieldsError } = await supabase
          .from('fields_formularios_relacion')
          .select(`
            orden,
            field:fields_formulario_creacion_leads (
              id,
              nombre,
              tipo,
              opciones,
              obligatorio,
              tooltip,
              dinamico,
              depende_de,
              estado,
              campo_origen
            )
          `)
          .eq('formulario_id', formData.id);

        if (fieldsError) throw fieldsError;

        const fields = (fieldsData || [])
          .filter(item => item.field && item.field.estado === 'activo')
          .map(item => {
            const ordenInfo = parseOrdenField(item.orden);
            return {
              ...item.field,
              ordenRaw: item.orden,
              seccion: ordenInfo.seccion,
              orden: ordenInfo.orden,
            };
          })
          .sort((a, b) => a.orden - b.orden);

        setPitchFields(fields);
      } catch (e) {
        console.error('[Pitch] Error cargando formulario Pitch:', e);
        setPitchFormulario(null);
        setPitchFields([]);
      } finally {
        setLoadingPitchForm(false);
      }
    };

    fetchPitchFormulario();
  }, [isOpen]);

  // Cargar resultados de pitches del lead actual
  const fetchPitchResultados = useCallback(async () => {
    if (!lead?.card_id) return;
    setLoadingPitchResultados(true);
    try {
      const { data, error } = await supabase
        .from('pitches_resultados')
        .select('*')
        .eq('card_id', lead.card_id)
        .order('created_at', { ascending: true }); // ascendente para numerar Pitch 1, 2, 3...
      if (error) throw error;
      setPitchResultados(data || []);
    } catch (e) {
      console.error('[Pitch] Error cargando resultados:', e);
      setPitchResultados([]);
    } finally {
      setLoadingPitchResultados(false);
    }
  }, [lead?.card_id]);

  useEffect(() => {
    if (isOpen && activeTab === 'info' && subActiveTab === 'pitch' && lead?.card_id) {
      fetchPitchResultados();
    }
  }, [isOpen, activeTab, subActiveTab, lead?.card_id, fetchPitchResultados]);

  // Historial de presentaciones generadas (tab Presentación)
  useEffect(() => {
    let active = true;
    if (
      !isOpen ||
      activeTab !== 'info' ||
      subActiveTab !== 'pitch' ||
      pitchSectionTab !== 'presentacion' ||
      !lead?.card_id
    ) {
      return undefined;
    }

    setLoadingPresentationRuns(true);
    listPresentationRuns(lead.card_id)
      .then((rows) => {
        if (active) setPresentationRuns(rows);
      })
      .catch((err) => {
        console.warn('[Presentación] No se pudo cargar historial:', err?.message || err);
        if (active) setPresentationRuns([]);
      })
      .finally(() => {
        if (active) setLoadingPresentationRuns(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, activeTab, subActiveTab, pitchSectionTab, lead?.card_id]);

  // Reset del formulario, edición y sub-tab cuando cambia el lead.
  // Importante: el sub-tab vuelve a 'informacion' para que al abrir otro lead
  // siempre arranque ahí, no donde el usuario lo dejó en el lead anterior.
  useEffect(() => {
    setSubActiveTab('informacion');
    setPitchSectionTab('meet');
    setPresentationRuns([]);
    setPitchFormValues({});
    setPitchSelectOpenId(null);
    setEditingPitchId(null);
    setEditingPitchValues({});
    setEditingPitchSelectOpenId(null);
    setPitchCompletionInfo(null);
    setMotivoNewOpen(false);
    setMotivoNewBusqueda('');
    setMotivoEditOpen(false);
    setMotivoEditBusqueda('');
  }, [lead?.card_id]);

  // Determina si un campo del Pitch debe mostrarse según su dependencia
  // (depende_de). Soporta:
  //   - Campo del form: "Nombre campo padre | valor"
  //   - Propiedad del lead: "leads.setter | TRUE"
  // Usa los valores LOCALES (form nuevo o edición), no los de leads, salvo en
  // dependencias explícitas sobre columnas de leads.
  const shouldShowPitchField = (field, values) => {
    // Regla fija: calificación del Setter solo si:
    //   1) leads.setter === TRUE, y
    //   2) "¿Asistió al Pitch?" === 'Si'.
    if (isSetterCalificadoPitchField(field)) {
      const setterTrue = isTruthyLeadFlag(getLeadPropertyValue('setter', lead, localLeadData));
      const asistio = getPitchValueByColumn(values, 'attended') === 'Si';
      return setterTrue && asistio;
    }

    if (!field?.depende_de) return true;
    const dep = parseDependeDeField(field.depende_de);
    if (!dep) return true;

    // Dependencia sobre columna de leads (ej. leads.setter | TRUE).
    if (dep.fieldName.toLowerCase().startsWith('leads.')) {
      const prop = dep.fieldName.slice(6).trim();
      const actual = getLeadPropertyValue(prop, lead, localLeadData).toUpperCase();
      if (dep.valor === null) return actual !== '';
      return actual === String(dep.valor).trim().toUpperCase();
    }

    const parentField = pitchFields.find(f => f.nombre === dep.fieldName);
    if (!parentField) return true;

    const parentValue = values?.[parentField.id];

    // Si la condición es solo "fieldName" (sin valor), el hijo se muestra
    // cuando el padre tenga cualquier valor (no vacío).
    if (dep.valor === null) {
      return parentValue !== undefined && parentValue !== null && parentValue !== '';
    }

    return parentValue === dep.valor;
  };

  // Lee el valor de los values del form para la columna lógica indicada
  // (p. ej. 'pitch_result' o 'pitch_stage'), resolviendo el field por su nombre.
  const getPitchValueByColumn = (values, col) => {
    const field = pitchFields.find(f => getPitchColumnForField(f) === col);
    if (!field) return '';
    return values?.[field.id] ?? '';
  };

  // ¿Debe mostrarse el campo "Especifica por qué no se matriculó"?
  // OR de dos reglas independientes:
  //   1) pitch_result ∈ PITCH_RESULT_REQUIERE_MOTIVO
  //   2) pitch_stage tiene cualquier valor (≠ vacío) → cubre "Reprobado"
  const shouldShowMotivoField = (values) => {
    const resultado = getPitchValueByColumn(values, 'pitch_result');
    // Con "Matrícula" nunca se muestra el motivo (el lead sí se matriculó).
    if (resultado === 'Matrícula') return false;
    const etapa = getPitchValueByColumn(values, 'pitch_stage');
    const porResultado = PITCH_RESULT_REQUIERE_MOTIVO.includes(resultado);
    const porEtapa = etapa !== undefined && etapa !== null && etapa !== '';
    return porResultado || porEtapa;
  };

  // Resuelve la fila de config_categorias seleccionada (guardada por id) →
  // { categoria, categoria_padre }. Devuelve null si no hay selección válida.
  const getMotivoCategoria = (values) => {
    const id = values?.[MOTIVO_FIELD_KEY];
    if (!id) return null;
    const row = categorias.find(c => String(c.id) === String(id));
    if (!row) return null;
    return { categoria: row.categoria, categoria_padre: row.categoria_padre ?? null };
  };

  // Tipo de gestión elegido en el bloque de motivo (default: seguimiento).
  const getMotivoTipo = (values) => values?.[MOTIVO_TIPO_KEY] || 'seguimiento';

  // Convierte hora/minuto/AM-PM (claves sintéticas) a formato 24h.
  const parseMotivoHora = (values) => {
    let hora24 = parseInt(values?.[MOTIVO_HORA_KEY] || '9', 10);
    const periodo = values?.[MOTIVO_PERIODO_KEY] || 'AM';
    if (periodo === 'PM' && hora24 !== 12) hora24 += 12;
    else if (periodo === 'AM' && hora24 === 12) hora24 = 0;
    const minutos = parseInt(values?.[MOTIVO_MINUTO_KEY] || '0', 10);
    return { hora24, minutos };
  };

  // Construye el Date (fecha + hora) del recordatorio a partir de los values.
  const buildMotivoFecha = (values) => {
    const fechaStr = values?.[MOTIVO_FECHA_KEY];
    if (!fechaStr) return null;
    const fecha = new Date(`${fechaStr}T00:00:00`);
    if (isNaN(fecha.getTime())) return null;
    const { hora24, minutos } = parseMotivoHora(values);
    fecha.setHours(hora24, minutos, 0, 0);
    return fecha;
  };

  // Convierte una fila guardada (con columnas attended, pitch_stage, ...) al
  // formato { [field.id]: value } que esperan helpers de render/dependencia
  const resultadoToValues = (resultado) => {
    const values = {};
    pitchFields.forEach(field => {
      const col = getPitchColumnForField(field);
      if (col) values[field.id] = resultado?.[col] ?? '';
    });
    // Precargar el motivo: mapear el texto guardado (motivo_no_matricula) de
    // vuelta al id de config_categorias para que el dropdown quede seleccionado.
    if (resultado?.motivo_no_matricula) {
      const row = categorias.find(c => c.categoria === resultado.motivo_no_matricula);
      if (row) values[MOTIVO_FIELD_KEY] = row.id;
    }
    // Precargar el detalle del motivo y forzar tipo 'seguimiento' (en edición
    // no se muestra el radio: editar siempre genera un comentario).
    values[MOTIVO_DETALLE_KEY] = resultado?.detalle_motivo_no_matricula || '';
    values[MOTIVO_TIPO_KEY] = 'seguimiento';
    return values;
  };

  // Construye el payload card_id + columnas a partir de los valores y los fields.
  // Sólo guarda valores de campos que pasen la condición de dependencia; los
  // campos ocultos quedan como null para evitar guardar datos huérfanos.
  const buildPitchPayload = (values) => {
    const payload = {};
    pitchFields.forEach(field => {
      const col = getPitchColumnForField(field);
      if (!col) return;
      const visible = shouldShowPitchField(field, values);
      const v = values[field.id];
      payload[col] = (!visible || v === undefined || v === '') ? null : v;
    });
    // Campo especial motivo: si es visible y hay selección, guardar categoria,
    // categoria_padre y el detalle/observación; si no, las columnas quedan null.
    if (shouldShowMotivoField(values)) {
      const motivo = getMotivoCategoria(values);
      payload.motivo_no_matricula = motivo?.categoria ?? null;
      payload.motivo_no_matricula_categoria = motivo?.categoria_padre ?? null;
      payload.detalle_motivo_no_matricula = (values[MOTIVO_DETALLE_KEY] || '').trim() || null;
    } else {
      payload.motivo_no_matricula = null;
      payload.motivo_no_matricula_categoria = null;
      payload.detalle_motivo_no_matricula = null;
    }
    return payload;
  };

  // Validación del formulario Pitch (crear y editar comparten lógica).
  // Reglas:
  //   1) Todo campo dinámico VISIBLE marcado como obligatorio debe tener valor.
  //   2) Si el bloque de motivo es visible: categoría y detalle son obligatorios.
  //      Además, en modo crear con tipo 'recordatorio', la fecha/hora deben ser
  //      válidas (al menos 10 min en el futuro si es hoy).
  //   3) En modo crear, debe haber al menos un valor (evita filas vacías).
  const isPitchValuesValid = (values, isEdit = false) => {
    for (const field of pitchFields) {
      if (!shouldShowPitchField(field, values)) continue;
      if (field.obligatorio) {
        const v = values[field.id];
        if (v === undefined || v === null || v === '') return false;
      }
    }

    if (shouldShowMotivoField(values)) {
      if (!values[MOTIVO_FIELD_KEY]) return false;
      const detalle = (values[MOTIVO_DETALLE_KEY] || '').trim();
      if (!detalle) return false;
      if (!isEdit && getMotivoTipo(values) === 'recordatorio') {
        const fechaStr = values[MOTIVO_FECHA_KEY];
        if (!fechaStr) return false;
        const fechaBase = new Date(`${fechaStr}T00:00:00`);
        if (isNaN(fechaBase.getTime())) return false;
        const { hora24, minutos } = parseMotivoHora(values);
        if (!isTimeValid(fechaBase, hora24, minutos)) return false;
      }
    }

    if (!isEdit) {
      const algunValor = pitchFields.some(field => {
        if (!shouldShowPitchField(field, values)) return false;
        const v = values[field.id];
        return v !== undefined && v !== null && v !== '';
      });
      if (!algunValor) return false;
    }

    return true;
  };

  const isPitchFormValid = () => isPitchValuesValid(pitchFormValues, false);

  // Setter del form nuevo que limpia los hijos cuando cambia el padre,
  // para que no queden valores huérfanos de campos que ya no se ven.
  const setPitchFormValue = (fieldId, val) => {
    setPitchFormValues(prev => {
      const next = { ...prev, [fieldId]: val };
      // Si este field tiene hijos que dependían de un valor específico, limpiarlos
      const changedField = pitchFields.find(f => f.id === fieldId);
      if (changedField) {
        pitchFields.forEach(f => {
          const dep = parseDependeDeField(f.depende_de);
          if (!dep) return;
          if (dep.fieldName === changedField.nombre && !shouldShowPitchField(f, next)) {
            next[f.id] = '';
          }
        });
      }
      // Si el motivo dejó de ser visible, limpiar todas sus claves sintéticas.
      if (!shouldShowMotivoField(next)) clearMotivoKeys(next);
      return next;
    });
  };

  // Mismo setter pero para el modo edición de una tarjeta
  const setEditingPitchValue = (fieldId, val) => {
    setEditingPitchValues(prev => {
      const next = { ...prev, [fieldId]: val };
      const changedField = pitchFields.find(f => f.id === fieldId);
      if (changedField) {
        pitchFields.forEach(f => {
          const dep = parseDependeDeField(f.depende_de);
          if (!dep) return;
          if (dep.fieldName === changedField.nombre && !shouldShowPitchField(f, next)) {
            next[f.id] = '';
          }
        });
      }
      if (!shouldShowMotivoField(next)) clearMotivoKeys(next);
      return next;
    });
  };

  // Setter directo de cualquier clave sintética del motivo (categoría, detalle,
  // tipo, fecha/hora). No toca los campos dinámicos del formulario.
  const setMotivoNewValue = (key, val) => {
    setPitchFormValues(prev => ({ ...prev, [key]: val }));
  };
  const setMotivoEditValue = (key, val) => {
    setEditingPitchValues(prev => ({ ...prev, [key]: val }));
  };

  // Mapping de combinación (attended | pitch_result) → fase_id_pipefy destino
  // que el lead tomará tras registrar el resultado del pitch. Si attended='No'
  // el destino es 340855086, tanto si reprogramó como si no (la diferencia es
  // que, si NO reprogramó, además se reasigna el lead al setter antes de seguir).
  const FASE_DESTINO_BY_PITCH = {
    'Si|Matrícula':         '339756299',
    'Si|Pago pendiente':    '340643642',
    'Si|Posible matrícula': '340643263',
    'Si|Interés futuro':    '340483950',
    'Si|Reprobado':         '341775176',
    'Si|No matrícula':      '341775176',
  };
  const FASE_DESTINO_NO_ASISTIO = '340855086';

  const determinarFaseDestinoPitch = (payload) => {
    // Cuando no asistió, "¿Reprogramó?" es obligatorio. Ambas respuestas
    // (Si/No) mueven a 340855086; si NO reprogramó, además se reasigna el lead
    // al setter (eso lo maneja handleCrearResultadoPitch, no este ruteo).
    if (payload.rescheduled === 'Si' || payload.rescheduled === 'No') {
      return FASE_DESTINO_NO_ASISTIO;
    }
    const key = `${payload.attended}|${payload.pitch_result}`;
    return FASE_DESTINO_BY_PITCH[key] || null;
  };

  // Espera a que la fase del lead se actualice en Supabase (Pipefy → sync
  // toma unos segundos). Devuelve la fila actualizada o null si timeout.
  const esperarCambioDeFase = async (cardId, faseDestinoId, maxWaitMs = 15000) => {
    const start = Date.now();
    while (Date.now() - start < maxWaitMs) {
      const { data } = await supabase
        .from('leads')
        .select('fase_id_pipefy, fase_nombre_pipefy, etapa_funnel, pitch_intentos, pitch_seguimientos')
        .eq('card_id', cardId)
        .maybeSingle();
      if (data && String(data.fase_id_pipefy) === String(faseDestinoId)) {
        return data;
      }
      await new Promise(r => setTimeout(r, 1500));
    }
    return null;
  };

  // Reasigna el lead al setter (mismo webhook que la reasignación manual del
  // sidebar). Se usa cuando el lead NO asistió al Pitch y NO reprogramó: el
  // setter que lo agendó debe volver a agendarlo. Hace SOLO el webhook; si
  // falla lanza error para abortar todo el flujo del Pitch (no se guarda nada).
  // Devuelve { setterEmail, setterNombre } para reutilizar en el resto del flujo.
  const reasignarLeadAlSetterPitch = async () => {
    const setterEmail = getLeadPropertyValue('setter_email', lead, localLeadData);
    if (!setterEmail) {
      throw new Error('El lead no tiene setter_email; no se puede reasignar al setter.');
    }
    const comercialAnteriorEmail = comercialEmailActual || lead?.comercial_email || null;

    // Nombres desde usuarios (por email) para anterior y nuevo (setter).
    const emails = [comercialAnteriorEmail, setterEmail].filter(Boolean);
    const { data: usuariosData } = await supabase
      .from('usuarios')
      .select('email, nombre')
      .in('email', emails);
    const nombrePorEmail = (em) =>
      usuariosData?.find(u => u.email === em)?.nombre || em?.split('@')[0] || null;

    const response = await fetch('https://api.mdenglish.us/webhook/reasignar-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        card_id: lead.card_id,
        lead_nombre: lead.nombre,
        comercial_anterior_email: comercialAnteriorEmail,
        comercial_anterior_nombre: nombrePorEmail(comercialAnteriorEmail),
        comercial_nuevo_email: setterEmail,
        comercial_nuevo_nombre: nombrePorEmail(setterEmail),
        usuario_que_reasigna: userEmail,
        respond_io_url: lead.respond_io_url || null,
      }),
    });
    if (!response.ok) throw new Error('Webhook reasignar-lead respondió con error');
    return { setterEmail, setterNombre: nombrePorEmail(setterEmail) };
  };

  // Crea la notificación "Se te ha reasignado un nuevo lead" para el setter,
  // igual que la reasignación manual. No lanza error si falla (best-effort).
  const crearNotificacionReasignacionPitch = async (comercialNuevoEmail) => {
    try {
      const { data: configNotif } = await supabase
        .from('config_notificaciones')
        .select('id, descripcion_template')
        .eq('tipo', 'Se te ha reasignado un nuevo lead')
        .eq('activo', true)
        .single();
      if (!configNotif) return;
      const descripcionFinal = (configNotif.descripcion_template || '')
        .replace('{{nombre}}', lead.nombre || 'Lead')
        .replace('{{reasignado_por}}', userName || userEmail);
      const { error: notifError } = await supabase
        .from('notificaciones')
        .insert({
          config_id: configNotif.id,
          card_id: lead.card_id,
          comercial_email: comercialNuevoEmail,
          nombre_lead: lead.nombre,
          descripcion: descripcionFinal,
          datos_extra: {
            comercial_anterior: comercialEmailActual || lead?.comercial_email || null,
            reasignado_por: userEmail,
          },
        });
      if (notifError) console.error('[Pitch] Error creando notificación de reasignación:', notifError);
    } catch (notifErr) {
      console.error('[Pitch] Error creando notificación de reasignación:', notifErr);
    }
  };

  // Crea un comentario (origen 'Pitch') en `comentarios` y lo envía al webhook
  // de Respond, igual que un seguimiento normal. Se usa cuando el comercial
  // registra el motivo de por qué el lead no se matriculó durante el Pitch.
  // El texto es el detalle escrito por el comercial.
  const crearComentarioDesdePitch = async (motivo, detalle) => {
    if (!lead?.card_id || !motivo?.categoria) return;
    const texto = (detalle || '').trim() || 'Sin detalle';
    const nuevoComentario = {
      lead_id: lead.card_id,
      texto,
      autor_email: userEmail || 'unknown',
      origen: 'Pitch',
      fase: lead.fase_nombre_pipefy || null,
      etapa_funnel: lead.etapa_funnel || null,
      categoria: motivo.categoria,
      categoria_padre: motivo.categoria_padre ?? null,
    };
    try {
      const { data, error } = await supabase
        .from('comentarios')
        .insert([nuevoComentario])
        .select()
        .single();
      if (error) throw error;

      try {
        await fetch('https://api.mdenglish.us/webhook/crear_seguimiento_portal_a_respond', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: data.id,
            lead_id: lead.card_id,
            texto,
            autor_email: userEmail,
            autor_nombre: userName,
            origen: 'Pitch',
            fase: lead.fase_nombre_pipefy || null,
            etapa_funnel: lead.etapa_funnel || null,
            categoria: motivo.categoria,
            categoria_padre: motivo.categoria_padre ?? null,
            created_at: data.created_at,
            lead_nombre: lead.nombre,
            lead_telefono: lead.telefono,
            respond_io_url: lead.respond_io_url || null,
          }),
        });
      } catch (whErr) {
        console.error('[Pitch] Error webhook comentario:', whErr);
      }
    } catch (e) {
      console.error('[Pitch] Error creando comentario desde Pitch:', e);
    }
  };

  // Crea un recordatorio desde el formulario Pitch (espeja el tab Recordatorio):
  // inserta en `recordatorios` (un trigger en BD crea el comentario), actualiza
  // el lead y notifica al webhook de Respond. `texto` es la observación.
  const crearRecordatorioDesdePitch = async (motivo, texto, fechaCompleta) => {
    if (!lead?.card_id || !motivo?.categoria || !fechaCompleta) return;
    const observacion = (texto || '').trim() || 'Sin detalle';
    try {
      const { error: insertErr } = await supabase
        .from('recordatorios')
        .insert([{
          lead_id: lead.card_id,
          fecha_programada: fechaCompleta.toISOString(),
          observacion,
          creado_por: userEmail,
          estado: 'Programado',
          fase: lead.fase_nombre_pipefy || null,
          etapa_funnel: lead.etapa_funnel || null,
          categoria: motivo.categoria,
        }]);
      if (insertErr) throw insertErr;

      const { count: conteoFaseActual } = await supabase
        .from('recordatorios')
        .select('*', { count: 'exact', head: true })
        .eq('lead_id', lead.card_id)
        .eq('fase', lead.fase_nombre_pipefy || '');

      await supabase
        .from('leads')
        .update({
          fecha_programada_recordatorio: fechaCompleta.toISOString(),
          estado_gestion: 'gestionado',
          conteo_recordatorios: conteoFaseActual || 0,
          recordatorio_activo: true,
        })
        .eq('card_id', lead.card_id);

      try {
        await fetch('https://api.mdenglish.us/webhook/crear_seguimiento_portal_a_respond', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: null,
            lead_id: lead.card_id,
            texto: observacion,
            autor_email: userEmail,
            autor_nombre: userName,
            origen: 'Recordatorio',
            fase: lead.fase_nombre_pipefy || null,
            etapa_funnel: lead.etapa_funnel || null,
            categoria: motivo.categoria,
            created_at: new Date().toISOString(),
            lead_nombre: lead.nombre,
            lead_telefono: lead.telefono,
            respond_io_url: lead.respond_io_url || null,
            fecha_programada: fechaCompleta.toISOString(),
          }),
        });
      } catch (whErr) {
        console.error('[Pitch] Error webhook recordatorio:', whErr);
      }
    } catch (e) {
      console.error('[Pitch] Error creando recordatorio desde Pitch:', e);
    }
  };

  // Crear nuevo resultado de pitch.
  // Flujo (loader activo durante TODO):
  //   1) Insert en pitches_resultados (estado='registrado').
  //   2) Update leads.pitch_seguimientos += 1.
  //   3) Webhook Pipefy: mover a la fase destino según attended/pitch_result.
  //   4) Polling Supabase hasta que fase_id_pipefy refleje el cambio.
  //   5) Recién al final: actualizar localLeadData + setear pitchCompletionInfo
  //      para mostrar el panel con el mensaje extendido.
  // Sin toasts de éxito; los toasts solo se usan para errores.
  const handleCrearResultadoPitch = async () => {
    if (!lead?.card_id || savingPitch || !isPitchFormValid()) return;
    setSavingPitch(true);
    try {
      const intentosActual    = localLeadData.pitch_intentos     ?? lead.pitch_intentos     ?? 0;
      const seguimientosActual = localLeadData.pitch_seguimientos ?? lead.pitch_seguimientos ?? 0;
      const nuevoSeguimientos = seguimientosActual + 1;
      const pitchNumero = Math.max(intentosActual, nuevoSeguimientos);

      const pitchPayload = buildPitchPayload(pitchFormValues);

      // No asistió + NO reprogramó → reasignar el lead al setter ANTES de
      // guardar nada, PERO solo si el lead tiene setter asignado (setter_email).
      // Si no hay setter, se omite la reasignación y el lead solo se mueve a la
      // fase (340855086), igual que el camino normal del no-show. Si el webhook
      // de reasignación falla, abortamos todo (el throw cae en el catch).
      const setterEmailLead = getLeadPropertyValue('setter_email', lead, localLeadData);
      const debeReasignarAlSetter = pitchPayload.rescheduled === 'No' && !!setterEmailLead;
      let reasignacionInfo = null;
      if (debeReasignarAlSetter) {
        reasignacionInfo = await reasignarLeadAlSetterPitch();
      }

      const insertPayload = {
        card_id: lead.card_id,
        fecha_pitch: lead.fecha_pitch ?? null,
        pitch_numero: pitchNumero,
        estado: 'registrado',
        ...pitchPayload,
      };

      const { error: insertErr } = await supabase
        .from('pitches_resultados')
        .insert(insertPayload);
      if (insertErr) throw insertErr;

      // Si se registró un motivo de "no matrícula", crear la gestión elegida:
      //   - Seguimiento → comentario (origen 'Pitch') + webhook Respond
      //   - Recordatorio → recordatorio (el trigger en BD crea el comentario)
      if (shouldShowMotivoField(pitchFormValues)) {
        const motivoCreado = getMotivoCategoria(pitchFormValues);
        if (motivoCreado?.categoria) {
          const detalle = (pitchFormValues[MOTIVO_DETALLE_KEY] || '').trim();
          if (getMotivoTipo(pitchFormValues) === 'recordatorio') {
            const fechaCompleta = buildMotivoFecha(pitchFormValues);
            await crearRecordatorioDesdePitch(motivoCreado, detalle, fechaCompleta);
          } else {
            await crearComentarioDesdePitch(motivoCreado, detalle);
          }
        }
      }

      // Update de leads: contador de seguimientos + columnas que se reflejan
      // en el lead (p. ej. motivo_matricula = último valor del pitch).
      const leadUpdate = { pitch_seguimientos: nuevoSeguimientos };
      PITCH_COLUMNS_SYNC_LEADS.forEach(col => {
        if (col in pitchPayload) leadUpdate[col] = pitchPayload[col];
      });
      // Reasignación al setter: además del webhook, replicamos lo que hace la
      // reasignación manual sobre la fila del lead.
      if (debeReasignarAlSetter && reasignacionInfo) {
        leadUpdate.comercial_email = reasignacionInfo.setterEmail;
        leadUpdate.estado_gestion = 'sin_gestionar';
        leadUpdate.revisado = false;
        leadUpdate.fecha_asignacion = new Date().toISOString();
      }
      const { error: updateErr } = await supabase
        .from('leads')
        .update(leadUpdate)
        .eq('card_id', lead.card_id);
      if (updateErr) throw updateErr;

      // Notificación al nuevo comercial (setter), igual que la reasignación manual.
      if (debeReasignarAlSetter && reasignacionInfo) {
        await crearNotificacionReasignacionPitch(reasignacionInfo.setterEmail);
      }

      const faseDestinoId = determinarFaseDestinoPitch(pitchPayload);

      // Caso degradado: sin regla de fase mapeada → terminamos sin webhook.
      if (!faseDestinoId) {
        setLocalLeadData(prev => ({ ...prev, pitch_seguimientos: nuevoSeguimientos }));
        setPitchFormValues({});
        setPitchSelectOpenId(null);
        await fetchPitchResultados();
        setPitchCompletionInfo({ faseNombre: null, faseError: false, fasePending: false });
        return;
      }

      const { data: faseData } = await supabase
        .from('config_stepper')
        .select('nombre_fase')
        .eq('fase_id_pipefy', faseDestinoId)
        .maybeSingle();
      const faseNombreDestino = faseData?.nombre_fase || faseDestinoId;

      let webhookOk = true;
      let faseActualizada = null;
      try {
        faseActualizada = await actualizarFaseDesdePortal(lead.card_id, faseNombreDestino, {
          telefono: lead.telefono,
          faseResuelta: {
            fase_id_pipefy: String(faseDestinoId),
            fase_nombre_pipefy: faseNombreDestino,
            etapa_funnel: lead.etapa_funnel || null,
          },
          faseAnterior: {
            fase_id_pipefy: lead.fase_id_pipefy ? String(lead.fase_id_pipefy) : null,
            fase_nombre_pipefy: lead.fase_nombre_pipefy || null,
            etapa_funnel: lead.etapa_funnel || null,
          },
        });
      } catch (whErr) {
        console.error('[Pitch] Error actualizando fase:', whErr);
        webhookOk = false;
      }

      const updated = webhookOk
        ? {
            fase_id_pipefy: faseActualizada?.fase_id_pipefy ?? String(faseDestinoId),
            fase_nombre_pipefy: faseActualizada?.fase_nombre_pipefy ?? faseNombreDestino,
            etapa_funnel: faseActualizada?.etapa_funnel ?? lead.etapa_funnel,
            pitch_seguimientos: nuevoSeguimientos,
            pitch_intentos: intentosActual,
          }
        : await esperarCambioDeFase(lead.card_id, faseDestinoId);

      // Update final unificado (form + tarjetas + datos del lead).
      const reasignOverlay = (debeReasignarAlSetter && reasignacionInfo)
        ? { comercial_email: reasignacionInfo.setterEmail }
        : {};
      if (updated) {
        setLocalLeadData(prev => ({
          ...prev,
          pitch_seguimientos: updated.pitch_seguimientos ?? nuevoSeguimientos,
          pitch_intentos: updated.pitch_intentos ?? intentosActual,
          fase_id_pipefy: updated.fase_id_pipefy,
          fase_nombre_pipefy: updated.fase_nombre_pipefy,
          etapa_funnel: updated.etapa_funnel,
          ...reasignOverlay,
        }));
        setFaseLocal(updated.fase_nombre_pipefy);
        onRefreshData?.();
      } else {
        setLocalLeadData(prev => ({ ...prev, pitch_seguimientos: nuevoSeguimientos, ...reasignOverlay }));
        if (debeReasignarAlSetter && reasignacionInfo) onRefreshData?.();
      }
      setPitchFormValues({});
      setPitchSelectOpenId(null);
      await fetchPitchResultados();

      setPitchCompletionInfo({
        faseNombre: updated?.fase_nombre_pipefy || faseNombreDestino,
        faseError: !webhookOk,
        fasePending: webhookOk && !updated,
      });
    } catch (e) {
      console.error('[Pitch] Error creando resultado:', e);
      setToastMessage('No se pudo crear el resultado. Intenta de nuevo.');
      setTimeout(() => setToastMessage(null), 4000);
    } finally {
      setSavingPitch(false);
    }
  };

  // Iniciar edición de una tarjeta
  const handleStartEditPitch = (resultado) => {
    const initialValues = {};
    pitchFields.forEach(field => {
      const col = getPitchColumnForField(field);
      initialValues[field.id] = col ? (resultado[col] || '') : '';
    });
    // Precargar el motivo (mapear texto guardado → id de config_categorias)
    if (resultado.motivo_no_matricula) {
      const row = categorias.find(c => c.categoria === resultado.motivo_no_matricula);
      if (row) initialValues[MOTIVO_FIELD_KEY] = row.id;
    }
    // Precargar detalle y forzar tipo 'seguimiento' (en edición no hay radio).
    initialValues[MOTIVO_DETALLE_KEY] = resultado.detalle_motivo_no_matricula || '';
    initialValues[MOTIVO_TIPO_KEY] = 'seguimiento';
    setEditingPitchValues(initialValues);
    setEditingPitchId(resultado.id);
    setEditingPitchSelectOpenId(null);
    setMotivoEditOpen(false);
    setMotivoEditBusqueda('');
  };

  const handleCancelEditPitch = () => {
    setEditingPitchId(null);
    setEditingPitchValues({});
    setEditingPitchSelectOpenId(null);
    setMotivoEditOpen(false);
    setMotivoEditBusqueda('');
  };

  const handleSaveEditPitch = async () => {
    if (!editingPitchId || savingEditPitchId) return;
    setSavingEditPitchId(editingPitchId);
    try {
      // Motivo original (antes de editar) para detectar si cambió.
      const original = pitchResultados.find(r => r.id === editingPitchId);
      const motivoOriginal = original?.motivo_no_matricula || null;
      const detalleOriginal = original?.detalle_motivo_no_matricula || null;

      const payload = buildPitchPayload(editingPitchValues);
      const { error } = await supabase
        .from('pitches_resultados')
        .update(payload)
        .eq('id', editingPitchId);
      if (error) throw error;

      // Reflejar en leads las columnas sincronizadas (p. ej. motivo_matricula).
      const leadSync = {};
      PITCH_COLUMNS_SYNC_LEADS.forEach(col => {
        if (col in payload) leadSync[col] = payload[col];
      });
      if (Object.keys(leadSync).length > 0) {
        await supabase.from('leads').update(leadSync).eq('card_id', lead.card_id);
      }

      // Editar siempre crea un comentario de Seguimiento, pero solo si cambió
      // la categoría o el detalle respecto al original (evita duplicados).
      const motivoNuevo = shouldShowMotivoField(editingPitchValues)
        ? getMotivoCategoria(editingPitchValues)
        : null;
      const detalleNuevo = shouldShowMotivoField(editingPitchValues)
        ? (editingPitchValues[MOTIVO_DETALLE_KEY] || '').trim()
        : '';
      if (motivoNuevo?.categoria &&
          (motivoNuevo.categoria !== motivoOriginal || (detalleNuevo || null) !== detalleOriginal)) {
        await crearComentarioDesdePitch(motivoNuevo, detalleNuevo);
      }

      handleCancelEditPitch();
      await fetchPitchResultados();
    } catch (e) {
      console.error('[Pitch] Error editando resultado:', e);
      alert('No se pudo guardar el cambio. Intenta de nuevo.');
    } finally {
      setSavingEditPitchId(null);
    }
  };

  // ===========================================================================

  // Cargar configuración del stepper dinámico
  useEffect(() => {
    const cargarConfigStepper = async () => {
      
      if (!lead?.fase_id_pipefy) {
        console.log('[Stepper] No hay fase_id_pipefy, no se muestra stepper');
        setStepperSteps([]);
        setConfigStepper(null);
        return;
      }

      try {
        // Obtener config de la fase actual (sin .single() para evitar error 406)
        const { data: configResults, error } = await supabase
          .from('config_stepper')
          .select('*')
          .eq('fase_id_pipefy', String(lead.fase_id_pipefy));

        const configActual = configResults?.[0] || null;

        if (error || !configActual) {
          setStepperSteps([]);
          setConfigStepper(null);
          return;
        }

        setConfigStepper(configActual);

        // Determinar qué fases mostrar según el tipo
        let fasesAMostrar = [];
        let fasesFuturo = [];
        let colorAUsar = configActual.color || 'verde';

        // Obtener todas las configs para hacer lookup de nombres
        const { data: allConfigs } = await supabase
          .from('config_stepper')
          .select('fase_id_pipefy, label_corto, nombre_fase');
        
        // Crear mapa de fase_id -> label_corto
        const labelMap = {};
        allConfigs?.forEach(c => {
          labelMap[c.fase_id_pipefy] = c.label_corto || c.nombre_fase;
        });

        if (configActual.tipo === 'gestion') {
          // Tipo gestión: usar fases_a_mostrar con color configurado + fases_futuro en gris
          fasesAMostrar = parsearFases(configActual.fases_a_mostrar);
          fasesFuturo = parsearFases(configActual.fases_futuro);
        } else if (configActual.tipo === 'finalizada' || configActual.tipo === 'interes_futuro') {
          // Tipo finalizada/interes_futuro: buscar config de fase_anterior
          if (lead.fase_anterior) {
            // Buscar la config que coincida con la fase_anterior (puede ser por nombre o id)
            const configAnterior = allConfigs?.find(c => 
              c.nombre_fase === lead.fase_anterior || 
              c.fase_id_pipefy === lead.fase_anterior
            );
            
            if (configAnterior) {
              // Necesitamos obtener fases_a_mostrar y fases_futuro de configAnterior
              const { data: configAnteriorFull } = await supabase
                .from('config_stepper')
                .select('*')
                .eq('fase_id_pipefy', configAnterior.fase_id_pipefy);
              
              if (configAnteriorFull?.[0]) {
                fasesAMostrar = parsearFases(configAnteriorFull[0].fases_a_mostrar);
                fasesFuturo = parsearFases(configAnteriorFull[0].fases_futuro);
              }
            }
          }
          // El color es el de la fase ACTUAL (donde está el lead)
          colorAUsar = configActual.color || 'rojo';
        }

        // Construir el stepper final con nombres reales
        const stepsFinales = [
          ...fasesAMostrar.map(f => ({ 
            ...f, 
            tipo: 'mostrar',
            labelReal: labelMap[f.faseId] || f.label 
          })),
          ...fasesFuturo.map(f => ({ 
            ...f, 
            tipo: 'futuro',
            labelReal: labelMap[f.faseId] || f.label 
          }))
        ];

        setStepperSteps(stepsFinales);
        setStepperColor(colorAUsar);

      } catch (error) {
        console.error('Error cargando config stepper:', error);
        setStepperSteps([]);
        setConfigStepper(null);
      }
    };

    cargarConfigStepper();
  }, [lead?.fase_id_pipefy, lead?.fase_anterior]);

  // useEffect para cargar historial de fases
  useEffect(() => {
    const cargarHistorialFases = async () => {
      if (!lead?.card_id) {
        setHistorialFases([]);
        return;
      }

      setLoadingHistorialFases(true);
      try {
        const { data, error } = await supabase
          .from('historial')
          .select('*')
          .eq('lead_id', lead.card_id)
          .eq('modulo', 'comercial')
          .order('created_at', { ascending: true }); // Más antiguo primero

        if (error) throw error;
        setHistorialFases(data || []);
        // Resetear paginación
        setHistorialFasesExpanded({});
        setHistorialFasesPaginacion({});
      } catch (error) {
        console.error('Error cargando historial de fases:', error);
        setHistorialFases([]);
      } finally {
        setLoadingHistorialFases(false);
      }
    };

    cargarHistorialFases();
  }, [lead?.card_id]);

  // Helper para obtener el valor actual (local o del lead)
  const getFieldValue = (fieldName) => {
    if (localLeadData.hasOwnProperty(fieldName)) {
      return localLeadData[fieldName];
    }
    return lead?.[fieldName];
  };

  // Función para toggle de lead HOT 🔥
  const handleToggleHot = async () => {
    if (!lead?.card_id || togglingHot) return;

    try {
      setTogglingHot(true);
      const newValue = !isHot;

      const { error } = await supabase
        .from('leads')
        .update({ is_hot: newValue })
        .eq('card_id', lead.card_id);

      if (error) throw error;

      setIsHot(newValue);
      onRefreshData?.();
    } catch (error) {
      console.error('Error actualizando estado hot:', error);
    } finally {
      setTogglingHot(false);
    }
  };

  // Función para cambiar la fase del lead
  const handleCambiarFase = async (nuevaFase) => {
    if (!lead?.card_id || cambiandoFase || nuevaFase === faseLocal) return;
    
    setCambiandoFase(true);
    setFaseDropdownOpen(false);
    
    const faseAnterior = faseLocal;
    setFaseLocal(nuevaFase);
    
    try {
      const resolved = await actualizarFaseDesdePortal(lead.card_id, nuevaFase, {
        telefono: lead.telefono,
        faseAnterior: {
          fase_id_pipefy: lead.fase_id_pipefy ? String(lead.fase_id_pipefy) : null,
          fase_nombre_pipefy: lead.fase_nombre_pipefy || null,
          etapa_funnel: lead.etapa_funnel || null,
        },
      });

      const faseNombre = resolved?.fase_nombre_pipefy || nuevaFase;
      setFaseLocal(faseNombre);
      setLocalLeadData(prev => ({
        ...prev,
        fase_id_pipefy: resolved?.fase_id_pipefy ?? prev?.fase_id_pipefy,
        fase_nombre_pipefy: faseNombre,
        etapa_funnel: resolved?.etapa_funnel ?? prev?.etapa_funnel,
      }));
      
      setToastMessage(`Fase actualizada a "${faseNombre}"`);
      setTimeout(() => setToastMessage(null), 3000);
      
    } catch (error) {
      console.error('Error cambiando fase:', error);
      setFaseLocal(faseAnterior);
      setToastMessage('Error al cambiar la fase');
      setTimeout(() => setToastMessage(null), 3000);
    } finally {
      setCambiandoFase(false);
    }
  };

  // Obtener información del comercial actual
  const comercialEmailActual = getFieldValue('comercial_email') || lead?.comercial_email;
  const comercialActual = comerciales.find(c => c.email === comercialEmailActual);
  const nombreComercialActual = comercialActual?.nombre || comercialEmailActual?.split('@')[0] || 'Sin asignar';

  // Función para guardar un campo editado
  // Campos que se sincronizan con Respond.io
  const CAMPOS_RESPOND_IO = [
    'nombre', 'telefono', 'email', 'ocupacion', 'pais', 'motivacion', 
    'motivacion_detalle', 'nivel_ingles', 'ano_residencia', 'lugar_trabajo',
    'universidad', 'como_adquirio_ingles', 'como_adquirio_ingles_detalle',
    'cuando_empezar', 'especialidad', 'plan_pago', 'consulta_decision', 'referido_por',
    'fuente_dato', 'rango_de_inversion', 'hizo_pitch', 'sexo', 'edad'
  ];

  const handleSaveField = async (fieldName, value) => {
    if (!lead?.card_id) return;
    
    // Actualización optimista inmediata
    setLocalLeadData(prev => ({ ...prev, [fieldName]: value || null }));
    setEditingField(null);
    setEditValue('');
    setSearchSelectQuery('');
    
    try {
      setSavingField(fieldName);
      
      const { error } = await supabase
        .from('leads')
        .update({ [fieldName]: value || null })
        .eq('card_id', lead.card_id);
      
      if (error) throw error;
      
      // Sincronizar con Respond.io si es un campo relevante
      if (CAMPOS_RESPOND_IO.includes(fieldName)) {
        try {
          await fetch('https://api.mdenglish.us/webhook/actualizar_respond_io', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              card_id: lead.card_id,
              campo_actualizado: fieldName,
              valor_nuevo: value || null,
              respond_io_url: lead.respond_io_url,
              lead_data: {
                nombre: fieldName === 'nombre' ? value : (getFieldValue('nombre') || lead.nombre),
                telefono: fieldName === 'telefono' ? value : (getFieldValue('telefono') || lead.telefono),
                email: fieldName === 'email' ? value : (getFieldValue('email') || lead.email),
                ocupacion: fieldName === 'ocupacion' ? value : (getFieldValue('ocupacion') || lead.ocupacion),
                pais: fieldName === 'pais' ? value : (getFieldValue('pais') || lead.pais),
                motivacion: fieldName === 'motivacion' ? value : (getFieldValue('motivacion') || lead.motivacion),
                motivacion_detalle: fieldName === 'motivacion_detalle' ? value : (getFieldValue('motivacion_detalle') || lead.motivacion_detalle),
                nivel_ingles: fieldName === 'nivel_ingles' ? value : (getFieldValue('nivel_ingles') || lead.nivel_ingles),
                ano_residencia: fieldName === 'ano_residencia' ? value : (getFieldValue('ano_residencia') || lead.ano_residencia),
                lugar_trabajo: fieldName === 'lugar_trabajo' ? value : (getFieldValue('lugar_trabajo') || lead.lugar_trabajo),
                universidad: fieldName === 'universidad' ? value : (getFieldValue('universidad') || lead.universidad),
                como_adquirio_ingles: fieldName === 'como_adquirio_ingles' ? value : (getFieldValue('como_adquirio_ingles') || lead.como_adquirio_ingles),
                como_adquirio_ingles_detalle: fieldName === 'como_adquirio_ingles_detalle' ? value : (getFieldValue('como_adquirio_ingles_detalle') || lead.como_adquirio_ingles_detalle),
                cuando_empezar: fieldName === 'cuando_empezar' ? value : (getFieldValue('cuando_empezar') || lead.cuando_empezar),
                especialidad: fieldName === 'especialidad' ? value : (getFieldValue('especialidad') || lead.especialidad),
                plan_pago: fieldName === 'plan_pago' ? value : (getFieldValue('plan_pago') || lead.plan_pago),
                consulta_decision: fieldName === 'consulta_decision' ? value : (getFieldValue('consulta_decision') || lead.consulta_decision),
                referido_por: fieldName === 'referido_por' ? value : (getFieldValue('referido_por') || lead.referido_por),
                fuente_dato: fieldName === 'fuente_dato' ? value : (getFieldValue('fuente_dato') || lead.fuente_dato),
                rango_de_inversion: fieldName === 'rango_de_inversion' ? value : (getFieldValue('rango_de_inversion') || lead.rango_de_inversion),
                hizo_pitch: fieldName === 'hizo_pitch' ? value : (getFieldValue('hizo_pitch') || lead.hizo_pitch),
                sexo: fieldName === 'sexo' ? value : (getFieldValue('sexo') || lead.sexo),
                edad: fieldName === 'edad' ? value : (getFieldValue('edad') || lead.edad)
              }
            })
          });
        } catch (webhookError) {
          console.error('Error sincronizando con Respond.io:', webhookError);
          // No revertimos el cambio local porque Supabase sí se actualizó
        }
      }
      
      // Refrescar datos en background
      onRefreshData?.();
    } catch (error) {
      console.error('Error guardando campo:', error);
      // Revertir cambio local si falla
      setLocalLeadData(prev => {
        const newData = { ...prev };
        delete newData[fieldName];
        return newData;
      });
      alert('Error al guardar. Intenta de nuevo.');
    } finally {
      setSavingField(null);
    }
  };

  // Función para iniciar edición de un campo
  const startEditing = (fieldName, currentValue) => {
    setEditingField(fieldName);
    setEditValue(currentValue || '');
    setSearchSelectQuery('');
  };

  // Función para cancelar edición
  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
    setSearchSelectQuery('');
  };

  // Handler para confirmar reasignación de comercial
  const handleConfirmarReasignacion = async () => {
    if (!comercialSeleccionado || !lead?.card_id) return;

    try {
      setReasignando(true);
      
      // Obtener datos actualizados del lead desde la base de datos
      const { data: leadActualizado } = await supabase
        .from('leads')
        .select('respond_io_url')
        .eq('card_id', lead.card_id)
        .single();
      
      // Enviar al webhook de n8n
      const payload = {
        card_id: lead.card_id,
        lead_nombre: lead.nombre,
        comercial_anterior_email: lead.comercial_email,
        comercial_anterior_nombre: nombreComercialActual,
        comercial_nuevo_email: comercialSeleccionado.email,
        comercial_nuevo_nombre: comercialSeleccionado.nombre,
        usuario_que_reasigna: userEmail,
        respond_io_url: leadActualizado?.respond_io_url || lead.respond_io_url || null
      };

      const response = await fetch('https://api.mdenglish.us/webhook/reasignar-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Error al reasignar');
      }

      // Actualizar el lead en Supabase - incluyendo el nuevo comercial
      const { error: updateError } = await supabase
        .from('leads')
        .update({ 
          comercial_email: comercialSeleccionado.email,
          estado_gestion: 'sin_gestionar',
          revisado: false,
          fecha_asignacion: new Date().toISOString()
        })
        .eq('card_id', lead.card_id);

      if (updateError) {
        console.error('Error actualizando estado del lead:', updateError);
      }

      // Actualización optimista inmediata del comercial
      setLocalLeadData(prev => ({ 
        ...prev, 
        comercial_email: comercialSeleccionado.email 
      }));

      // Crear notificación para el comercial nuevo (usando template de config_notificaciones)
      try {
        // Obtener el template de la config (buscar por tipo, no por UUID)
        const { data: configNotif } = await supabase
          .from('config_notificaciones')
          .select('id, descripcion_template')
          .eq('tipo', 'Se te ha reasignado un nuevo lead')
          .eq('activo', true)
          .single();
        
        if (configNotif) {
          // Reemplazar variables en el template
          let descripcionFinal = configNotif.descripcion_template
            .replace('{{nombre}}', lead.nombre || 'Lead')
            .replace('{{reasignado_por}}', userName || userEmail);
          
          const { error: notifError } = await supabase
            .from('notificaciones')
            .insert({
              config_id: configNotif.id,
              card_id: lead.card_id,
              comercial_email: comercialSeleccionado.email,
              nombre_lead: lead.nombre,
              descripcion: descripcionFinal,
              datos_extra: {
                comercial_anterior: lead.comercial_email,
                reasignado_por: userEmail
              }
            });
          
          if (notifError) {
            console.error('Error creando notificación de reasignación:', notifError);
          }
        }
      } catch (notifErr) {
        console.error('Error creando notificación:', notifErr);
      }

      // Cerrar modales y refrescar
      setMostrarModalReasignar(false);
      setComercialDropdownOpen(false);
      setComercialSeleccionado(null);
      
      // Refrescar datos
      onRefreshData?.();
      
      alert(`Lead reasignado exitosamente a ${comercialSeleccionado.nombre}`);

    } catch (error) {
      console.error('Error reasignando lead:', error);
      alert('No se pudo reasignar el lead. Intenta nuevamente.');
    } finally {
      setReasignando(false);
    }
  };

  // Cerrar dropdown de comerciales al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.comercial-dropdown')) {
        setComercialDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Cerrar dropdown de categorías al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.categoria-dropdown')) {
        setCategoriaDropdownOpen(false);
      }
      if (!e.target.closest('.sigla-dropdown')) {
        setSiglaDropdownOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Cerrar dropdowns del motivo (creación/edición) al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.motivo-dropdown')) {
        setMotivoNewOpen(false);
        setMotivoEditOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Focus en input de búsqueda cuando se abre dropdown de categoría
  useEffect(() => {
    if (categoriaDropdownOpen) {
      setTimeout(() => {
        // Intenta hacer focus en el ref disponible (seguimiento o recordatorio)
        if (categoriaSearchRef.current) {
          categoriaSearchRef.current.focus();
        } else if (categoriaRecordatorioSearchRef.current) {
          categoriaRecordatorioSearchRef.current.focus();
        }
      }, 50);
    }
  }, [categoriaDropdownOpen]);

  // Función para generar resumen con IA
  const handleGenerarResumen = async () => {
    if (!lead?.card_id) return;
    
    try {
      setGenerandoResumen(true);
      
      // 1. Obtener comentarios del lead
      const { data: comentarios } = await supabase
        .from('comentarios')
        .select('texto, created_at, origen, fase, etapa_funnel')
        .eq('lead_id', lead.card_id)
        .order('created_at', { ascending: false });
      
      // 2. Obtener recordatorios del lead
      const { data: recordatoriosData } = await supabase
        .from('recordatorios')
        .select('fecha_programada, observacion, estado, fase, etapa_funnel, created_at')
        .eq('lead_id', lead.card_id)
        .order('created_at', { ascending: false });
      
      // 3. Construir el payload con toda la información
      const payload = {
        lead: {
          card_id: lead.card_id,
          nombre: lead.nombre,
          email: lead.email,
          telefono: lead.telefono,
          pais: lead.pais,
          ocupacion: lead.ocupacion,
          nivel_ingles: lead.nivel_ingles,
          motivacion: lead.motivacion,
          es_reincidente: lead.es_reincidente,
          pitch_con: lead.pitch_con,
          fase_actual: lead.fase_nombre_pipefy,
          etapa_funnel: lead.etapa_funnel,
          estado_gestion: lead.estado_gestion,
          created_at: lead.created_at,
          updated_at: lead.updated_at
        },
        comentarios: comentarios || [],
        recordatorios: recordatoriosData || []
      };
      
      // 4. Enviar al webhook de n8n
      const response = await fetch('https://api.mdenglish.us/webhook/generar-resumen-ia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Error al generar resumen');
      }
      
      const result = await response.json();
      
      // 5. Guardar el resumen en la base de datos
      const resumenGenerado = result.resumen_ia || result.resumen;
      const fechaGeneracion = new Date().toISOString();
      
      if (resumenGenerado) {
        const { error: updateError } = await supabase
          .from('leads')
          .update({ 
            resumen_ia: resumenGenerado,
            resumen_ia_fecha: fechaGeneracion
          })
          .eq('card_id', lead.card_id);
        
        if (updateError) throw updateError;
        
        setResumenIA(resumenGenerado);
        setResumenIAFecha(fechaGeneracion);
        
        // Refrescar datos del dashboard silenciosamente
        onRefreshData?.();
      } else {
        throw new Error('No se recibió resumen de la IA');
      }
      
    } catch (error) {
      console.error('Error generando resumen:', error);
      alert('No se pudo generar el resumen. Intenta nuevamente.');
    } finally {
      setGenerandoResumen(false);
    }
  };

  // Cargar comentarios de seguimiento
  const fetchRecordatorios = useCallback(async (page = 0, reset = false) => {
    if (!lead?.card_id) return;
    
    try {
      setLoadingRecordatorios(true);
      
      const from = page * RECORDATORIOS_PER_PAGE;
      const to = from + RECORDATORIOS_PER_PAGE - 1;
      
      // Traer comentarios con el nombre del autor desde la tabla usuarios.
      // El trigger que copia un recordatorio a comentarios no siempre lleva
      // llamada/mensaje: se cruzan con recordatorios para pintar el Detalle.
      const [{ data, error }, { data: recs }] = await Promise.all([
        supabase
          .from('comentarios')
          .select(`
            *,
            usuarios:autor_email (nombre)
          `, { count: 'exact' })
          .eq('lead_id', lead.card_id)
          .order('created_at', { ascending: false })
          .range(from, to),
        supabase
          .from('recordatorios')
          .select('id, observacion, llamada, mensaje, created_at')
          .eq('lead_id', lead.card_id),
      ]);
      
      if (error) throw error;

      const comentarios = (data || []).map((comentario) =>
        enriquecerComentarioConCanal(comentario, recs || [])
      );
      
      if (reset) {
        setRecordatorios(comentarios);
      } else {
        setRecordatorios(prev => [...prev, ...comentarios]);
      }
      
      setRecordatoriosPage(page);
      setHasMoreRecordatorios((data?.length || 0) === RECORDATORIOS_PER_PAGE);
      
    } catch (error) {
      console.error('Error cargando comentarios:', error.message);
    } finally {
      setLoadingRecordatorios(false);
    }
  }, [lead?.card_id]);

  // Cargar más recordatorios (infinite scroll)
  const loadMoreRecordatorios = () => {
    if (!loadingRecordatorios && hasMoreRecordatorios) {
      fetchRecordatorios(recordatoriosPage + 1);
    }
  };

  const tieneSiglaCanal = haySiglaCanal(siglaLlamada, siglaMensaje);

  const cerrarSiglaDropdown = (which) => {
    setCategoriaDropdownOpen(false);
    setSiglaDropdownOpen((prev) => (prev === which ? null : which));
  };

  const renderCanalSiglas = () => (
    <div className="mb-3">
      <label className="text-xs text-slate-500 mb-1.5 block font-medium">
        Llamada o mensaje <span className="text-slate-400 font-normal">(obligatorio, uno o ambos)</span>
      </label>
      <div className="flex gap-2">
        <CanalSiglaSelect
          icon={<Phone size={16} />}
          value={siglaLlamada}
          options={OPCIONES_LLAMADA}
          placeholder="Llamada"
          open={siglaDropdownOpen === 'llamada'}
          onToggle={() => cerrarSiglaDropdown('llamada')}
          onChange={(opt) => {
            setSiglaLlamada(opt);
            setSiglaDropdownOpen(null);
          }}
          accentClass="text-[#1717AF]"
        />
        <CanalSiglaSelect
          icon={<WhatsAppGlyph size={16} />}
          value={siglaMensaje}
          options={OPCIONES_MENSAJE}
          placeholder="Mensaje"
          open={siglaDropdownOpen === 'mensaje'}
          onToggle={() => cerrarSiglaDropdown('mensaje')}
          onChange={(opt) => {
            setSiglaMensaje(opt);
            setSiglaDropdownOpen(null);
          }}
          accentClass="text-[#25D366]"
        />
      </div>
    </div>
  );

  // Enviar nuevo mensaje
  const handleEnviarMensaje = async () => {
    if (!lead?.card_id) return;
    if (enviandoMensaje) return;
    // El detalle del seguimiento es obligatorio.
    if (!nuevoMensaje.trim()) return;
    if (!siglaLlamada && !siglaMensaje) return;
    
    try {
      setEnviandoMensaje(true);
      
      const textoFinal = nuevoMensaje.trim();
      
      const nuevoComentario = {
        lead_id: lead.card_id,
        texto: textoFinal,
        autor_email: userEmail || 'unknown',
        origen: 'Seguimiento',
        fase: lead.fase_nombre_pipefy || null,
        etapa_funnel: lead.etapa_funnel || null,
        categoria: categoriaSeleccionada || 'Otro',
        ...camposCanal(siglaLlamada, siglaMensaje),
      };
      
      const { data, error } = await supabase
        .from('comentarios')
        .insert([nuevoComentario])
        .select()
        .single();
      
      if (error) throw error;
      
      // Enviar al webhook de Respond
      let respondOk = false;
      try {
        const webhookResponse = await fetch('https://api.mdenglish.us/webhook/crear_seguimiento_portal_a_respond', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: data.id,
            lead_id: lead.card_id,
            texto: textoFinal,
            autor_email: userEmail,
            autor_nombre: userName,
            origen: 'Seguimiento',
            fase: lead.fase_nombre_pipefy || null,
            etapa_funnel: lead.etapa_funnel || null,
            categoria: categoriaSeleccionada || 'Otro',
            ...camposCanal(siglaLlamada, siglaMensaje),
            created_at: data.created_at,
            lead_nombre: lead.nombre,
            lead_telefono: lead.telefono,
            respond_io_url: lead.respond_io_url || null
          })
        });
        const webhookResult = await webhookResponse.json();
        if (webhookResult?.result === 'ok') {
          respondOk = true;
        }
      } catch (webhookError) {
        console.error('Error enviando webhook seguimiento:', webhookError);
      }
      
      // Agregar al inicio de la lista con el nombre y fecha
      const comentarioConNombre = {
        ...data,
        created_at: data.created_at || new Date().toISOString(),
        usuarios: { nombre: userName }
      };
      setRecordatorios(prev => [comentarioConNombre, ...prev]);
      setNuevoMensaje('');
      setCategoriaSeleccionada('Otro'); // Reset a default
      setSiglaLlamada(SIGLA_LLAMADA_DEFAULT);
      setSiglaMensaje(SIGLA_MENSAJE_DEFAULT);
      setSiglaDropdownOpen(null);
      
      // Toast de éxito (unificado si Respond respondió ok)
      const mensajeToast = respondOk 
        ? '¡Listo! El seguimiento ya está registrado en OV y Respond.'
        : 'Se creó tu seguimiento exitosamente';
      setToastMessage(mensajeToast);
      setTimeout(() => setToastMessage(null), 3000);
      
    } catch (error) {
      console.error('Error enviando comentario:', error);
      setToastMessage('Error al crear el seguimiento');
      setTimeout(() => setToastMessage(null), 3000);
    } finally {
      setEnviandoMensaje(false);
    }
  };

  // Manejar scroll para infinite scroll (con flex-col-reverse)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Con flex-col-reverse el scroll empieza en 0 y va negativo hacia arriba
    // Detectamos si está cerca del tope (mensajes antiguos)
    const isNearTop = scrollTop <= -scrollHeight + clientHeight + 150;
    if (isNearTop && !loadingRecordatorios && hasMoreRecordatorios) {
      loadMoreRecordatorios();
    }
  };

  // Cargar recordatorios cuando cambia el lead o se abre el tab
  useEffect(() => {
    if (isOpen && lead?.card_id && activeTab === 'seguimiento') {
      setRecordatorios([]);
      setRecordatoriosPage(0);
      setHasMoreRecordatorios(true);
      setFiltroEtapa(null);
      setFiltroFase(null);
      setSiglaLlamada(SIGLA_LLAMADA_DEFAULT);
      setSiglaMensaje(SIGLA_MENSAJE_DEFAULT);
      setSiglaDropdownOpen(null);
      fetchRecordatorios(0, true);
      
      // Scroll al final para ver mensajes recientes e input (estilo chat)
      setTimeout(() => {
        // Scroll del contenedor de mensajes
        if (recordatoriosContainerRef.current) {
          recordatoriosContainerRef.current.scrollTop = recordatoriosContainerRef.current.scrollHeight;
        }
        // Scroll del sidebar (contenedor padre)
        if (sidebarContentRef.current) {
          sidebarContentRef.current.scrollTop = sidebarContentRef.current.scrollHeight;
        }
      }, 300);
    }
  }, [isOpen, lead?.card_id, activeTab, fetchRecordatorios]);

  // Cargar categorías de seguimiento
  useEffect(() => {
    const cargarCategorias = async () => {
      try {
        setLoadingCategorias(true);
        const { data, error } = await supabase
          .from('config_categorias')
          .select('id, categoria, categoria_padre, formulario_pitch, posicion')
          .eq('modulo', 'comercial')
          .eq('estado', true)
          .order('posicion', { ascending: true });
        
        if (error) throw error;
        
        setCategorias(data || []);
        // Asegurar que "Otro" esté seleccionado por defecto
        const tieneOtro = data?.some(c => c.categoria === 'Otro');
        if (tieneOtro) {
          setCategoriaSeleccionada('Otro');
        } else if (data?.length > 0) {
          setCategoriaSeleccionada(data[0].categoria);
        }
      } catch (error) {
        console.error('Error cargando categorías:', error);
        // Fallback: solo "Otro"
        setCategorias([{ id: 'default', categoria: 'Otro', categoria_padre: null, formulario_pitch: false }]);
        setCategoriaSeleccionada('Otro');
      } finally {
        setLoadingCategorias(false);
      }
    };
    
    // Se cargan también en el sub-tab Pitch porque el campo "Especifica por qué
    // no se matriculó" reutiliza estas mismas categorías de config_categorias.
    const enPitch = activeTab === 'info' && subActiveTab === 'pitch';
    if (isOpen && (activeTab === 'seguimiento' || activeTab === 'recordatorio' || enPitch)) {
      cargarCategorias();
    }
  }, [isOpen, activeTab, subActiveTab]);

  // Cargar URL de booking del usuario
  const fetchBookingUrl = useCallback(async () => {
    if (!userEmail) return;
    
    try {
      setLoadingUrls(true);
      
      // Usar el comercial asignado al lead, no el usuario logueado
      const comercialEmail = lead?.comercial_email;
      if (!comercialEmail) {
        setUrlBooking(null);
        return;
      }
      
      const { data, error } = await supabase
        .from('usuarios')
        .select('url_booking')
        .eq('email', comercialEmail)
        .single();
      
      if (error) throw error;
      
      setUrlBooking(data?.url_booking || null);
    } catch (error) {
      console.error('Error cargando URL de booking:', error.message);
      setUrlBooking(null);
    } finally {
      setLoadingUrls(false);
    }
  }, [lead?.comercial_email]);

  // Cargar URL de booking cuando se abre el sidebar y el lead está listo para agendar
  useEffect(() => {
    if (isOpen && mostrarBotonAgendar && lead?.comercial_email) {
      fetchBookingUrl();
    }
  }, [isOpen, mostrarBotonAgendar, lead?.comercial_email, fetchBookingUrl]);

  // Cargar recordatorios del lead para el calendario
  const fetchRecordatoriosCalendario = useCallback(async () => {
    if (!lead?.card_id) return;
    
    try {
      setLoadingRecordatoriosCalendario(true);
      const { data, error } = await supabase
        .from('recordatorios')
        .select('*')
        .eq('lead_id', lead.card_id)
        .order('fecha_programada', { ascending: true });
      
      if (error) throw error;
      setRecordatoriosDelLead(data || []);
    } catch (error) {
      console.error('Error cargando recordatorios:', error.message);
    } finally {
      setLoadingRecordatoriosCalendario(false);
    }
  }, [lead?.card_id]);

  // Función para verificar si hay recordatorio activo
  const fetchRecordatorioActivo = useCallback(async () => {
    if (!lead?.card_id) return;
    
    try {
      setLoadingRecordatorioActivo(true);
      
      const { data, error } = await supabase
        .from('recordatorios')
        .select('*')
        .eq('lead_id', lead.card_id)
        .eq('estado', 'Programado')
        .limit(1)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error verificando recordatorio activo:', error);
      }
      
      setRecordatorioActivo(data || null);
    } catch (error) {
      console.error('Error:', error);
      setRecordatorioActivo(null);
    } finally {
      setLoadingRecordatorioActivo(false);
    }
  }, [lead?.card_id]);

  // Cargar recordatorios cuando se abre el tab
  useEffect(() => {
    if (isOpen && lead?.card_id && activeTab === 'recordatorio') {
      fetchRecordatoriosCalendario();
      fetchRecordatorioActivo();
      // Resetear formulario y sub-tab
      setFechaSeleccionada(null);
      setHoraInput('09');
      setMinutoInput('00');
      setPeriodoInput('AM');
      setTextoRecordatorio('');
      setErrorRecordatorio(null);
      setSiglaLlamada(SIGLA_LLAMADA_DEFAULT);
      setSiglaMensaje(SIGLA_MENSAJE_DEFAULT);
      setSiglaDropdownOpen(null);
      setSubTabRecordatorio('programar'); // Siempre iniciar en "Programar"
      
      // Scroll al inicio para el tab de recordatorio
      setTimeout(() => {
        if (sidebarContentRef.current) {
          sidebarContentRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [isOpen, lead?.card_id, activeTab, fetchRecordatoriosCalendario, fetchRecordatorioActivo]);

  // Cargar configuración de recordatorio automático (TICI)
  const fetchConfigRecordatorioAuto = useCallback(async () => {
    if (!lead?.card_id || !lead?.numero_de_recordatorio_automatico || !lead?.fase_nombre_pipefy) {
      setConfigRecordatorioAuto(null);
      return;
    }
    
    try {
      setLoadingConfigTici(true);
      
      const { data, error } = await supabase
        .from('config_recordatorios_automaticos')
        .select('*')
        .eq('numero', lead.numero_de_recordatorio_automatico)
        .eq('fase', lead.fase_nombre_pipefy)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Error cargando config TICI:', error);
      }
      
      setConfigRecordatorioAuto(data || null);
    } catch (error) {
      console.error('Error en fetchConfigRecordatorioAuto:', error);
      setConfigRecordatorioAuto(null);
    } finally {
      setLoadingConfigTici(false);
    }
  }, [lead?.card_id, lead?.numero_de_recordatorio_automatico, lead?.fase_nombre_pipefy]);

  // Cargar historial de recordatorios automáticos (TICI)
  const fetchHistorialTici = useCallback(async () => {
    if (!lead?.card_id) return;
    
    try {
      setLoadingHistorialTici(true);
      
      const { data, error } = await supabase
        .from('recordatorios_automaticos')
        .select('*')
        .eq('card_id', lead.card_id)
        .order('numero', { ascending: true });
      
      if (error) {
        console.error('Error cargando historial TICI:', error);
        setHistorialTici({});
        return;
      }
      
      // Agrupar por fase (nombre) - incluir TODAS las fases que tengan registros
      const agrupado = {};
      (data || []).forEach(r => {
        const faseKey = r.fase || 'Sin fase';
        if (!agrupado[faseKey]) {
          agrupado[faseKey] = [];
        }
        agrupado[faseKey].push(r);
      });
      
      // Ordenar registros dentro de cada fase por numero
      Object.keys(agrupado).forEach(fase => {
        agrupado[fase].sort((a, b) => (a.numero || 0) - (b.numero || 0));
      });
      
      setHistorialTici(agrupado);
      
      // Inicializar índices en 0 para cada fase
      const indices = {};
      Object.keys(agrupado).forEach(fase => {
        indices[fase] = 0;
      });
      setIndiceTiciPorFase(indices);
      
    } catch (error) {
      console.error('Error en fetchHistorialTici:', error);
      setHistorialTici({});
    } finally {
      setLoadingHistorialTici(false);
    }
  }, [lead?.card_id]);

  // Cargar datos de TICI cuando se abre el tab
  useEffect(() => {
    if (isOpen && lead?.card_id && activeTab === 'tici') {
      fetchConfigRecordatorioAuto();
      fetchHistorialTici();
      setSubTabTici('programado');
    }
  }, [isOpen, lead?.card_id, activeTab, fetchConfigRecordatorioAuto, fetchHistorialTici]);

  // Countdown en tiempo real para recordatorio automático
  useEffect(() => {
    if (!lead?.fecha_recordatorio_automatico || activeTab !== 'tici') {
      setTiempoRestante({ dias: 0, horas: 0, minutos: 0, segundos: 0 });
      return;
    }

    const calcularTiempoRestante = () => {
      const fechaRecordatorio = new Date(lead.fecha_recordatorio_automatico);
      const ahora = new Date();
      const diffMs = fechaRecordatorio - ahora;
      
      if (diffMs <= 0) {
        setTiempoRestante({ dias: 0, horas: 0, minutos: 0, segundos: 0 });
        return;
      }
      
      const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const segundos = Math.floor((diffMs % (1000 * 60)) / 1000);
      
      setTiempoRestante({ dias, horas, minutos, segundos });
    };

    calcularTiempoRestante();
    const interval = setInterval(calcularTiempoRestante, 1000);
    
    return () => clearInterval(interval);
  }, [lead?.fecha_recordatorio_automatico, activeTab]);

  // Handler para posponer recordatorio automático
  const handlePosponerRecordatorio = async () => {
    if (!configRecordatorioAuto || !lead) return;
    
    try {
      setPosponiendo(true);
      
      const payload = {
        ...configRecordatorioAuto,
        pais: lead.pais,
        current_phase: lead.current_phase,
        telefono: lead.telefono,
        card_id: lead.card_id,
        nombre: lead.nombre,
        fase_nombre_pipefy: lead.fase_nombre_pipefy,
        fecha_recordatorio_automatico: lead.fecha_recordatorio_automatico
      };
      
      const response = await fetch('https://api.mdenglish.us/webhook/posponer_recordatorio_automatico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Error al posponer recordatorio');
      }
      
      // Esperar y parsear la respuesta del webhook
      const result = await response.json();
      
      // Verificar que el webhook respondió correctamente
      if (result?.result === 'ok') {
        // Refrescar datos del lead directamente desde la base de datos
        await refreshLeadData();
        // También refrescar la configuración del recordatorio
        fetchConfigRecordatorioAuto();
        // Refrescar datos del dashboard en background
        onRefreshData?.();
        // Mostrar toast de éxito
        setToastMessage('Recordatorio pospuesto exitosamente');
        setTimeout(() => setToastMessage(null), 3000);
      } else {
        throw new Error('Respuesta inesperada del webhook');
      }
      
    } catch (error) {
      console.error('Error posponiendo recordatorio:', error);
      setToastMessage('Error al posponer el recordatorio');
      setTimeout(() => setToastMessage(null), 3000);
    } finally {
      setPosponiendo(false);
    }
  };

  // Función para programar recordatorio
  const handleProgramarRecordatorio = async () => {
    if (!fechaSeleccionada || !textoRecordatorio.trim() || !lead?.card_id) return;
    if (!siglaLlamada && !siglaMensaje) {
      setErrorRecordatorio('Elige el resultado de la llamada o del mensaje');
      return;
    }
    
    // Convertir hora AM/PM a formato 24h
    let hora24 = parseInt(horaInput);
    if (periodoInput === 'PM' && hora24 !== 12) {
      hora24 += 12;
    } else if (periodoInput === 'AM' && hora24 === 12) {
      hora24 = 0;
    }
    const minutos = parseInt(minutoInput);
    
    // Validar que la hora sea válida (mínimo 10 min en el futuro si es hoy)
    if (!isTimeValid(fechaSeleccionada, hora24, minutos)) {
      setErrorRecordatorio('La hora debe ser al menos 10 minutos en el futuro');
      return;
    }
    
    try {
      setProgramandoRecordatorio(true);
      setErrorRecordatorio(null);
      
      // Combinar fecha y hora
      const fechaCompleta = new Date(fechaSeleccionada);
      fechaCompleta.setHours(hora24, minutos, 0, 0);
      
      // Crear recordatorio (el trigger en BD creará el comentario automáticamente)
      const nuevoRecordatorioData = {
        lead_id: lead.card_id,
        fecha_programada: fechaCompleta.toISOString(),
        observacion: textoRecordatorio.trim(),
        creado_por: userEmail,
        estado: 'Programado',
        fase: lead.fase_nombre_pipefy || null,
        etapa_funnel: lead.etapa_funnel || null,
        categoria: categoriaSeleccionada || 'Otro',
        ...camposCanal(siglaLlamada, siglaMensaje),
      };
      
      const { error: errorRecordatorioInsert } = await supabase
        .from('recordatorios')
        .insert([nuevoRecordatorioData]);
      
      if (errorRecordatorioInsert) throw errorRecordatorioInsert;

      // El trigger crea el comentario con la observación, pero no copia
      // llamada/mensaje. Se escriben en el comentario recién creado.
      try {
        const observacion = nuevoRecordatorioData.observacion;
        const { data: comentariosRec } = await supabase
          .from('comentarios')
          .select('id, origen, llamada, mensaje')
          .eq('lead_id', lead.card_id)
          .eq('texto', observacion)
          .order('created_at', { ascending: false })
          .limit(8);
        const destino = (comentariosRec || []).find((c) =>
          /recordatorio/i.test(c.origen || '') && !c.llamada && !c.mensaje
        ) || (comentariosRec || []).find((c) => /recordatorio/i.test(c.origen || '')) || comentariosRec?.[0];
        if (destino?.id) {
          await supabase
            .from('comentarios')
            .update(camposCanal(siglaLlamada, siglaMensaje))
            .eq('id', destino.id);
        }
      } catch (copyErr) {
        console.error('Error copiando canal al comentario del recordatorio:', copyErr);
      }
      
      // 3. Contar recordatorios del lead en la fase actual
      const { count: conteoFaseActual, error: errorConteo } = await supabase
        .from('recordatorios')
        .select('*', { count: 'exact', head: true })
        .eq('lead_id', lead.card_id)
        .eq('fase', lead.fase_nombre_pipefy || '');
      
      if (errorConteo) throw errorConteo;
      
      // 4. Actualizar lead con el conteo correcto y activar recordatorio
      const { error: errorLead } = await supabase
        .from('leads')
        .update({
          fecha_programada_recordatorio: fechaCompleta.toISOString(),
          estado_gestion: 'gestionado',
          conteo_recordatorios: conteoFaseActual || 0,
          recordatorio_activo: true
        })
        .eq('card_id', lead.card_id);
      
      if (errorLead) throw errorLead;
      
      // Enviar al webhook de Respond
      let respondOk = false;
      try {
        const webhookResponse = await fetch('https://api.mdenglish.us/webhook/crear_seguimiento_portal_a_respond', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: null,
            lead_id: lead.card_id,
            texto: textoRecordatorio.trim(),
            autor_email: userEmail,
            autor_nombre: userName,
            origen: 'Recordatorio',
            fase: lead.fase_nombre_pipefy || null,
            etapa_funnel: lead.etapa_funnel || null,
            categoria: categoriaSeleccionada || 'Otro',
            ...camposCanal(siglaLlamada, siglaMensaje),
            created_at: new Date().toISOString(),
            lead_nombre: lead.nombre,
            lead_telefono: lead.telefono,
            respond_io_url: lead.respond_io_url || null,
            fecha_programada: fechaCompleta.toISOString()
          })
        });
        const webhookResult = await webhookResponse.json();
        if (webhookResult?.result === 'ok') {
          respondOk = true;
        }
      } catch (webhookError) {
        console.error('Error enviando webhook recordatorio:', webhookError);
      }
      
      // Toast de éxito (unificado si Respond respondió ok)
      const mensajeToast = respondOk 
        ? '¡Listo! El recordatorio ya está registrado en OV y Respond.'
        : 'Se creó tu recordatorio exitosamente';
      setToastMessage(mensajeToast);
      setTimeout(() => setToastMessage(null), 3000);
      
      // Éxito - limpiar formulario
      setFechaRecordatorioCreado(fechaCompleta);
      setFechaSeleccionada(null);
      setTextoRecordatorio('');
      setHoraInput('09');
      setMinutoInput('00');
      setPeriodoInput('AM');
      setSiglaLlamada(SIGLA_LLAMADA_DEFAULT);
      setSiglaMensaje(SIGLA_MENSAJE_DEFAULT);
      setSiglaDropdownOpen(null);
      fetchRecordatoriosCalendario();
      fetchRecordatorioActivo(); // Actualizar estado de recordatorio activo
      
      // Refrescar dashboard silenciosamente
      onRefreshData?.();
      
    } catch (error) {
      console.error('Error programando recordatorio:', error);
      setErrorRecordatorio('No se pudo programar el recordatorio, intente nuevamente');
    } finally {
      setProgramandoRecordatorio(false);
    }
  };

  // Función para cargar historial de recordatorios
  const HISTORIAL_PER_PAGE = 10;
  
  const fetchHistorialRecordatorios = useCallback(async (page = 0) => {
    if (!lead?.card_id) return;
    
    try {
      setLoadingHistorial(true);
      
      const from = page * HISTORIAL_PER_PAGE;
      const to = from + HISTORIAL_PER_PAGE - 1;
      
      const { data, error, count } = await supabase
        .from('recordatorios')
        .select('*', { count: 'exact' })
        .eq('lead_id', lead.card_id)
        .order('fecha_programada', { ascending: false })
        .range(from, to);
      
      if (error) throw error;
      
      setHistorialRecordatorios(data || []);
      setTotalHistorial(count || 0);
      setHistorialPage(page);
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setLoadingHistorial(false);
    }
  }, [lead?.card_id]);

  // Función para cancelar un recordatorio
  const handleCancelarRecordatorio = async (recordatorioId) => {
    if (!recordatorioId || !lead?.card_id) return;
    
    try {
      setCancelandoRecordatorio(recordatorioId);
      setConfirmarCancelacion(null); // Cerrar modal de confirmación
      
      // 1. Actualizar estado del recordatorio a "Cancelado"
      const { error: errorUpdate } = await supabase
        .from('recordatorios')
        .update({ estado: 'Cancelado' })
        .eq('id', recordatorioId);
      
      if (errorUpdate) throw errorUpdate;
      
      // 2. Actualizar lead (solo puede haber 1 recordatorio activo por lead)
      const { error: errorLead, data: dataLead } = await supabase
        .from('leads')
        .update({ 
          recordatorio_activo: false,
          estado_gestion: 'sin_gestionar',
          revisado: false,
          fecha_asignacion: new Date().toISOString()
        })
        .eq('card_id', lead.card_id)
        .select();
      
      
      if (errorLead) {
        console.error('Error actualizando lead:', errorLead);
      }
      
      // 3. Recargar historial, calendario y limpiar estado activo
      fetchHistorialRecordatorios(historialPage);
      fetchRecordatoriosCalendario();
      setRecordatorioActivo(null); // Ya no hay recordatorio activo
      
      // Refrescar dashboard silenciosamente
      onRefreshData?.();
      
    } catch (error) {
      console.error('Error cancelando recordatorio:', error);
    } finally {
      setCancelandoRecordatorio(null);
    }
  };

  // Cargar historial cuando se cambia a ese sub-tab
  useEffect(() => {
    if (subTabRecordatorio === 'historial' && lead?.card_id) {
      fetchHistorialRecordatorios(0);
    }
  }, [subTabRecordatorio, lead?.card_id, fetchHistorialRecordatorios]);

  // Helpers para el calendario
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const isDateValid = (date) => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 6);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate >= today && checkDate <= maxDate;
  };
  
  // Validar si la hora seleccionada es válida (al menos 10 minutos en el futuro si es hoy)
  const isTimeValid = (date, hour, minute) => {
    if (!date) return true;
    const now = new Date();
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hour, minute, 0, 0);
    
    // Si la fecha es hoy, verificar que sea al menos 10 minutos en el futuro
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (checkDate.getTime() === today.getTime()) {
      const minTime = new Date(now.getTime() + 10 * 60 * 1000); // 10 minutos desde ahora
      return selectedDateTime >= minTime;
    }
    return true;
  };

  const hasRecordatorio = (day) => {
    const checkDate = new Date(mesActual.getFullYear(), mesActual.getMonth(), day);
    return recordatoriosDelLead.some(r => {
      const rDate = new Date(r.fecha_programada);
      return rDate.getFullYear() === checkDate.getFullYear() &&
             rDate.getMonth() === checkDate.getMonth() &&
             rDate.getDate() === checkDate.getDate();
    });
  };

  const getRecordatorioForDay = (day) => {
    const checkDate = new Date(mesActual.getFullYear(), mesActual.getMonth(), day);
    return recordatoriosDelLead.find(r => {
      const rDate = new Date(r.fecha_programada);
      return rDate.getFullYear() === checkDate.getFullYear() &&
             rDate.getMonth() === checkDate.getMonth() &&
             rDate.getDate() === checkDate.getDate();
    });
  };

  // Formatear fecha para mostrar
  const formatearFechaRecordatorio = (fecha) => {
    if (!fecha) return 'Ahora';
    const date = new Date(fecha);
    if (isNaN(date.getTime())) return 'Ahora';
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Efecto para manejar apertura/cierre del sidebar
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Prevenir scroll del body cuando el sidebar está abierto
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      // Cancelar cualquier edición pendiente al cerrar el sidebar
      setEditingField(null);
      setEditValue('');
      setSavingField(null);
      setSearchSelectQuery('');
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  // Efecto separado para resetear el tab solo cuando cambia el lead (card_id diferente)
  useEffect(() => {
    if (isOpen && leadProp?.card_id) {
      setActiveTab(initialTab);
    }
  }, [leadProp?.card_id, isOpen, initialTab]);

  const handleClose = () => {
    // Cancelar cualquier edición pendiente antes de cerrar
    setEditingField(null);
    setEditValue('');
    setSavingField(null);
    setSearchSelectQuery('');
    
    setIsAnimating(false);
    setTimeout(onClose, 300); // Esperar a que termine la animación
  };

  if (!isOpen && !isAnimating) return null;
  if (!lead) return null;

  const gestionStatus = getGestionStatus(lead);
  const statusBadge = statusBadges[gestionStatus];

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isAnimating && isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Sidebar */}
      <div 
        className={`fixed top-0 right-0 h-full w-full md:w-[600px] lg:w-[650px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isAnimating && isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Contenedor con scroll */}
        <div className="h-full flex flex-col overflow-hidden">
          
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-start justify-between gap-2 sm:gap-4">
              {/* Info del lead */}
              <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                {/* Avatar con bandera - oculto en mobile */}
                <div className="relative hidden sm:block flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-3xl shadow-lg">
                    {getCountryFlag(lead.pais)}
                  </div>
                </div>
                
                {/* Nombre y ocupación */}
                <div className="flex-1 min-w-0">
                  {editingField === 'nombre' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="flex-1 text-lg sm:text-xl font-bold text-slate-800 bg-slate-100 px-2 py-1 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-[#1717AF]/30 min-w-0"
                        autoFocus
                        placeholder="Nombre del lead"
                      />
                      <button
                        onClick={() => handleSaveField('nombre', editValue)}
                        className="p-1.5 bg-[#1717AF] text-white rounded-lg hover:bg-[#1717AF]/90 flex-shrink-0"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1.5 bg-slate-200 text-slate-500 rounded-lg hover:bg-slate-300 flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <h2 
                      className="text-lg sm:text-xl font-bold text-slate-800 cursor-pointer hover:text-[#1717AF] transition-colors group flex items-center gap-2 truncate"
                      onClick={() => startEditing('nombre', getFieldValue('nombre'))}
                    >
                      <span className="truncate">{getFieldValue('nombre') || 'Sin nombre'}</span>
                      <Edit2 size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </h2>
                  )}
                  
                  {/* País en mobile (visible solo en mobile) */}
                  <p className="text-xs text-slate-400 sm:hidden mt-0.5">{lead.pais || 'Sin país'}</p>
                  
                  {/* Comercial asignado con dropdown de reasignación (disponible para todos) */}
                  <div className="relative comercial-dropdown mt-1">
                    {comerciales.length > 0 ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setComercialDropdownOpen(!comercialDropdownOpen);
                          }}
                          className="text-sm text-slate-500 flex items-center gap-1.5 hover:text-[#1717AF] transition-colors"
                        >
                          <Users size={14} />
                          <span>De: {nombreComercialActual}</span>
                          <ChevronDown size={14} className={`transition-transform duration-200 ${comercialDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {/* Dropdown de comerciales */}
                        {comercialDropdownOpen && (
                          <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-50 max-h-80 overflow-y-auto">
                            <div className="px-4 py-2 border-b border-slate-100">
                              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Reasignar a:</p>
                            </div>
                            {comerciales
                              .filter(c => c.email !== lead?.comercial_email)
                              .map((comercial) => {
                                // Solo mostrar indicador de conexión para comerciales (puede_ver_todos = false)
                                const mostrarConexion = comercial.puede_ver_todos !== true;
                                const online = mostrarConexion ? isUserOnline(comercial.ultima_conexion) : false;
                                const connectionStatus = mostrarConexion ? formatLastConnection(comercial.ultima_conexion) : null;
                                
                                return (
                                  <button
                                    key={comercial.email}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setComercialSeleccionado(comercial);
                                      setMostrarModalReasignar(true);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-slate-50 ${
                                      comercialSeleccionado?.email === comercial.email
                                        ? 'bg-[#1717AF]/10 text-[#1717AF]'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {mostrarConexion ? (
                                      <>
                                        <div className="flex items-center gap-2">
                                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                          <span className="font-medium">{comercial.nombre}</span>
                                        </div>
                                        <div className={`text-xs ml-4 ${online ? 'text-emerald-600' : 'text-slate-400'}`}>
                                          {connectionStatus}
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="font-medium">{comercial.nombre}</div>
                                        <div className="text-xs text-slate-400 truncate">{comercial.email}</div>
                                      </>
                                    )}
                                  </button>
                                );
                              })}
                            {comerciales.filter(c => c.email !== lead?.comercial_email).length === 0 && (
                              <p className="px-4 py-3 text-sm text-slate-400 text-center">No hay otros comerciales disponibles</p>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 flex items-center gap-1.5">
                        <Users size={14} />
                        <span>De: {nombreComercialActual}</span>
                      </p>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                    <Briefcase size={14} />
                    {lead.ocupacion || 'Ocupación no especificada'}
                  </p>
                  {/* Badges de fase y tag */}
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {/* Dropdown de fase */}
                    <div className="relative">
                      <button
                        onClick={() => setFaseDropdownOpen(!faseDropdownOpen)}
                        disabled={cambiandoFase}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 ${
                          cambiandoFase 
                            ? 'bg-slate-100 text-slate-400 cursor-wait'
                            : 'bg-[#1717AF]/10 text-[#1717AF] hover:bg-[#1717AF]/20 cursor-pointer'
                        }`}
                      >
                        {cambiandoFase ? (
                          <RefreshCcw size={12} className="animate-spin" />
                        ) : (
                          <ChevronDown size={12} className={`transition-transform ${faseDropdownOpen ? 'rotate-180' : ''}`} />
                        )}
                        {faseLocal || lead.fase_nombre_pipefy || 'Sin fase'}
                      </button>
                      
                      {/* Dropdown de fases */}
                      {faseDropdownOpen && (
                        <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 max-h-64 overflow-y-auto">
                          {funnelSteps.map((fase) => (
                            <button
                              key={fase.id}
                              onClick={() => handleCambiarFase(fase.label || fase.id)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                                (faseLocal || lead.fase_nombre_pipefy) === (fase.label || fase.id)
                                  ? 'bg-[#1717AF]/5 text-[#1717AF] font-medium'
                                  : 'text-slate-700'
                              }`}
                            >
                              {fase.label || fase.id}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Tag del lead con colores dinámicos */}
                    {lead.label && (() => {
                      const tagConfig = configTags[lead.label];
                      const bgColor = tagConfig?.color_tag || '#8B5CF6'; // Violeta por defecto
                      const textColor = tagConfig?.color_letra_tag || '#FFFFFF'; // Blanco por defecto
                      return (
                        <span 
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: bgColor, color: textColor }}
                        >
                          <Tag size={10} />
                          {lead.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              {/* Acciones y cerrar */}
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {/* Acciones rápidas - más compactas en mobile */}
                <div className="flex items-center gap-0.5 sm:gap-1 sm:mr-2">
                  {/* Marcar como HOT 🔥 */}
                  <button
                    onClick={handleToggleHot}
                    disabled={togglingHot}
                    className={`p-2 sm:p-2.5 rounded-xl transition-all duration-200 ${
                      isHot
                        ? 'text-orange-600 bg-gradient-to-br from-orange-100 to-red-100 shadow-md shadow-orange-200 ring-2 ring-orange-300'
                        : 'text-slate-400 bg-slate-50 hover:bg-orange-50 hover:text-orange-500'
                    }`}
                    title={isHot ? "Quitar marca de lead caliente" : "Marcar como lead caliente"}
                  >
                    <Flame size={18} className={`sm:w-5 sm:h-5 ${togglingHot ? 'animate-pulse' : ''}`} />
                  </button>
                  
                  {/* Marcar como pendiente - oculto en mobile muy pequeño */}
                  {onMarcarNoRevisado && (
                    <button
                      onClick={() => lead.revisado !== false && onMarcarNoRevisado(lead)}
                      disabled={lead.revisado === false}
                      className={`hidden xs:block p-2 sm:p-2.5 rounded-xl transition-all duration-200 ${
                        lead.revisado === false
                          ? 'text-slate-300 bg-slate-50 cursor-not-allowed'
                          : 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                      }`}
                      title={lead.revisado === false ? "Este lead ya está pendiente" : "Marcar como no leído"}
                    >
                      <RotateCcw size={18} className="sm:w-5 sm:h-5" />
                    </button>
                  )}
                  
                  {/* WhatsApp con contador de ventana 24h */}
                  <WhatsAppButtonSidebar lead={lead} size={18} onCrearRespond={handleCrearRespond} />
                  <WhatsAppWebButton
                    telefono={getFieldValue('telefono') || lead.telefono}
                    size={18}
                    className="p-2 sm:p-2.5 rounded-xl transition-all duration-200"
                  />
                </div>
                
                {/* Botón cerrar - siempre visible */}
                <button
                  onClick={handleClose}
                  className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all duration-200"
                >
                  <X size={22} className="sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Banner "Listo para agendar" - solo cuando el lead está en fases de pitch */}
          {mostrarBotonAgendar && urlBooking && (
            <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-200">
                    <CalendarPlus size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">¡Listo para agendar!</p>
                    <p className="text-xs text-emerald-600">Este lead está listo para agendar un Pitch</p>
                  </div>
                </div>
                <button
                  onClick={() => window.open(urlBooking, '_blank')}
                  className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 transition-all duration-200 shadow-lg shadow-emerald-200 flex items-center gap-2"
                >
                  <CalendarPlus size={16} />
                  Agendar
                </button>
              </div>
            </div>
          )}

          {/* Banner "Pitch agendado" - cuando el lead tiene pitch programado */}
          {mostrarPitchAgendado && (
            <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-200">
                    <CalendarCheck size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">¡Pitch agendado!</p>
                    <p className="text-xs text-amber-600">
                      Tienes un nuevo Pitch agendado para el <span className="font-semibold">{formatFechaPitch(lead?.fecha_pitch)}</span>
                    </p>
                    <p className="text-xs text-amber-700 font-medium mt-0.5">¡Vamos por esa matrícula! 🎯</p>
                  </div>
                </div>
                {lead?.link_pitch && (
                  <button
                    onClick={() => window.open(lead.link_pitch, '_blank')}
                    className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-xl hover:bg-amber-600 transition-all duration-200 shadow-lg shadow-amber-200 flex items-center gap-2 whitespace-nowrap"
                  >
                    <ExternalLink size={16} />
                    Ir al Pitch
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Stepper del funnel - Dinámico */}
          {stepperSteps.length > 0 && (
            <div className="flex-shrink-0 px-6 pt-5 pb-14 bg-slate-50/50 border-b border-slate-100">
              <div className="flex items-center justify-between">
                {stepperSteps.map((step, index) => {
                  const isLast = index === stepperSteps.length - 1;
                  const isFuturo = step.tipo === 'futuro';
                  
                  // Obtener colores (soporta hex o nombre)
                  const colores = isFuturo 
                    ? getStepperColors('gris')
                    : getStepperColors(stepperColor);
                  
                  const colorHex = colores.hex;
                  const grisHex = '#CBD5E1';
                  const labelText = step.labelReal || step.label;

                  return (
                    <div key={`${step.faseId}-${index}`} className="flex items-center flex-1 last:flex-none">
                      {/* Step */}
                      <div className="flex flex-col items-center relative group">
                        {/* Círculo/Dot */}
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${colores.bgLight}`}
                          style={{ 
                            backgroundColor: isFuturo ? '#E2E8F0' : undefined,
                            opacity: isFuturo ? 0.7 : 1
                          }}
                          title={labelText}
                        >
                          {isFuturo ? (
                            <Circle size={14} className="text-slate-400" />
                          ) : (
                            <div 
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: colorHex }}
                            />
                          )}
                        </div>
                        
                        {/* Tooltip para mobile (visible al hacer tap) */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[10px] font-medium rounded-lg opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200 pointer-events-none z-50 whitespace-nowrap max-w-[120px] text-center truncate sm:hidden">
                          {labelText}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                        </div>
                        
                        {/* Label corto (oculto en mobile, visible en desktop) */}
                        <span 
                          className="absolute -bottom-8 text-[9px] font-medium text-center leading-tight max-w-[65px] line-clamp-3 hidden sm:block"
                          style={{ color: isFuturo ? '#94A3B8' : colorHex }}
                          title={labelText}
                        >
                          {labelText}
                        </span>
                      </div>
                      
                      {/* Línea conectora */}
                      {!isLast && (
                        <div 
                          className="flex-1 h-0.5 mx-0.5 sm:mx-1 transition-colors duration-300"
                          style={{ backgroundColor: isFuturo ? grisHex : colorHex }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex-shrink-0 px-6 pt-4 border-b border-slate-100">
            <div className="flex gap-1">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-medium transition-all duration-200 ${
                      isActive 
                        ? 'bg-white text-[#1717AF] border-t border-l border-r border-slate-200 -mb-px' 
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <IconComponent size={16} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contenido del tab - con scroll */}
          <div ref={sidebarContentRef} className="flex-1 overflow-y-auto p-6">
            {activeTab === 'info' && (
              <div className="flex flex-col h-full">
                {/* Sub-tabs */}
                <div className="flex gap-1 mb-4 flex-shrink-0 p-1 bg-slate-100 rounded-lg">
                  <button
                    onClick={() => setSubActiveTab('informacion')}
                    className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                      subActiveTab === 'informacion'
                        ? 'bg-white text-slate-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Información
                  </button>
                  <button
                    onClick={() => setSubActiveTab('pitch')}
                    className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                      subActiveTab === 'pitch'
                        ? 'bg-white text-slate-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Pitch
                  </button>
                  <button
                    onClick={() => setSubActiveTab('resumen-ia')}
                    className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                      subActiveTab === 'resumen-ia'
                        ? 'bg-white text-slate-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Resumen IA
                  </button>
                </div>

                {/* Contenido del sub-tab */}
                <div className="flex-1 overflow-y-auto">
                  {subActiveTab === 'informacion' && (
                    <div className="space-y-5">
                      {/* Versión dinámica del formulario Info General */}
                      {loadingInfoGeneral ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 size={24} className="animate-spin text-[#1717AF]" />
                        </div>
                      ) : infoGeneralFormulario ? (
                        /* FORMULARIO DINÁMICO */
                        (() => {
                          // Obtener valor del campo desde el lead
                          const getFieldValueDynamic = (field) => {
                            if (!field.campo_origen) return null;
                            const [tabla, columna] = field.campo_origen.split(',').map(s => s.trim());
                            if (tabla === 'leads' && lead) {
                              return localLeadData[columna] !== undefined ? localLeadData[columna] : lead[columna];
                            }
                            return null;
                          };
                          
                          // Verificar si un campo debe mostrarse (dependencias)
                          const shouldShowField = (field) => {
                            if (!field.dinamico || !field.depende_de) return true;
                            const dep = parseDependeDeField(field.depende_de);
                            if (!dep) return true;
                            if (dep.valor === null) return true;
                            
                            const parentField = infoGeneralFields.find(f => f.nombre === dep.fieldName);
                            if (!parentField) return true;
                            
                            const parentValue = getFieldValueDynamic(parentField);
                            return parentValue === dep.valor;
                          };
                          
                          // Renderizar un campo según su tipo
                          const renderDynamicField = (field) => {
                            if (!shouldShowField(field)) return null;
                            
                            const value = getFieldValueDynamic(field);
                            const displayValue = value || 'No disponible';
                            const isEditing = editingField === field.nombre;
                            const isSaving = savingField === field.nombre;
                            const [, columna] = (field.campo_origen || ',').split(',').map(s => s.trim());
                            
                            // Campo tipo SELECT
                            if (field.tipo === 'select' && field.opciones) {
                              let parsedOpciones = parseOpcionesField(field.opciones);
                              const dep = parseDependeDeField(field.depende_de);
                              if (dep && dep.valor === null) {
                                const parentField = infoGeneralFields.find(f => f.nombre === dep.fieldName);
                                const parentValue = parentField ? getFieldValueDynamic(parentField) : null;
                                if (parentValue) {
                                  parsedOpciones = parsedOpciones.filter(o => o.grupo === parentValue);
                                }
                              }
                              const opciones = parsedOpciones.map(o => o.value);
                              const filteredOptions = opciones.filter(opt => 
                                opt.toLowerCase().includes(infoGeneralSearchQuery.toLowerCase())
                              );
                              
                              if (isEditing) {
                                return (
                                  <div key={field.id} className="relative p-3 bg-white rounded-xl border border-[#1717AF]/30 shadow-sm shadow-[#1717AF]/5 transition-all duration-200">
                                    <div className="flex items-center justify-between mb-2">
                                      <p className="text-[10px] uppercase tracking-wider text-[#1717AF] font-semibold">{field.nombre}</p>
                                      <button
                                        onClick={cancelEditing}
                                        className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100 transition-colors"
                                      >
                                        <X size={12} />
                                      </button>
                                    </div>
                                    <div className="relative mb-2">
                                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                      <input
                                        ref={infoGeneralSearchRef}
                                        type="text"
                                        value={infoGeneralSearchQuery}
                                        onChange={(e) => setInfoGeneralSearchQuery(e.target.value)}
                                        placeholder="Buscar..."
                                        className="w-full pl-7 pr-2 py-1.5 text-sm bg-slate-50 border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30"
                                        autoFocus
                                      />
                                    </div>
                                    <div className="max-h-36 overflow-y-auto rounded-lg bg-slate-50">
                                      {filteredOptions.length > 0 ? (
                                        filteredOptions.map((opt) => (
                                          <button
                                            key={opt}
                                            onClick={() => handleSaveField(columna, opt)}
                                            disabled={isSaving}
                                            className={`w-full px-2.5 py-1.5 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                              opt === value 
                                                ? 'bg-[#1717AF] text-white font-medium' 
                                                : 'text-slate-600 hover:bg-slate-100'
                                            }`}
                                          >
                                            {opt}
                                          </button>
                                        ))
                                      ) : (
                                        <p className="px-2.5 py-2 text-xs text-slate-400 text-center">Sin resultados</p>
                                      )}
                                    </div>
                                  </div>
                                );
                              }
                              
                              return (
                                <div 
                                  key={field.id}
                                  className="relative p-3 bg-white rounded-2xl border border-slate-100 group cursor-pointer hover:border-slate-200 hover:shadow-sm transition-all duration-200"
                                  onClick={() => { startEditing(field.nombre, value); setInfoGeneralSearchQuery(''); }}
                                >
                                  <div className="flex items-start gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                                      <ClipboardList size={14} className="text-slate-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">{field.nombre}</p>
                                      <p className={`text-sm leading-snug ${value ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                        {displayValue}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                      {value && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCopy(value, field.nombre); }}
                                          className={`p-1 rounded-md transition-all duration-150 ${
                                            copiedField === field.nombre
                                              ? 'bg-emerald-100 text-emerald-600'
                                              : 'text-slate-400 hover:bg-white hover:text-slate-600'
                                          }`}
                                        >
                                          {copiedField === field.nombre ? <Check size={12} /> : <Copy size={12} />}
                                        </button>
                                      )}
                                      <div className="p-1 text-slate-300">
                                        <ChevronDown size={10} />
                                      </div>
                                    </div>
                                  </div>
                                  {isSaving && (
                                    <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                                      <Loader2 size={14} className="animate-spin text-[#1717AF]" />
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            // Campo tipo TEXTO
                            if (isEditing) {
                              return (
                                <div key={field.id} className="relative p-3 bg-white rounded-xl border border-[#1717AF]/30 shadow-sm shadow-[#1717AF]/5 transition-all duration-200">
                                  <p className="text-[10px] uppercase tracking-wider text-[#1717AF] font-semibold mb-1.5">{field.nombre}</p>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="text"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      className="flex-1 px-2 py-1.5 text-sm bg-slate-100 border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30"
                                      placeholder="No disponible"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => handleSaveField(columna, editValue)}
                                      disabled={isSaving}
                                      className="p-1.5 bg-[#1717AF] text-white rounded-lg hover:bg-[#1717AF]/90 disabled:opacity-50 transition-colors"
                                    >
                                      {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      className="p-1.5 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
                                    >
                                      <X size={12} />
                                    </button>
                                  </div>
                                </div>
                              );
                            }
                            
                            return (
                              <div 
                                key={field.id}
                                className="relative p-3 bg-white rounded-2xl border border-slate-100 group cursor-pointer hover:border-slate-200 hover:shadow-sm transition-all duration-200"
                                onClick={() => startEditing(field.nombre, value)}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                                    <FileText size={14} className="text-slate-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">{field.nombre}</p>
                                    <p className={`text-sm leading-snug ${value ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                      {displayValue}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {value && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleCopy(value, field.nombre); }}
                                        className={`p-1 rounded-md transition-all duration-150 ${
                                          copiedField === field.nombre
                                            ? 'bg-emerald-100 text-emerald-600'
                                            : 'text-slate-400 hover:bg-white hover:text-slate-600'
                                        }`}
                                      >
                                        {copiedField === field.nombre ? <Check size={12} /> : <Copy size={12} />}
                                      </button>
                                    )}
                                    <div className="p-1 text-slate-300">
                                      <Edit2 size={10} />
                                    </div>
                                  </div>
                                </div>
                                {isSaving && (
                                  <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                                    <Loader2 size={14} className="animate-spin text-[#1717AF]" />
                                  </div>
                                )}
                              </div>
                            );
                          };
                          
                          // Si tiene secciones, agrupar por sección
                          if (infoGeneralFormulario.tiene_secciones && infoGeneralSecciones.length > 0) {
                            return (
                              <>
                                {infoGeneralSecciones.map((seccion) => {
                                  const camposSeccion = infoGeneralFields
                                    .filter(f => f.seccion === seccion.nombre)
                                    .sort((a, b) => a.orden - b.orden);
                                  
                                  if (camposSeccion.length === 0) return null;
                                  
                                  return (
                                    <div key={seccion.nombre} className="space-y-2">
                                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
                                        <ClipboardList size={12} className="text-[#1717AF]" />
                                        {seccion.nombre}
                                      </h3>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {camposSeccion.map(field => renderDynamicField(field))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          }
                          
                          // Sin secciones, mostrar todos los campos en orden
                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {infoGeneralFields.map(field => renderDynamicField(field))}
                            </div>
                          );
                        })()
                      ) : (
                      /* FORMULARIO ESTÁTICO (fallback) */
                      (() => {
                        const EditableTextField = ({ fieldName, label, icon: Icon, placeholder = 'No especificado', copyable = false }) => {
                          const value = getFieldValue(fieldName);
                          const isEditing = editingField === fieldName;
                          const isSaving = savingField === fieldName;
                          const displayValue = value || placeholder;
                          const needsTruncate = displayValue.length > 60;
                          
                          if (isEditing) {
                            return (
                              <div className="relative p-3 bg-white rounded-xl border border-[#1717AF]/30 shadow-sm shadow-[#1717AF]/5 transition-all duration-200">
                                <p className="text-[10px] uppercase tracking-wider text-[#1717AF] font-semibold mb-1.5">{label}</p>
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="flex-1 px-2 py-1.5 text-sm bg-slate-100 border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30"
                                    placeholder={placeholder}
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleSaveField(fieldName, editValue)}
                                    disabled={isSaving}
                                    className="p-1.5 bg-[#1717AF] text-white rounded-lg hover:bg-[#1717AF]/90 disabled:opacity-50 transition-colors"
                                  >
                                    {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="p-1.5 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <div 
                              className="relative p-3 bg-white rounded-2xl border border-slate-100 group cursor-pointer hover:border-slate-200 hover:shadow-sm transition-all duration-200 border border-transparent hover:border-slate-200"
                              onClick={() => startEditing(fieldName, value)}
                            >
                              <div className="flex items-start gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                                  <Icon size={14} className="text-slate-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">{label}</p>
                                  <p className={`text-sm leading-snug ${value ? 'text-slate-700' : 'text-slate-400 italic'} line-clamp-2`}>
                                    {needsTruncate ? displayValue.substring(0, 60) + '...' : displayValue}
                                  </p>
                                </div>
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {copyable && value && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(value, fieldName);
                                      }}
                                      className={`p-1 rounded-md transition-all duration-150 ${
                                        copiedField === fieldName
                                          ? 'bg-emerald-100 text-emerald-600'
                                          : 'text-slate-400 hover:bg-white hover:text-slate-600'
                                      }`}
                                    >
                                      {copiedField === fieldName ? <Check size={12} /> : <Copy size={12} />}
                                    </button>
                                  )}
                                  {needsTruncate && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setModalTextoCompleto({ label, value: displayValue });
                                      }}
                                      className="p-1 text-slate-400 hover:text-[#1717AF] rounded-md"
                                    >
                                      <MoreHorizontal size={12} />
                                    </button>
                                  )}
                                  <div className="p-1 text-slate-300">
                                    <Edit2 size={10} />
                                  </div>
                                </div>
                              </div>
                              {isSaving && (
                                <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                                  <Loader2 size={14} className="animate-spin text-[#1717AF]" />
                                </div>
                              )}
                            </div>
                          );
                        };

                        // Componente para campo editable con select y búsqueda - UI mejorada
                        const EditableSelectField = ({ fieldName, label, icon: Icon, options, placeholder = 'No especificado', detailFieldName = null }) => {
                          const value = getFieldValue(fieldName);
                          const isEditing = editingField === fieldName;
                          const isSaving = savingField === fieldName;
                          const displayValue = value || placeholder;
                          const needsTruncate = displayValue.length > 60;
                          
                          const filteredOptions = options.filter(opt => 
                            opt.toLowerCase().includes(searchSelectQuery.toLowerCase())
                          );
                          
                          if (isEditing) {
                            return (
                              <div className="relative p-3 bg-white rounded-xl border border-[#1717AF]/30 shadow-sm shadow-[#1717AF]/5 transition-all duration-200">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-[10px] uppercase tracking-wider text-[#1717AF] font-semibold">{label}</p>
                                  <button
                                    onClick={cancelEditing}
                                    className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100 transition-colors"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                                <div className="relative mb-2">
                                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                  <input
                                    type="text"
                                    value={searchSelectQuery}
                                    onChange={(e) => setSearchSelectQuery(e.target.value)}
                                    placeholder="Buscar..."
                                    className="w-full pl-7 pr-2 py-1.5 text-sm bg-slate-50 border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30"
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-36 overflow-y-auto rounded-lg bg-slate-50">
                                  {filteredOptions.length > 0 ? (
                                    filteredOptions.map((opt) => (
                                      <button
                                        key={opt}
                                        onClick={() => {
                                          handleSaveField(fieldName, opt);
                                          if (opt !== 'Otro' && detailFieldName && value === 'Otro') {
                                            handleSaveField(detailFieldName, null);
                                          }
                                        }}
                                        disabled={isSaving}
                                        className={`w-full px-2.5 py-1.5 text-left text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                          opt === value 
                                            ? 'bg-[#1717AF] text-white font-medium' 
                                            : 'text-slate-600 hover:bg-slate-100'
                                        }`}
                                      >
                                        {opt}
                                      </button>
                                    ))
                                  ) : (
                                    <p className="px-2.5 py-2 text-xs text-slate-400 text-center">Sin resultados</p>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          return (
                            <div 
                              className="relative p-3 bg-white rounded-2xl border border-slate-100 group cursor-pointer hover:border-slate-200 hover:shadow-sm transition-all duration-200 border border-transparent hover:border-slate-200"
                              onClick={() => startEditing(fieldName, value)}
                            >
                              <div className="flex items-start gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                                  <Icon size={14} className="text-slate-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">{label}</p>
                                  <p className={`text-sm leading-snug ${value ? 'text-slate-700' : 'text-slate-400 italic'} line-clamp-2`}>
                                    {needsTruncate ? displayValue.substring(0, 60) + '...' : displayValue}
                                  </p>
                                </div>
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {needsTruncate && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setModalTextoCompleto({ label, value: displayValue });
                                      }}
                                      className="p-1 text-slate-400 hover:text-[#1717AF] rounded-md"
                                    >
                                      <MoreHorizontal size={12} />
                                    </button>
                                  )}
                                  <div className="p-1 text-slate-300">
                                    <ChevronDown size={10} />
                                  </div>
                                </div>
                              </div>
                              {isSaving && (
                                <div className="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center">
                                  <Loader2 size={14} className="animate-spin text-[#1717AF]" />
                                </div>
                              )}
                            </div>
                          );
                        };

                        const ocupacion = getFieldValue('ocupacion');
                        const motivacion = getFieldValue('motivacion');
                        const comoAdquirioIngles = getFieldValue('como_adquirio_ingles');

                        return (
                          <>
                            {/* CONTACTO */}
                            <div className="space-y-2">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
                                <Phone size={12} className="text-[#1717AF]" />
                                Contacto
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <EditableTextField fieldName="telefono" label="Teléfono" icon={Phone} placeholder="No disponible" copyable={true} />
                                <EditableTextField fieldName="email" label="Correo" icon={Mail} placeholder="No disponible" copyable={true} />
                                <EditableSelectField fieldName="consulta_decision" label="Consulta la decisión" icon={MessageCircle} options={OPCIONES_CONSULTA_DECISION} />
                                {getFieldValue('referido_por') && (
                                  <EditableTextField fieldName="referido_por" label="Referido por" icon={Star} />
                                )}
                              </div>
                            </div>

                            {/* OCUPACIÓN */}
                            <div className="space-y-2">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
                                <Briefcase size={12} className="text-[#1717AF]" />
                                Ocupación
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <EditableSelectField fieldName="ocupacion" label="Ocupación" icon={Briefcase} options={OPCIONES_OCUPACION} />
                                
                                {ocupacion === 'Estudiante' && (
                                  <EditableTextField fieldName="universidad" label="Universidad" icon={ClipboardList} />
                                )}
                                
                                {ocupacion === 'Médico General' && (
                                  <EditableTextField fieldName="lugar_trabajo" label="Lugar de trabajo" icon={MapPin} />
                                )}
                                
                                {ocupacion === 'Residente' && (
                                  <>
                                    <EditableSelectField fieldName="ano_residencia" label="Año de residencia" icon={BarChart3} options={OPCIONES_ANO_RESIDENCIA} />
                                    <EditableTextField fieldName="lugar_trabajo" label="Lugar de trabajo" icon={MapPin} />
                                  </>
                                )}
                                
                                {ocupacion === 'Especialista' && (
                                  <>
                                    <EditableSelectField fieldName="especialidad" label="Especialidad" icon={Star} options={OPCIONES_ESPECIALIDAD} />
                                    <EditableTextField fieldName="lugar_trabajo" label="Lugar de trabajo" icon={MapPin} />
                                  </>
                                )}
                              </div>
                            </div>

                            {/* MOTIVACIÓN */}
                            <div className="space-y-2">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
                                <Star size={12} className="text-[#1717AF]" />
                                Motivación
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <EditableSelectField fieldName="motivacion" label="Motivación" icon={Star} options={OPCIONES_MOTIVACION} detailFieldName="motivacion_detalle" />
                                
                                {motivacion === 'Otro' && (
                                  <EditableTextField fieldName="motivacion_detalle" label="Detalle motivación" icon={MessageCircle} />
                                )}
                                
                                <EditableSelectField fieldName="cuando_empezar" label="Cuando empezaría" icon={Clock} options={OPCIONES_CUANDO_EMPEZAR} />
                              </div>
                            </div>

                            {/* NIVEL DE INGLÉS */}
                            <div className="space-y-2">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
                                <BarChart3 size={12} className="text-[#1717AF]" />
                                Nivel de inglés
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <EditableSelectField fieldName="nivel_ingles" label="Nivel de inglés" icon={BarChart3} options={OPCIONES_NIVEL_INGLES} />
                                <EditableSelectField fieldName="como_adquirio_ingles" label="Cómo adquirió inglés" icon={ClipboardList} options={OPCIONES_COMO_ADQUIRIO_INGLES} detailFieldName="como_adquirio_ingles_detalle" />
                                
                                {comoAdquirioIngles === 'Otro' && (
                                  <EditableTextField fieldName="como_adquirio_ingles_detalle" label="Detalle cómo adquirió" icon={MessageCircle} />
                                )}
                              </div>
                            </div>

                            {/* PLAN DE PAGO */}
                            <div className="space-y-2">
                              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
                                <FileText size={12} className="text-[#1717AF]" />
                                Plan de pago
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <EditableSelectField fieldName="plan_pago" label="Plan de pago" icon={FileText} options={OPCIONES_PLAN_PAGO} />
                              </div>
                            </div>
                          </>
                        );
                      })()
                      )}
                    </div>
                  )}

                  {subActiveTab === 'pitch' && (() => {
                    // Render de un campo del formulario Pitch (modo input controlado)
                    const renderPitchInput = ({
                      field,
                      values,
                      setValue,
                      openSelectId,
                      setOpenSelectId,
                      keyPrefix = 'new',
                    }) => {
                      const value = values[field.id] ?? '';

                      // Campo SELECT
                      if (field.tipo === 'select' && field.opciones) {
                        const parsed = parseOpcionesField(field.opciones);
                        const opciones = parsed.map(o => o.value);
                        const isOpen = openSelectId === `${keyPrefix}-${field.id}`;

                        return (
                          <div key={field.id} className="relative">
                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                              {field.nombre}
                              {field.obligatorio && <span className="text-rose-500 ml-1">*</span>}
                            </label>
                            <button
                              type="button"
                              onClick={() => setOpenSelectId(isOpen ? null : `${keyPrefix}-${field.id}`)}
                              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white border rounded-xl text-left transition-colors ${
                                isOpen ? 'border-[#1717AF]/40 ring-1 ring-[#1717AF]/20' : 'border-slate-200 hover:border-slate-300'
                              } ${value ? 'text-slate-700' : 'text-slate-400'}`}
                            >
                              <span className="truncate">{value || 'Seleccionar...'}</span>
                              <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isOpen && (
                              <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                                <div className="max-h-48 overflow-y-auto">
                                  {opciones.length > 0 ? (
                                    opciones.map(opt => (
                                      <button
                                        key={opt}
                                        type="button"
                                        onClick={() => {
                                          setValue(field.id, opt);
                                          setOpenSelectId(null);
                                        }}
                                        className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                                          opt === value
                                            ? 'bg-[#1717AF] text-white font-medium'
                                            : 'text-slate-600 hover:bg-slate-50'
                                        }`}
                                      >
                                        {opt}
                                      </button>
                                    ))
                                  ) : (
                                    <p className="px-3 py-2 text-xs text-slate-400 text-center">Sin opciones</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Campo TEXTO (default)
                      return (
                        <div key={field.id}>
                          <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                            {field.nombre}
                            {field.obligatorio && <span className="text-rose-500 ml-1">*</span>}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(field.id, e.target.value)}
                            placeholder={field.tooltip || 'Escribe aquí...'}
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]/40 transition-colors"
                          />
                        </div>
                      );
                    };

                    // Bloque "Especifica por qué no se matriculó":
                    //   - Crear: radio Seguimiento | Recordatorio + categoría +
                    //     detalle (y fecha/hora si es Recordatorio).
                    //   - Editar: solo categoría + detalle (sin radio; siempre
                    //     genera un comentario de Seguimiento).
                    const renderMotivoField = ({ values, setMotivoValue, isEdit, open, setOpen, busqueda, setBusqueda }) => {
                      const selectedId = values[MOTIVO_FIELD_KEY];
                      const seleccion = categorias.find(c => String(c.id) === String(selectedId));
                      // Solo opciones marcadas para el formulario de Pitch, ordenadas por grupo.
                      const base = sortCategorias(categorias.filter(c => c.formulario_pitch === true));
                      const filtradas = base.filter(c =>
                        c.categoria.toLowerCase().includes((busqueda || '').toLowerCase())
                      );
                      const tipo = getMotivoTipo(values);
                      const esRecordatorio = !isEdit && tipo === 'recordatorio';

                      // Rango permitido para la fecha del recordatorio (hoy → +6 meses).
                      const hoyStr = new Date().toISOString().split('T')[0];
                      const maxDate = new Date();
                      maxDate.setMonth(maxDate.getMonth() + 6);
                      const maxStr = maxDate.toISOString().split('T')[0];

                      return (
                        <div className="space-y-3 pt-1">
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                            Especifica por qué no se matriculó
                            <span className="text-rose-500 ml-1">*</span>
                          </p>

                          {/* Radio Seguimiento | Recordatorio (solo al crear) */}
                          {!isEdit && (
                            <div className="flex items-center gap-2">
                              {[
                                { id: 'seguimiento', label: 'Seguimiento' },
                                { id: 'recordatorio', label: 'Recordatorio' },
                              ].map(opt => (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => setMotivoValue(MOTIVO_TIPO_KEY, opt.id)}
                                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border transition-colors ${
                                    tipo === opt.id
                                      ? 'border-[#1717AF] bg-[#1717AF]/5 text-[#1717AF]'
                                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                  }`}
                                >
                                  <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                                    tipo === opt.id ? 'border-[#1717AF]' : 'border-slate-300'
                                  }`}>
                                    {tipo === opt.id && <span className="w-1.5 h-1.5 rounded-full bg-[#1717AF]" />}
                                  </span>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Fecha + hora (solo Recordatorio al crear) */}
                          {esRecordatorio && (
                            <div className="flex items-end gap-2">
                              <div className="flex-1">
                                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                                  Fecha<span className="text-rose-500 ml-1">*</span>
                                </label>
                                <input
                                  type="date"
                                  min={hoyStr}
                                  max={maxStr}
                                  value={values[MOTIVO_FECHA_KEY] || ''}
                                  onChange={(e) => setMotivoValue(MOTIVO_FECHA_KEY, e.target.value)}
                                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]/40 transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                                  Hora<span className="text-rose-500 ml-1">*</span>
                                </label>
                                <div className="flex items-center gap-1">
                                  <select
                                    value={values[MOTIVO_HORA_KEY] || '09'}
                                    onChange={(e) => setMotivoValue(MOTIVO_HORA_KEY, e.target.value)}
                                    className="px-2 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]"
                                  >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                                      <option key={h} value={h.toString().padStart(2, '0')}>{h.toString().padStart(2, '0')}</option>
                                    ))}
                                  </select>
                                  <span className="text-slate-400">:</span>
                                  <select
                                    value={values[MOTIVO_MINUTO_KEY] || '00'}
                                    onChange={(e) => setMotivoValue(MOTIVO_MINUTO_KEY, e.target.value)}
                                    className="px-2 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]"
                                  >
                                    {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                                      <option key={m} value={m.toString().padStart(2, '0')}>{m.toString().padStart(2, '0')}</option>
                                    ))}
                                  </select>
                                  <select
                                    value={values[MOTIVO_PERIODO_KEY] || 'AM'}
                                    onChange={(e) => setMotivoValue(MOTIVO_PERIODO_KEY, e.target.value)}
                                    className="px-1 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]"
                                  >
                                    <option value="AM">AM</option>
                                    <option value="PM">PM</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Categoría (ambos caminos) */}
                          <div className="relative motivo-dropdown">
                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                              Categoría<span className="text-rose-500 ml-1">*</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => { setOpen(!open); setBusqueda(''); }}
                              disabled={loadingCategorias}
                              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white border rounded-xl text-left transition-colors ${
                                open ? 'border-[#1717AF]/40 ring-1 ring-[#1717AF]/20' : 'border-slate-200 hover:border-slate-300'
                              } ${seleccion ? 'text-slate-700' : 'text-slate-400'}`}
                            >
                              <span className="truncate">
                                {loadingCategorias ? 'Cargando...' : (seleccion?.categoria || 'Seleccionar...')}
                              </span>
                              <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                            </button>
                            {open && (
                              <div className="absolute z-40 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                                <div className="p-2 border-b border-slate-100">
                                  <div className="relative">
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                      type="text"
                                      autoFocus
                                      value={busqueda}
                                      onChange={(e) => setBusqueda(e.target.value)}
                                      placeholder="Buscar categoría..."
                                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]"
                                    />
                                  </div>
                                </div>
                                <div className="max-h-44 overflow-y-auto">
                                  {filtradas.length > 0 ? (
                                    filtradas.map(cat => (
                                      <button
                                        key={cat.id}
                                        type="button"
                                        onClick={() => { setMotivoValue(MOTIVO_FIELD_KEY, cat.id); setOpen(false); setBusqueda(''); }}
                                        className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                                          String(cat.id) === String(selectedId)
                                            ? 'bg-[#1717AF] text-white font-medium'
                                            : 'text-slate-600 hover:bg-slate-50'
                                        }`}
                                      >
                                        {cat.categoria}
                                        {cat.categoria_padre && (
                                          <span className={`block text-[10px] ${String(cat.id) === String(selectedId) ? 'text-white/70' : 'text-slate-400'}`}>
                                            {cat.categoria_padre}
                                          </span>
                                        )}
                                      </button>
                                    ))
                                  ) : (
                                    <p className="px-3 py-2 text-xs text-slate-400 text-center">Sin resultados</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Detalle / Observación (obligatorio, ambos caminos) */}
                          <div>
                            <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                              {esRecordatorio ? 'Observación' : 'Detalle'}<span className="text-rose-500 ml-1">*</span>
                            </label>
                            <textarea
                              value={values[MOTIVO_DETALLE_KEY] || ''}
                              onChange={(e) => setMotivoValue(MOTIVO_DETALLE_KEY, e.target.value)}
                              placeholder={esRecordatorio ? 'Escribe la observación del recordatorio...' : 'Escribe el detalle del seguimiento...'}
                              rows={2}
                              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]/40 resize-none transition-colors"
                            />
                          </div>
                        </div>
                      );
                    };

                    // Tarjetas: ordenamos por pitch_numero asc (más antiguo primero)
                    // y luego invertimos para mostrar más reciente arriba. Si la fila
                    // no tiene pitch_numero (datos viejos), caemos al orden cronológico.
                    const tarjetas = pitchResultados
                      .map((r, idx) => ({ ...r, numero: r.pitch_numero ?? idx + 1 }))
                      .slice()
                      .reverse();

                    const formValid = isPitchFormValid();
                    const editFormValid = isPitchValuesValid(editingPitchValues, true);

                    // Estado del sub-tab Pitch:
                    //  - no_aplica:   el lead no está actualmente en fase Pitch (340566951)
                    //  - pendiente:   está en fase Pitch y aún no ha registrado el resultado
                    //                 del intento actual (pitch_seguimientos < pitch_intentos)
                    //  - registrado:  está en fase Pitch pero ya registró el resultado
                    //                 del intento actual (pitch_seguimientos === pitch_intentos)
                    //
                    // localLeadData tiene prioridad sobre lead para reflejar updates
                    // optimistas (insert de resultado, cambio de fase post-webhook).
                    const FASE_PITCH_ACTIVA = '340566951';
                    const faseIdEfectiva = localLeadData.fase_id_pipefy ?? lead?.fase_id_pipefy ?? '';
                    const enFasePitch = String(faseIdEfectiva) === FASE_PITCH_ACTIVA;
                    const intentos = localLeadData.pitch_intentos ?? lead?.pitch_intentos ?? 0;
                    const seguimientos = localLeadData.pitch_seguimientos ?? lead?.pitch_seguimientos ?? 0;
                    const pitchTabState = !enFasePitch
                      ? 'no_aplica'
                      : (seguimientos < intentos ? 'pendiente' : 'registrado');

                    const fechaPitchTexto = formatFechaPitchTarjeta(lead?.fecha_pitch);
                    const presentationUrl = lead?.card_id
                      ? `/presentation/${lead.card_id}`
                      : null;

                    const formatPresentationDate = (iso) => {
                      if (!iso) return '—';
                      try {
                        return new Date(iso).toLocaleString('es-CO', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        });
                      } catch {
                        return '—';
                      }
                    };

                    return (
                      <div className="space-y-4">
                        {/* Sub-sección Pitch: Meet | Presentación */}
                        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
                          <button
                            type="button"
                            onClick={() => setPitchSectionTab('meet')}
                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                              pitchSectionTab === 'meet'
                                ? 'bg-white text-slate-700 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            Meet
                          </button>
                          <button
                            type="button"
                            onClick={() => setPitchSectionTab('presentacion')}
                            className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                              pitchSectionTab === 'presentacion'
                                ? 'bg-white text-slate-700 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                          >
                            Presentación
                          </button>
                        </div>

                        {pitchSectionTab === 'presentacion' && (
                          <div className="space-y-4">
                            <button
                              type="button"
                              disabled={!presentationUrl}
                              onClick={() => {
                                if (!presentationUrl) return;
                                window.open(presentationUrl, '_blank', 'noopener,noreferrer');
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl text-sm font-semibold text-white bg-gradient-to-r from-[#02214A] to-[#1717AF] hover:opacity-95 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            >
                              <Play size={16} fill="currentColor" />
                              Abrir presentación
                            </button>
                            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                              Abre la presentación personalizada de este lead. Al finalizar podrás generar un link público con el recorrido mostrado.
                            </p>

                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                  Generadas
                                </h4>
                                {presentationRuns.length > 0 && (
                                  <span className="text-[10px] text-slate-400">
                                    {presentationRuns.length}
                                  </span>
                                )}
                              </div>

                              {loadingPresentationRuns ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 size={18} className="animate-spin text-slate-400" />
                                </div>
                              ) : presentationRuns.length === 0 ? (
                                <div className="p-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 text-center">
                                  <Presentation size={18} className="mx-auto text-slate-300 mb-2" />
                                  <p className="text-xs text-slate-500">
                                    Aún no hay presentaciones generadas para este lead.
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {[...presentationRuns]
                                    .sort((a, b) => (b.version ?? 0) - (a.version ?? 0))
                                    .map((run) => (
                                      <a
                                        key={run.id || run.version}
                                        href={`/result/${run.version}/${lead.card_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block p-3.5 rounded-2xl border border-slate-200 bg-white hover:border-[#1717AF]/30 hover:shadow-sm transition-all"
                                      >
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-700">
                                              Versión {run.version}
                                            </p>
                                            <p className="text-[11px] text-slate-500 mt-0.5">
                                              {formatPresentationDate(run.created_at)}
                                            </p>
                                            {run.created_by && (
                                              <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                                                {run.created_by}
                                              </p>
                                            )}
                                          </div>
                                          <ExternalLink size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                                        </div>
                                      </a>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {pitchSectionTab === 'meet' && (
                      <div className="space-y-5">
                        {/* Estado: completado — recién registrado este pitch + lead movido de fase. */}
                        {/* Tiene prioridad sobre los 3 estados de abajo y se limpia al cambiar de lead. */}
                        {pitchCompletionInfo && (
                          <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0">
                                <Check size={16} className="text-emerald-500" />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-1">
                                  Resultado registrado
                                </h4>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                  Ya registraste el resultado del pitch actual. Si el lead vuelve a entrar a la fase Pitch, podrás registrar un nuevo resultado.
                                  {pitchCompletionInfo.faseNombre && !pitchCompletionInfo.faseError && (
                                    <>
                                      {' '}Además movimos el lead a la fase{' '}
                                      <span className="font-semibold text-emerald-700">
                                        {pitchCompletionInfo.faseNombre}
                                      </span>
                                      {pitchCompletionInfo.fasePending && (
                                        <span className="text-amber-600">
                                          {' '}(la actualización puede tardar unos segundos)
                                        </span>
                                      )}
                                      .
                                    </>
                                  )}
                                  {pitchCompletionInfo.faseError && (
                                    <span className="text-rose-600">
                                      {' '}No pudimos mover el lead de fase automáticamente; intenta cambiarla manualmente.
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Estado: no aplica (lead no está en fase Pitch) */}
                        {!pitchCompletionInfo && pitchTabState === 'no_aplica' && (
                          <div className="p-5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0">
                                <Trophy size={16} className="text-slate-400" />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-1">
                                  Sección no disponible
                                </h4>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                  El registro de resultado se habilita cuando el lead entra a la fase <span className="font-medium text-slate-600">Pitch</span>. Mientras tanto, aquí podrás consultar el historial de pitches anteriores.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Estado: pendiente — formulario activo */}
                        {!pitchCompletionInfo && pitchTabState === 'pendiente' && (
                          <div className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100">
                            <div className="flex items-center gap-2 mb-1">
                              <Trophy size={18} className="text-amber-500" />
                              <h4 className="font-semibold text-slate-700">Pitch</h4>
                              <span className="text-[10px] font-medium text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                                Resultado pendiente
                              </span>
                            </div>
                            {fechaPitchTexto && (
                              <p className="text-[11px] text-slate-500 mb-4 ml-7">
                                Pitch agendado para <span className="font-medium text-slate-700">{fechaPitchTexto}</span>
                              </p>
                            )}
                            {!fechaPitchTexto && <div className="mb-3" />}

                            {loadingPitchForm ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 size={20} className="animate-spin text-amber-500" />
                              </div>
                            ) : pitchFields.length === 0 ? (
                              <div className="text-center py-6 text-sm text-slate-500">
                                No se encontró el formulario Pitch.
                              </div>
                            ) : (
                              <>
                                <div className="space-y-3">
                                  {pitchFields
                                    .filter(field => shouldShowPitchField(field, pitchFormValues))
                                    .map(field => renderPitchInput({
                                      field,
                                      values: pitchFormValues,
                                      setValue: setPitchFormValue,
                                      openSelectId: pitchSelectOpenId,
                                      setOpenSelectId: setPitchSelectOpenId,
                                      keyPrefix: 'new',
                                    }))}
                                  {shouldShowMotivoField(pitchFormValues) && renderMotivoField({
                                    values: pitchFormValues,
                                    setMotivoValue: setMotivoNewValue,
                                    isEdit: false,
                                    open: motivoNewOpen,
                                    setOpen: setMotivoNewOpen,
                                    busqueda: motivoNewBusqueda,
                                    setBusqueda: setMotivoNewBusqueda,
                                  })}
                                </div>

                                <div className="flex justify-end mt-4">
                                  <button
                                    type="button"
                                    onClick={handleCrearResultadoPitch}
                                    disabled={!formValid || savingPitch}
                                    className={`px-4 py-2 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-lg shadow-amber-200 flex items-center gap-2 ${
                                      !formValid || savingPitch
                                        ? 'bg-amber-300 cursor-not-allowed'
                                        : 'bg-amber-500 hover:bg-amber-600'
                                    }`}
                                  >
                                    {savingPitch ? (
                                      <>
                                        <Loader2 size={14} className="animate-spin" />
                                        Guardando...
                                      </>
                                    ) : (
                                      <>
                                        <Plus size={14} />
                                        Crear resultado
                                      </>
                                    )}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Estado: registrado — ya guardó el resultado del intento actual */}
                        {!pitchCompletionInfo && pitchTabState === 'registrado' && (
                          <div className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center flex-shrink-0">
                                <Check size={16} className="text-emerald-500" />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-1">
                                  Resultado registrado
                                </h4>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                  Ya registraste el resultado del pitch actual. Si el lead vuelve a entrar a la fase Pitch, podrás registrar un nuevo resultado.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Lista de tarjetas de resultados */}
                        <div>
                          <div className="flex items-center justify-between mb-3 px-1">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                              <History size={12} className="text-[#1717AF]" />
                              Historial de pitches
                            </h3>
                            {pitchResultados.length > 0 && (
                              <span className="text-[11px] text-slate-400">
                                {pitchResultados.length} registro{pitchResultados.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>

                          {loadingPitchResultados ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 size={20} className="animate-spin text-[#1717AF]" />
                            </div>
                          ) : tarjetas.length === 0 ? (
                            <div className="text-center py-8 text-sm text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                              Aún no hay pitches registrados para este lead.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {tarjetas.map(resultado => {
                                const isEditing = editingPitchId === resultado.id;
                                const isSavingThis = savingEditPitchId === resultado.id;
                                const fechaCreacion = resultado.created_at
                                  ? new Date(resultado.created_at).toLocaleDateString('es-ES', {
                                      day: 'numeric', month: 'short', year: 'numeric',
                                      hour: 'numeric', minute: '2-digit', hour12: true,
                                    })
                                  : '';
                                const fueEditado = resultado.updated_at &&
                                  resultado.created_at &&
                                  new Date(resultado.updated_at).getTime() - new Date(resultado.created_at).getTime() > 1000;

                                return (
                                  <div
                                    key={resultado.id}
                                    className={`p-4 bg-white rounded-2xl border transition-all ${
                                      isEditing
                                        ? 'border-[#1717AF]/40 shadow-sm shadow-[#1717AF]/10'
                                        : 'border-slate-100 hover:border-slate-200 hover:shadow-sm'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                          <Trophy size={14} className="text-amber-600" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-semibold text-slate-700">
                                            Pitch {resultado.numero}
                                          </p>
                                          {fechaCreacion && (
                                            <p className="text-[10px] text-slate-400">
                                              {fechaCreacion}
                                              {fueEditado && <span className="ml-1 italic">(editado)</span>}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      {!isEditing ? (
                                        <button
                                          type="button"
                                          onClick={() => handleStartEditPitch(resultado)}
                                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-[#1717AF] hover:bg-[#1717AF]/5 rounded-lg transition-colors"
                                        >
                                          <Edit2 size={12} />
                                          Editar
                                        </button>
                                      ) : (
                                        <div className="flex items-center gap-1">
                                          <button
                                            type="button"
                                            onClick={handleCancelEditPitch}
                                            disabled={isSavingThis}
                                            className="p-1.5 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                                            title="Cancelar"
                                          >
                                            <X size={12} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={handleSaveEditPitch}
                                            disabled={isSavingThis || !editFormValid}
                                            className="p-1.5 bg-[#1717AF] text-white rounded-lg hover:bg-[#1717AF]/90 transition-colors disabled:opacity-50"
                                            title="Guardar"
                                          >
                                            {isSavingThis ? (
                                              <Loader2 size={12} className="animate-spin" />
                                            ) : (
                                              <Check size={12} />
                                            )}
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {isEditing ? (
                                      <div className="space-y-3">
                                        {pitchFields
                                          .filter(field => shouldShowPitchField(field, editingPitchValues))
                                          .map(field => renderPitchInput({
                                            field,
                                            values: editingPitchValues,
                                            setValue: setEditingPitchValue,
                                            openSelectId: editingPitchSelectOpenId,
                                            setOpenSelectId: setEditingPitchSelectOpenId,
                                            keyPrefix: `edit-${resultado.id}`,
                                          }))}
                                        {shouldShowMotivoField(editingPitchValues) && renderMotivoField({
                                          values: editingPitchValues,
                                          setMotivoValue: setMotivoEditValue,
                                          isEdit: true,
                                          open: motivoEditOpen,
                                          setOpen: setMotivoEditOpen,
                                          busqueda: motivoEditBusqueda,
                                          setBusqueda: setMotivoEditBusqueda,
                                        })}
                                      </div>
                                    ) : (
                                      (() => {
                                        const cardValues = resultadoToValues(resultado);
                                        const visibles = pitchFields.filter(field => {
                                          if (shouldShowPitchField(field, cardValues)) return true;
                                          const col = getPitchColumnForField(field);
                                          return col && resultado[col];
                                        });
                                        return (
                                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {visibles.map(field => {
                                              const col = getPitchColumnForField(field);
                                              const v = col ? resultado[col] : null;
                                              return (
                                                <div key={field.id} className="p-2.5 bg-slate-50 rounded-xl">
                                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">
                                                    {field.nombre}
                                                  </p>
                                                  <p className={`text-sm leading-snug ${v ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                                    {v || 'Sin dato'}
                                                  </p>
                                                </div>
                                              );
                                            })}
                                            {resultado.motivo_no_matricula && (
                                              <div className="p-2.5 bg-slate-50 rounded-xl sm:col-span-2">
                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-0.5">
                                                  Especifica por qué no se matriculó
                                                </p>
                                                <p className="text-sm leading-snug text-slate-700">
                                                  {resultado.motivo_no_matricula}
                                                  {resultado.motivo_no_matricula_categoria && (
                                                    <span className="block text-[11px] text-slate-400">
                                                      {resultado.motivo_no_matricula_categoria}
                                                    </span>
                                                  )}
                                                  {resultado.detalle_motivo_no_matricula && (
                                                    <span className="block text-[11px] text-slate-500 mt-1 italic">
                                                      {resultado.detalle_motivo_no_matricula}
                                                    </span>
                                                  )}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                        )}
                      </div>
                    );
                  })()}

                  {subActiveTab === 'resumen-ia' && (
                    <div className="space-y-6">
                      {/* Resumen IA */}
                      <div className="p-5 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl border border-violet-100">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles size={18} className="text-violet-500" />
                          <h4 className="font-semibold text-slate-700">Resumen inteligente</h4>
                          <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">
                            IA
                          </span>
                        </div>
                        
                        {resumenIA ? (
                          <div className="space-y-2">
                            <div className="w-full min-h-28 p-3 bg-white/70 border border-violet-200 rounded-xl text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                              {resumenIA}
                            </div>
                            {resumenIAFecha && (
                              <p className="text-[11px] text-violet-400 italic text-right">
                                Generado el {new Date(resumenIAFecha).toLocaleDateString('es-ES', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })} a las {new Date(resumenIAFecha).toLocaleTimeString('es-ES', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="w-full h-28 p-3 bg-white/70 border border-violet-200 rounded-xl text-sm text-slate-400 flex items-center justify-center">
                            {generandoResumen ? (
                              <span className="flex items-center gap-2">
                                <Loader2 size={16} className="animate-spin" />
                                Generando resumen...
                              </span>
                            ) : (
                              'Haz clic en "Generar" para crear un resumen con IA'
                            )}
                          </div>
                        )}
                        
                        <div className="flex justify-end mt-3">
                          <button 
                            onClick={handleGenerarResumen}
                            disabled={generandoResumen}
                            className={`px-4 py-2 text-white text-sm font-medium rounded-xl transition-all duration-200 shadow-lg shadow-violet-200 flex items-center gap-2 ${
                              generandoResumen 
                                ? 'bg-violet-400 cursor-not-allowed' 
                                : 'bg-violet-600 hover:bg-violet-700'
                            }`}
                          >
                            {generandoResumen ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Generando...
                              </>
                            ) : (
                              <>
                                <Sparkles size={14} />
                                {resumenIA ? 'Regenerar' : 'Generar'}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'seguimiento' && (() => {
              return (
              <div className="flex flex-col" style={{ height: 'calc(100vh - 380px)', minHeight: '300px' }}>
                {/* Lista de seguimientos - scroll */}
                <div 
                  ref={recordatoriosContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto space-y-3 pr-1"
                >
                  {recordatorios.length === 0 && !loadingRecordatorios ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                        <MessageCircle size={28} className="text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-700 mb-2">
                        Sin seguimiento
                      </h3>
                      <p className="text-sm text-slate-400">
                        Aún no hay mensajes de seguimiento para este lead
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Inicio del historial */}
                      {!hasMoreRecordatorios && recordatorios.length > 0 && (
                        <p className="text-center text-xs text-slate-400 py-3">
                          Inicio del historial
                        </p>
                      )}
                      
                      {/* Loader para cargar más */}
                      {loadingRecordatorios && (
                        <div className="flex justify-center py-4">
                          <Loader2 size={24} className="text-[#1717AF] animate-spin" />
                        </div>
                      )}
                      
                      {/* Mensajes ordenados: reciente abajo (estilo chat) */}
                      {[...recordatorios].reverse().map((comentario, index) => {
                        // Obtener nombre del autor (del join con usuarios o del email)
                        const nombreAutor = comentario.usuarios?.nombre || 
                          (comentario.autor_email === userEmail ? userName : comentario.autor_email?.split('@')[0]) || 
                          'Usuario';
                        
                        return (
                          <div 
                            key={comentario.id || index}
                            className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:border-slate-200 transition-all duration-200"
                            style={{ borderLeft: '3px solid #10b981' }}
                          >
                            {/* Contenido del mensaje */}
                            <div className="p-4 pb-3">
                              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                                <span className="font-semibold text-slate-600">Categoría: </span>
                                <span className="text-[#1717AF]">{comentario.categoria || 'Otro'}</span>
                                <span className="font-semibold text-slate-600 ml-2">Detalle: </span>
                                {textoDetalleConCanal(comentario.texto, comentario.llamada, comentario.mensaje)}
                              </p>
                            </div>
                            
                            {/* Footer con fase, fecha y autor */}
                            <div className="px-4 py-2.5 bg-slate-50/50 flex items-center gap-4 text-xs">
                              {/* Fase */}
                              {comentario.fase && (
                                <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium">
                                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                  {comentario.fase}
                                </span>
                              )}
                              
                              {/* Fecha */}
                              <span className="text-slate-400">
                                {formatearFechaRecordatorio(comentario.created_at)}
                              </span>
                              
                              {/* Autor */}
                              <span className="font-medium text-slate-500 ml-auto">
                                {nombreAutor}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
                
                {/* Campo para nuevo mensaje - ABAJO (como WhatsApp) */}
                <div ref={seguimientoInputRef} className="flex-shrink-0 border-t border-slate-100 pt-4 mt-2">
                  {/* Dropdown de categoría con buscador */}
                  <div className="mb-3 relative categoria-dropdown">
                    <label className="text-xs text-slate-500 mb-1.5 block font-medium">Categoría</label>
                    <button
                      type="button"
                      onClick={() => {
                        setCategoriaDropdownOpen(!categoriaDropdownOpen);
                        setCategoriaBusqueda('');
                        setSiglaDropdownOpen(null);
                      }}
                      disabled={loadingCategorias}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF] transition-all duration-200 flex items-center justify-between"
                    >
                      <span className={categoriaSeleccionada ? 'text-slate-700' : 'text-slate-400'}>
                        {loadingCategorias ? 'Cargando...' : (categoriaSeleccionada || 'Seleccionar categoría')}
                      </span>
                      <ChevronDown size={16} className={`text-slate-400 transition-transform ${categoriaDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown con buscador */}
                    {categoriaDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
                        {/* Input de búsqueda */}
                        <div className="p-2 border-b border-slate-100">
                          <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              ref={categoriaSearchRef}
                              type="text"
                              value={categoriaBusqueda}
                              onChange={(e) => setCategoriaBusqueda(e.target.value)}
                              placeholder="Buscar categoría..."
                              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]"
                            />
                          </div>
                        </div>
                        
                        {/* Lista de opciones */}
                        <div className="max-h-40 overflow-y-auto">
                          {(() => {
                            const filtradas = sortCategorias(categorias).filter(cat => 
                              cat.categoria.toLowerCase().includes(categoriaBusqueda.toLowerCase())
                            );
                            
                            if (filtradas.length === 0) {
                              return <p className="px-3 py-2 text-xs text-slate-400 text-center">Sin resultados</p>;
                            }
                            
                            return filtradas.map((cat) => (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => {
                                  setCategoriaSeleccionada(cat.categoria);
                                  setCategoriaDropdownOpen(false);
                                  setCategoriaBusqueda('');
                                }}
                                className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${
                                  cat.categoria === categoriaSeleccionada 
                                    ? 'bg-[#1717AF]/5 text-[#1717AF] font-medium' 
                                    : 'text-slate-700'
                                }`}
                              >
                                {cat.categoria}
                                {cat.categoria_padre && (
                                  <span className="block text-[10px] text-slate-400">
                                    {cat.categoria_padre}
                                  </span>
                                )}
                              </button>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {renderCanalSiglas()}

                  {/* Input de mensaje */}
                  <div className="flex items-end gap-2">
                    <div className="flex-1 relative">
                      <textarea
                        value={nuevoMensaje}
                        onChange={(e) => setNuevoMensaje(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleEnviarMensaje();
                          }
                        }}
                        placeholder="Escribe el detalle del seguimiento..."
                        rows={1}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF] resize-none transition-all duration-200"
                        style={{ minHeight: '42px', maxHeight: '120px' }}
                      />
                    </div>
                    <button
                      onClick={handleEnviarMensaje}
                      disabled={enviandoMensaje || !nuevoMensaje.trim() || !tieneSiglaCanal}
                      className={`p-2.5 rounded-xl transition-all duration-200 flex-shrink-0 ${
                        !enviandoMensaje && nuevoMensaje.trim() && tieneSiglaCanal
                          ? 'bg-[#1717AF] text-white hover:bg-[#02214A] shadow-lg shadow-[#1717AF]/20'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {enviandoMensaje ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <Send size={18} />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Enter para enviar • El detalle es obligatorio • Elige llamada o mensaje
                  </p>
                </div>
              </div>
              );
            })()}

            {activeTab === 'recordatorio' && (() => {
              const { daysInMonth, startingDay } = getDaysInMonth(mesActual);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
              const nombresMeses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
              
              // Convertir hora para validación
              let hora24Validation = parseInt(horaInput) || 12;
              if (periodoInput === 'PM' && hora24Validation !== 12) {
                hora24Validation += 12;
              } else if (periodoInput === 'AM' && hora24Validation === 12) {
                hora24Validation = 0;
              }
              const minutosValidation = parseInt(minutoInput) || 0;
              const timeIsValid = isTimeValid(fechaSeleccionada, hora24Validation, minutosValidation);
              
              const canSubmit = fechaSeleccionada && textoRecordatorio.trim().length > 0 && tieneSiglaCanal && !programandoRecordatorio && timeIsValid;
              
              // Formatear fecha para historial
              const formatFechaHistorial = (dateString) => {
                if (!dateString) return '-';
                const fecha = new Date(dateString);
                const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                const dia = fecha.getDate();
                const mes = meses[fecha.getMonth()];
                const año = fecha.getFullYear();
                let horas = fecha.getHours();
                const minutos = fecha.getMinutes().toString().padStart(2, '0');
                const ampm = horas >= 12 ? 'pm' : 'am';
                horas = horas % 12 || 12;
                return `${dia} ${mes} ${año}, ${horas}:${minutos} ${ampm}`;
              };
              
              // Estilos de estado para historial
              const estadoStyles = {
                'Programado': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
                'Vencido': { bg: 'bg-rose-100', text: 'text-rose-700' },
                'Cancelado': { bg: 'bg-slate-100', text: 'text-slate-500' }
              };
              
              const totalPagesHistorial = Math.ceil(totalHistorial / HISTORIAL_PER_PAGE);
              
              return (
                <div className="flex flex-col h-[calc(100vh-320px)] min-h-[400px]">
                  {/* Sub-tabs */}
                  <div className="flex gap-1 mb-4 flex-shrink-0 p-1 bg-slate-100 rounded-lg">
                    <button
                      onClick={() => setSubTabRecordatorio('programar')}
                      className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                        subTabRecordatorio === 'programar'
                          ? 'bg-white text-slate-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Programar
                    </button>
                    <button
                      onClick={() => setSubTabRecordatorio('historial')}
                      className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                        subTabRecordatorio === 'historial'
                          ? 'bg-white text-slate-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Historial
                    </button>
                  </div>
                  
                  {/* Contenido de Historial */}
                  {subTabRecordatorio === 'historial' && (
                    <div className="flex-1 overflow-y-auto">
                      {loadingHistorial ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 size={24} className="animate-spin text-slate-400" />
                        </div>
                      ) : historialRecordatorios.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                            <CalendarDays size={28} className="text-slate-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">Sin historial</h3>
                          <p className="text-sm text-slate-400">Aún no has programado recordatorios para este lead</p>
                        </div>
                      ) : (
                        <>
                          {/* Lista de recordatorios */}
                          <div className="space-y-3">
                            {historialRecordatorios.map((rec) => {
                              const estilo = estadoStyles[rec.estado] || estadoStyles['Programado'];
                              const esProgramado = rec.estado === 'Programado';
                              const estaCancelando = cancelandoRecordatorio === rec.id;
                              
                              return (
                                <div 
                                  key={rec.id}
                                  className="bg-white rounded-xl border border-slate-200 p-4"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      {/* Fecha y estado */}
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-medium text-slate-700">
                                          {formatFechaHistorial(rec.fecha_programada)}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estilo.bg} ${estilo.text}`}>
                                          {rec.estado}
                                        </span>
                                      </div>
                                      
                                      {/* Observación */}
                                      <p className="text-sm text-slate-500 line-clamp-2">
                                        {textoDetalleConCanal(rec.observacion || 'Sin observación', rec.llamada, rec.mensaje)}
                                      </p>
                                    </div>
                                    
                                    {/* Botón cancelar */}
                                    <button
                                      onClick={() => esProgramado && setConfirmarCancelacion(rec)}
                                      disabled={!esProgramado || estaCancelando}
                                      className={`p-2 rounded-xl transition-all duration-200 flex-shrink-0 ${
                                        esProgramado && !estaCancelando
                                          ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50'
                                          : 'text-slate-200 cursor-not-allowed'
                                      }`}
                                      title={esProgramado ? 'Cancelar recordatorio' : 'No se puede cancelar'}
                                    >
                                      {estaCancelando ? (
                                        <Loader2 size={18} className="animate-spin" />
                                      ) : (
                                        <XCircle size={18} />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Paginación */}
                          {totalPagesHistorial > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                              <p className="text-xs text-slate-400">
                                {historialPage * HISTORIAL_PER_PAGE + 1} - {Math.min((historialPage + 1) * HISTORIAL_PER_PAGE, totalHistorial)} de {totalHistorial}
                              </p>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => fetchHistorialRecordatorios(historialPage - 1)}
                                  disabled={historialPage === 0}
                                  className={`p-2 rounded-lg transition-colors ${
                                    historialPage === 0 
                                      ? 'text-slate-300 cursor-not-allowed' 
                                      : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                                >
                                  <ChevronLeft size={16} />
                                </button>
                                <span className="text-xs text-slate-500">
                                  {historialPage + 1} / {totalPagesHistorial}
                                </span>
                                <button
                                  onClick={() => fetchHistorialRecordatorios(historialPage + 1)}
                                  disabled={historialPage >= totalPagesHistorial - 1}
                                  className={`p-2 rounded-lg transition-colors ${
                                    historialPage >= totalPagesHistorial - 1 
                                      ? 'text-slate-300 cursor-not-allowed' 
                                      : 'text-slate-600 hover:bg-slate-100'
                                  }`}
                                >
                                  <ChevronRight size={16} />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Contenido de Programar (existente) */}
                  {subTabRecordatorio === 'programar' && (
                  <div className="flex-1 overflow-y-auto">
                  
                  {/* Empty state si ya hay recordatorio activo */}
                  {loadingRecordatorioActivo ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 size={24} className="animate-spin text-slate-400" />
                    </div>
                  ) : recordatorioActivo ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-6">
                        <Bell size={36} className="text-amber-500" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-3">
                        {lead.nombre?.split(' ')[0] || 'Este lead'} ya tiene un recordatorio activo
                      </h3>
                      <p className="text-sm text-slate-500 mb-2">
                        Programado para el{' '}
                        <span className="font-semibold text-slate-700">
                          {(() => {
                            const fecha = new Date(recordatorioActivo.fecha_programada);
                            const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                            const dia = fecha.getDate();
                            const mes = meses[fecha.getMonth()];
                            const año = fecha.getFullYear();
                            let horas = fecha.getHours();
                            const minutos = fecha.getMinutes().toString().padStart(2, '0');
                            const ampm = horas >= 12 ? 'pm' : 'am';
                            horas = horas % 12 || 12;
                            return `${dia} de ${mes} de ${año} a las ${horas}:${minutos} ${ampm}`;
                          })()}
                        </span>
                      </p>
                      <p className="text-sm text-slate-400 mb-6">
                        Si deseas programar otro, ve al historial, cancélalo, y programa uno nuevo.
                      </p>
                      <button
                        onClick={() => setSubTabRecordatorio('historial')}
                        className="px-6 py-3 rounded-xl bg-[#1717AF] text-white font-semibold hover:bg-[#02214A] transition-all duration-200 shadow-lg shadow-[#1717AF]/20"
                      >
                        Ir al Historial
                      </button>
                    </div>
                  ) : (
                  <>
                  {/* Calendario */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4">
                    {/* Header del calendario */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1))}
                        className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                      >
                        <ChevronLeft size={20} className="text-slate-600" />
                      </button>
                      <h3 className="text-sm font-semibold text-slate-700">
                        {nombresMeses[mesActual.getMonth()]} {mesActual.getFullYear()}
                      </h3>
                      <button
                        onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1))}
                        className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                      >
                        <ChevronRight size={20} className="text-slate-600" />
                      </button>
                    </div>
                    
                    {/* Días de la semana */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {diasSemana.map(dia => (
                        <div key={dia} className="text-center text-xs font-medium text-slate-400 py-2">
                          {dia}
                        </div>
                      ))}
                    </div>
                    
                    {/* Días del mes */}
                    <div className="grid grid-cols-7 gap-1">
                      {/* Espacios vacíos */}
                      {Array.from({ length: startingDay }).map((_, i) => (
                        <div key={`empty-${i}`} className="h-9" />
                      ))}
                      
                      {/* Días */}
                      {Array.from({ length: daysInMonth }).map((_, i) => {
                        const day = i + 1;
                        const dateToCheck = new Date(mesActual.getFullYear(), mesActual.getMonth(), day);
                        const isValid = isDateValid(dateToCheck);
                        const isSelected = fechaSeleccionada && 
                          fechaSeleccionada.getDate() === day && 
                          fechaSeleccionada.getMonth() === mesActual.getMonth() &&
                          fechaSeleccionada.getFullYear() === mesActual.getFullYear();
                        const hasRec = hasRecordatorio(day);
                        const recordatorioDelDia = hasRec ? getRecordatorioForDay(day) : null;
                        const isToday = today.getDate() === day && 
                                       today.getMonth() === mesActual.getMonth() && 
                                       today.getFullYear() === mesActual.getFullYear();
                        
                        return (
                          <div key={day} className="relative group">
                            <button
                              onClick={() => isValid && setFechaSeleccionada(dateToCheck)}
                              disabled={!isValid}
                              className={`w-full h-9 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                                isSelected
                                  ? 'bg-[#1717AF] text-white shadow-lg shadow-[#1717AF]/20'
                                  : isToday
                                    ? 'bg-slate-100 text-slate-700 ring-2 ring-[#1717AF]/30'
                                    : isValid
                                      ? 'text-slate-700 hover:bg-slate-100'
                                      : 'text-slate-300 cursor-not-allowed'
                              }`}
                            >
                              {day}
                              {/* Indicador de recordatorio */}
                              {hasRec && (
                                <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
                                  isSelected ? 'bg-white' : 'bg-amber-500'
                                }`} />
                              )}
                            </button>
                            
                            {/* Tooltip para días con recordatorio */}
                            {hasRec && recordatorioDelDia && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-slate-800 text-white text-xs rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 shadow-xl">
                                <p className="text-slate-300 mb-1 text-[10px] uppercase tracking-wide">Recordatorio</p>
                                <p className="font-medium truncate">
                                  {recordatorioDelDia.observacion?.substring(0, 50)}
                                  {recordatorioDelDia.observacion?.length > 50 ? '...' : ''}
                                </p>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab('seguimiento');
                                  }}
                                  className="mt-2 text-white bg-[#1717AF] px-2 py-1 rounded-lg hover:bg-[#02214A] transition-colors pointer-events-auto text-[10px] font-medium"
                                >
                                  Ver Seguimiento →
                                </button>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Leyenda */}
                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        Con recordatorio
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="w-2 h-2 rounded-full bg-[#1717AF]" />
                        Seleccionado
                      </div>
                    </div>
                  </div>
                  
                  {/* Formulario de programación */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Bell size={16} className="text-[#1717AF]" />
                      Programar recordatorio
                    </h4>
                    
                    {/* Fecha y hora seleccionadas */}
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500 mb-1 block">Fecha</label>
                        <div className={`px-3 py-2 rounded-xl border text-sm ${
                          fechaSeleccionada 
                            ? 'border-[#1717AF] bg-[#1717AF]/5 text-slate-700' 
                            : 'border-slate-200 bg-slate-50 text-slate-400'
                        }`}>
                          {fechaSeleccionada 
                            ? fechaSeleccionada.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
                            : 'Selecciona en el calendario'
                          }
                        </div>
                      </div>
                      <div className="w-44">
                        <label className="text-xs text-slate-500 mb-1 block">Hora</label>
                        <div className="flex items-center gap-1">
                          {/* Selector de hora */}
                          <select
                            value={horaInput}
                            onChange={(e) => setHoraInput(e.target.value)}
                            className="w-14 px-1 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF]"
                          >
                            {Array.from({ length: 13 }, (_, i) => i).map((h) => (
                              <option key={h} value={h.toString().padStart(2, '0')}>
                                {h.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <span className="text-slate-400 font-medium">:</span>
                          {/* Selector de minutos */}
                          <select
                            value={minutoInput}
                            onChange={(e) => setMinutoInput(e.target.value)}
                            className="w-14 px-1 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF]"
                          >
                            {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                              <option key={m} value={m.toString().padStart(2, '0')}>
                                {m.toString().padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          {/* Selector AM/PM */}
                          <select
                            value={periodoInput}
                            onChange={(e) => setPeriodoInput(e.target.value)}
                            className="w-14 px-1 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF]"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dropdown de categoría con buscador */}
                    <div className="mb-3 relative categoria-dropdown">
                      <label className="text-xs text-slate-500 mb-1.5 block font-medium">Selecciona categoría del seguimiento (por defecto quedará "Otro")</label>
                      <button
                        type="button"
                        onClick={() => {
                          setCategoriaDropdownOpen(!categoriaDropdownOpen);
                          setCategoriaBusqueda('');
                          setSiglaDropdownOpen(null);
                        }}
                        disabled={loadingCategorias}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF] transition-all duration-200 flex items-center justify-between"
                      >
                        <span className={categoriaSeleccionada ? 'text-slate-700' : 'text-slate-400'}>
                          {loadingCategorias ? 'Cargando...' : (categoriaSeleccionada || 'Seleccionar categoría')}
                        </span>
                        <ChevronDown size={16} className={`text-slate-400 transition-transform ${categoriaDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Dropdown con buscador */}
                      {categoriaDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
                          {/* Input de búsqueda */}
                          <div className="p-2 border-b border-slate-100">
                            <div className="relative">
                              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input
                                ref={categoriaRecordatorioSearchRef}
                                type="text"
                                value={categoriaBusqueda}
                                onChange={(e) => setCategoriaBusqueda(e.target.value)}
                                placeholder="Buscar categoría..."
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1717AF]/30 focus:border-[#1717AF]"
                              />
                            </div>
                          </div>
                          
                          {/* Lista de opciones */}
                          <div className="max-h-40 overflow-y-auto">
                            {(() => {
                              const filtradas = sortCategorias(categorias).filter(cat => 
                                cat.categoria.toLowerCase().includes(categoriaBusqueda.toLowerCase())
                              );
                              
                              if (filtradas.length === 0) {
                                return <p className="px-3 py-2 text-xs text-slate-400 text-center">Sin resultados</p>;
                              }
                              
                              return filtradas.map((cat) => (
                                <button
                                  key={cat.id}
                                  type="button"
                                  onClick={() => {
                                    setCategoriaSeleccionada(cat.categoria);
                                    setCategoriaDropdownOpen(false);
                                    setCategoriaBusqueda('');
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 ${
                                    cat.categoria === categoriaSeleccionada 
                                      ? 'bg-[#1717AF]/5 text-[#1717AF] font-medium' 
                                      : 'text-slate-700'
                                  }`}
                                >
                                  {cat.categoria}
                                  {cat.categoria_padre && (
                                    <span className="block text-[10px] text-slate-400">
                                      {cat.categoria_padre}
                                    </span>
                                  )}
                                </button>
                              ));
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {renderCanalSiglas()}

                    {/* Textarea */}
                    <div className="mb-3">
                      <label className="text-xs text-slate-500 mb-1 block">Detalle del recordatorio</label>
                      <textarea
                        value={textoRecordatorio}
                        onChange={(e) => setTextoRecordatorio(e.target.value)}
                        placeholder="Escribe el detalle del recordatorio..."
                        rows={3}
                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1717AF]/20 focus:border-[#1717AF] resize-none"
                      />
                    </div>
                    
                    {/* Advertencia si la hora es muy pronto (solo para hoy) */}
                    {fechaSeleccionada && !timeIsValid && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-600 flex items-center gap-2">
                        <Clock size={16} />
                        La hora debe ser al menos 10 minutos en el futuro
                      </div>
                    )}
                    
                    {/* Error message */}
                    {errorRecordatorio && (
                      <div className="mb-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-600">
                        {errorRecordatorio}
                      </div>
                    )}
                    
                    {/* Botón programar */}
                    <button
                      onClick={handleProgramarRecordatorio}
                      disabled={!canSubmit}
                      className={`w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                        canSubmit
                          ? 'bg-[#1717AF] text-white hover:bg-[#02214A] shadow-lg shadow-[#1717AF]/20'
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {programandoRecordatorio ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Programando...
                        </>
                      ) : (
                        <>
                          <CalendarDays size={18} />
                          Programar
                        </>
                      )}
                    </button>
                    
                    {/* Nota sobre límite */}
                    <p className="text-xs text-slate-400 text-center mt-2">
                      Puedes programar desde hoy (mín. 10 min) hasta 6 meses en el futuro
                    </p>
                  </div>
                  </>
                  )}
                  </div>
                  )}
                </div>
              );
            })()}

            {activeTab === 'historial' && (
              <div className="h-full">
                {/* Sub-tabs para filtrar por tipo */}
                <div className="mb-4">
                  <div className="flex gap-2 border-b border-slate-200">
                    {['Gestión', 'Finalizado'].map((tipo) => {
                      // Contar fases únicas (agrupadas) por tipo
                      const fasesDelTipo = historialFases.filter(h => h.tipo === (tipo === 'Gestión' ? 'Gestión' : 'Finalizado'));
                      const fasesUnicas = [...new Set(fasesDelTipo.map(h => h.nombre_fase))];
                      const contador = fasesUnicas.length;
                      
                      return (
                        <button
                          key={tipo}
                          onClick={() => setHistorialFasesTab(tipo)}
                          className={`px-4 py-2.5 text-sm font-medium transition-all relative ${
                            historialFasesTab === tipo
                              ? 'text-[#1717AF]'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          Fase de {tipo.toLowerCase()} ({contador})
                          {historialFasesTab === tipo && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1717AF] rounded-full" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Loading state */}
                {loadingHistorialFases && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={24} className="animate-spin text-[#1717AF]" />
                  </div>
                )}

                {/* Empty state */}
                {!loadingHistorialFases && (() => {
                  const fasesDelTipo = historialFases.filter(h => h.tipo === historialFasesTab);
                  return fasesDelTipo.length === 0;
                })() && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                      <History size={28} className="text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Sin historial</h3>
                    <p className="text-sm text-slate-400">
                      No hay registros de {historialFasesTab === 'Gestión' ? 'fases de gestión' : 'fases de finalización'}
                    </p>
                  </div>
                )}

                {/* Historial agrupado */}
                {!loadingHistorialFases && (() => {
                  const fasesDelTipo = historialFases.filter(h => h.tipo === historialFasesTab);
                  if (fasesDelTipo.length === 0) return null;

                  // Agrupar por nombre_fase manteniendo el orden de primera aparición
                  const gruposOrdenados = [];
                  const gruposMap = {};
                  
                  fasesDelTipo.forEach(item => {
                    if (!gruposMap[item.nombre_fase]) {
                      gruposMap[item.nombre_fase] = [];
                      gruposOrdenados.push(item.nombre_fase);
                    }
                    gruposMap[item.nombre_fase].push(item);
                  });

                  return (
                    <div className="space-y-3">
                      {gruposOrdenados.map((nombreFase) => {
                        const items = gruposMap[nombreFase];
                        const count = items.length;
                        const isExpanded = historialFasesExpanded[nombreFase] || false;
                        const currentIndex = historialFasesPaginacion[nombreFase] || 0;
                        const currentItem = items[currentIndex];

                        // Formatear fecha del último item (más reciente) para el header (Enero 23/26)
                        const ultimoItem = items[items.length - 1];
                        const fechaHeader = (() => {
                          if (!ultimoItem?.created_at) return '';
                          const fecha = new Date(ultimoItem.created_at);
                          const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                          const mes = meses[fecha.getMonth()];
                          const dia = fecha.getDate().toString().padStart(2, '0');
                          const año = fecha.getFullYear().toString().slice(-2);
                          return `${mes} ${dia}/${año}`;
                        })();

                        return (
                          <div
                            key={nombreFase}
                            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                          >
                            {/* Header de la tarjeta */}
                            <button
                              onClick={() => setHistorialFasesExpanded(prev => ({
                                ...prev,
                                [nombreFase]: !prev[nombreFase]
                              }))}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-medium text-slate-800">
                                  {nombreFase} {count > 1 && <span className="text-slate-500">({count})</span>}
                                </span>
                                <span className="text-sm text-slate-500">{fechaHeader}</span>
                              </div>
                              <ChevronDown 
                                size={18} 
                                className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                              />
                            </button>

                            {/* Contenido expandido */}
                            {isExpanded && currentItem && (
                              <div className="px-4 pb-4 border-t border-slate-100">
                                <div className="pt-3 space-y-2">
                                  <div className="flex items-center gap-2 text-sm">
                                    <CalendarDays size={14} className="text-slate-400" />
                                    <span className="text-slate-600">
                                      <span className="font-medium">Fecha:</span>{' '}
                                      {new Date(currentItem.created_at).toLocaleDateString('es-ES', {
                                        day: '2-digit',
                                        month: 'long',
                                        year: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Clock size={14} className="text-slate-400" />
                                    <span className="text-slate-600">
                                      <span className="font-medium">Hora:</span>{' '}
                                      {new Date(currentItem.created_at).toLocaleTimeString('es-ES', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <RotateCcw size={14} className="text-slate-400" />
                                    <span className="text-slate-600">
                                      <span className="font-medium">Origen:</span>{' '}
                                      {currentItem.fase_anterior || 'Sin fase anterior'}
                                    </span>
                                  </div>
                                </div>

                                {/* Paginador horizontal */}
                                {count > 1 && (
                                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setHistorialFasesPaginacion(prev => ({
                                          ...prev,
                                          [nombreFase]: Math.max(0, currentIndex - 1)
                                        }));
                                      }}
                                      disabled={currentIndex === 0}
                                      className="p-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      <ChevronLeft size={16} className="text-slate-500" />
                                    </button>
                                    <span className="text-xs text-slate-500 font-medium">
                                      {'<'} {currentIndex + 1}/{count} {'>'}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setHistorialFasesPaginacion(prev => ({
                                          ...prev,
                                          [nombreFase]: Math.min(count - 1, currentIndex + 1)
                                        }));
                                      }}
                                      disabled={currentIndex === count - 1}
                                      className="p-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      <ChevronRight size={16} className="text-slate-500" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {activeTab === 'tici' && (() => {
              // Determinar el estado del tab TICI
              // Convertir fase_id_pipefy a string para comparación consistente
              const faseIdString = lead?.fase_id_pipefy?.toString();
              const faseAplica = FASES_RECORDATORIO_AUTOMATICO.includes(faseIdString);
              const fechaRecordatorio = lead?.fecha_recordatorio_automatico ? new Date(lead.fecha_recordatorio_automatico) : null;
              const ahora = new Date();
              const tieneRecordatorioProgramado = faseAplica && fechaRecordatorio && fechaRecordatorio >= ahora;
              
              // Formatear tiempo de activación
              const formatearTiempoActivacion = (segundos) => {
                if (!segundos) return '';
                const horas = Math.floor(segundos / 3600);
                if (horas >= 24) {
                  const dias = Math.floor(horas / 24);
                  const horasRestantes = horas % 24;
                  if (horasRestantes === 0) {
                    return `${dias} ${dias === 1 ? 'día' : 'días'}`;
                  }
                  return `${dias} ${dias === 1 ? 'día' : 'días'} y ${horasRestantes} ${horasRestantes === 1 ? 'hora' : 'horas'}`;
                }
                return `${horas} ${horas === 1 ? 'hora' : 'horas'}`;
              };

              // Formatear fecha del recordatorio
              const formatearFechaRecordatorio = (fecha) => {
                if (!fecha) return '';
                const f = new Date(fecha);
                const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                const dia = f.getDate();
                const mes = meses[f.getMonth()];
                const año = f.getFullYear();
                let horas = f.getHours();
                const minutos = f.getMinutes().toString().padStart(2, '0');
                const ampm = horas >= 12 ? 'pm' : 'am';
                horas = horas % 12 || 12;
                return `${dia} de ${mes} de ${año} a las ${horas}:${minutos} ${ampm}`;
              };

              // Formatear countdown
              const formatearCountdown = () => {
                const { dias, horas, minutos, segundos } = tiempoRestante;
                const partes = [];
                if (dias > 0) partes.push(`${dias} ${dias === 1 ? 'día' : 'días'}`);
                if (horas > 0) partes.push(`${horas} ${horas === 1 ? 'hora' : 'horas'}`);
                if (minutos > 0) partes.push(`${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`);
                partes.push(`${segundos} ${segundos === 1 ? 'segundo' : 'segundos'}`);
                return partes.join(', ');
              };

              // Determinar tipo de template
              const tipoTemplate = configRecordatorioAuto?.template?.startsWith('Correo') ? 'correo' : 'whatsapp';

              return (
                <div className="flex flex-col h-[calc(100vh-320px)] min-h-[400px]">
                  {/* Sub-tabs */}
                  <div className="flex gap-1 mb-4 flex-shrink-0 p-1 bg-slate-100 rounded-lg">
                    <button
                      onClick={() => setSubTabTici('programado')}
                      className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                        subTabTici === 'programado'
                          ? 'bg-white text-slate-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Programado
                    </button>
                    <button
                      onClick={() => setSubTabTici('historial')}
                      className={`flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-all duration-200 ${
                        subTabTici === 'historial'
                          ? 'bg-white text-slate-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Historial
                    </button>
                  </div>

                  {/* Contenido de Programado */}
                  {subTabTici === 'programado' && (
                    <div className="flex-1 overflow-y-auto">
                      {loadingConfigTici ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 size={24} className="animate-spin text-[#1717AF]" />
                        </div>
                      ) : !faseAplica ? (
                        // Estado 1: La fase no aplica
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                            <Sparkles size={32} className="text-slate-300" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">
                            Fase sin recordatorio automático
                          </h3>
                          <p className="text-sm text-slate-400 max-w-xs">
                            La fase actual del lead no tiene configurados recordatorios automáticos.
                          </p>
                        </div>
                      ) : !tieneRecordatorioProgramado ? (
                        // Estado 2: Sin recordatorio programado
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-20 h-20 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
                            <Clock size={32} className="text-amber-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">
                            Sin recordatorio programado
                          </h3>
                          <p className="text-sm text-slate-400 max-w-xs">
                            No hay un recordatorio automático activo para este lead en la fase actual.
                          </p>
                        </div>
                      ) : (
                        // Estado 3: Con recordatorio programado
                        <div className="space-y-4">
                          {/* Card principal */}
                          <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 p-5 space-y-4">
                            {/* Título y previsualizar */}
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <h4 className="font-semibold text-slate-800">
                                  Recordatorio automático #{lead?.numero_de_recordatorio_automatico || 1}
                                </h4>
                                <p className="text-sm text-slate-500">
                                  Etapa: {lead?.fase_nombre_pipefy || 'Sin etapa'}
                                </p>
                              </div>
                              
                              {/* Botón Previsualizar */}
                              <button
                                onClick={() => {
                                  setShowPreviewModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#1717AF] hover:bg-[#1717AF]/5 rounded-lg transition-colors"
                              >
                                Previsualizar
                                {tipoTemplate === 'correo' ? (
                                  <Mail size={16} className="text-[#1717AF]" />
                                ) : (
                                  <MessageCircle size={16} className="text-[#25D366]" />
                                )}
                              </button>
                            </div>

                            {/* Fecha de envío con countdown */}
                            <div className="bg-white rounded-xl p-4 border border-slate-100">
                              <div className="flex items-center gap-2 mb-2">
                                <CalendarDays size={16} className="text-[#1717AF]" />
                                <span className="text-sm font-medium text-slate-700">Se enviará el</span>
                              </div>
                              <p className="text-base font-semibold text-slate-800 mb-1">
                                {formatearFechaRecordatorio(lead?.fecha_recordatorio_automatico)}
                              </p>
                              <div className="flex items-center gap-2 text-sm">
                                <div className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full font-medium">
                                  {formatearCountdown()}
                                </div>
                              </div>
                            </div>

                            {/* Información detallada del recordatorio */}
                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-3">
                              {/* Regla */}
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Regla</p>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                  {lead?.numero_de_recordatorio_automatico === 1 ? (
                                    <>
                                      Este recordatorio automático se programó{' '}
                                      <span className="font-semibold text-slate-800">
                                        {formatearTiempoActivacion(configRecordatorioAuto?.tiempo_activacion)}
                                      </span>{' '}
                                      después de que el lead entró en la fase{' '}
                                      <span className="font-semibold text-slate-800">"{lead?.fase_nombre_pipefy}"</span>.
                                    </>
                                  ) : (
                                    <>
                                      Este recordatorio automático se programó{' '}
                                      <span className="font-semibold text-slate-800">
                                        {formatearTiempoActivacion(configRecordatorioAuto?.tiempo_activacion)}
                                      </span>{' '}
                                      después de que se envió el recordatorio{' '}
                                      <span className="font-semibold text-slate-800">#{lead?.numero_de_recordatorio_automatico - 1}</span>.
                                    </>
                                  )}
                                </p>
                              </div>
                              
                              {/* WhatsApp */}
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">No te preocupes</p>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                  Si el lead te escribe por WhatsApp antes de que se cumpla el recordatorio, este se reprograma automáticamente{' '}
                                  <span className="font-semibold text-slate-800">
                                    {formatearTiempoActivacion(configRecordatorioAuto?.tiempo_activacion)}
                                  </span>{' '}
                                  después de su último mensaje.
                                </p>
                              </div>
                            </div>

                            {/* Importante - Posponer */}
                            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Importante</p>
                              <p className="text-sm text-amber-800 leading-relaxed">
                                Si logras contactarlo por otro medio, selecciona "Posponer" y el recordatorio se moverá{' '}
                                <span className="font-semibold">
                                  {formatearTiempoActivacion(configRecordatorioAuto?.tiempo_activacion)}
                                </span>{' '}
                                desde el momento en que lo indiques.
                              </p>
                            </div>

                            {/* Botón Posponer */}
                            <button
                              onClick={handlePosponerRecordatorio}
                              disabled={posponiendo}
                              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#1717AF] to-indigo-600 text-white font-semibold hover:from-[#0f0f8a] hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {posponiendo ? (
                                <>
                                  <Loader2 size={18} className="animate-spin" />
                                  Posponiendo...
                                </>
                              ) : (
                                <>
                                  <RotateCcw size={18} />
                                  Posponer
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contenido de Historial TICI */}
                  {subTabTici === 'historial' && (
                    <div className="flex-1 overflow-y-auto">
                      {loadingHistorialTici ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 size={24} className="animate-spin text-[#1717AF]" />
                        </div>
                      ) : Object.values(historialTici).every(arr => arr.length === 0) ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                            <History size={28} className="text-slate-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-700 mb-2">Sin historial</h3>
                          <p className="text-sm text-slate-400">
                            No hay recordatorios automáticos enviados para este lead.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {(() => {
                            // Orden específico de fases para el historial
                            const ordenFases = {
                              'Sin contacto': 1,
                              'Gestionando': 2,
                              'Pendiente de agenda': 3
                            };
                            
                            return Object.keys(historialTici)
                              .filter(fase => historialTici[fase].length > 0)
                              .sort((a, b) => {
                                const ordenA = ordenFases[a] || 999;
                                const ordenB = ordenFases[b] || 999;
                                return ordenA - ordenB;
                              });
                          })().map(fase => {
                            const registros = historialTici[fase] || [];
                            const estaAbierto = acordeonTiciAbierto === fase;
                            const indiceActual = indiceTiciPorFase[fase] || 0;
                            const registroActual = registros[indiceActual];
                            const faseName = fase;
                            
                            return (
                              <div key={fase} className="border border-slate-200 rounded-xl overflow-hidden">
                                {/* Header del acordeón */}
                                <button
                                  onClick={() => setAcordeonTiciAbierto(estaAbierto ? null : fase)}
                                  className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                                    estaAbierto ? 'bg-slate-100' : 'bg-white hover:bg-slate-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-slate-700">{faseName}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      registros.length > 0 ? 'bg-[#1717AF]/10 text-[#1717AF]' : 'bg-slate-100 text-slate-400'
                                    }`}>
                                      {registros.length} {registros.length === 1 ? 'enviado' : 'enviados'}
                                    </span>
                                  </div>
                                  <ChevronDown 
                                    size={18} 
                                    className={`text-slate-400 transition-transform ${estaAbierto ? 'rotate-180' : ''}`} 
                                  />
                                </button>
                                
                                {/* Contenido del acordeón */}
                                {estaAbierto && registros.length > 0 && (
                                  <div className="p-4 border-t border-slate-100 bg-white">
                                    {/* Navegación horizontal */}
                                    {registros.length > 1 && (
                                      <div className="flex items-center justify-between mb-4">
                                        <button
                                          onClick={() => setIndiceTiciPorFase(prev => ({
                                            ...prev,
                                            [fase]: Math.max(0, indiceActual - 1)
                                          }))}
                                          disabled={indiceActual === 0}
                                          className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                          <ChevronLeft size={18} className="text-slate-600" />
                                        </button>
                                        <span className="text-xs text-slate-500">
                                          {indiceActual + 1} de {registros.length}
                                        </span>
                                        <button
                                          onClick={() => setIndiceTiciPorFase(prev => ({
                                            ...prev,
                                            [fase]: Math.min(registros.length - 1, indiceActual + 1)
                                          }))}
                                          disabled={indiceActual === registros.length - 1}
                                          className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                        >
                                          <ChevronRight size={18} className="text-slate-600" />
                                        </button>
                                      </div>
                                    )}
                                    
                                    {/* Card del registro */}
                                    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4 space-y-3">
                                      {/* Header con número y etapa */}
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <div className="w-8 h-8 rounded-lg bg-[#1717AF]/10 flex items-center justify-center">
                                            <Sparkles size={16} className="text-[#1717AF]" />
                                          </div>
                                          <div>
                                            <p className="text-sm font-semibold text-slate-800">
                                              Recordatorio automático #{registroActual?.numero}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                              Etapa: {registroActual?.fase}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Fecha de envío */}
                                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                                        <div className="flex items-center gap-2 mb-1">
                                          <CalendarDays size={14} className="text-green-600" />
                                          <span className="text-xs font-medium text-slate-600">Se envió el</span>
                                        </div>
                                        <p className="text-sm font-semibold text-slate-800">
                                          {(() => {
                                            if (!registroActual?.created_at) return 'Fecha no disponible';
                                            const fecha = new Date(registroActual.created_at);
                                            const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                                            const dia = fecha.getDate();
                                            const mes = meses[fecha.getMonth()];
                                            const año = fecha.getFullYear();
                                            let horas = fecha.getHours();
                                            const minutos = fecha.getMinutes().toString().padStart(2, '0');
                                            const ampm = horas >= 12 ? 'PM' : 'AM';
                                            horas = horas % 12 || 12;
                                            return `${dia} de ${mes} de ${año} a las ${horas}:${minutos} ${ampm}`;
                                          })()}
                                        </p>
                                      </div>
                                      
                                      {/* Regla */}
                                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Regla</p>
                                        <p className="text-sm text-slate-700 leading-relaxed">
                                          {registroActual?.numero === 1 ? (
                                            <>
                                              Este recordatorio automático se programó{' '}
                                              <span className="font-semibold text-slate-800">
                                                {formatearTiempoActivacion(registroActual?.tiempo_activacion)}
                                              </span>{' '}
                                              después de que el lead entró en la fase{' '}
                                              <span className="font-semibold text-slate-800">"{registroActual?.fase}"</span>.
                                            </>
                                          ) : (
                                            <>
                                              Este recordatorio automático se programó{' '}
                                              <span className="font-semibold text-slate-800">
                                                {formatearTiempoActivacion(registroActual?.tiempo_activacion)}
                                              </span>{' '}
                                              después de que se envió el recordatorio{' '}
                                              <span className="font-semibold text-slate-800">#{registroActual?.numero - 1}</span>.
                                            </>
                                          )}
                                        </p>
                                      </div>
                                      
                                      {/* Botón Previsualizar */}
                                      {registroActual?.preview && (
                                        <button
                                          onClick={() => setPreviewHistorialTici(registroActual)}
                                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-sm font-medium"
                                        >
                                          {registroActual?.template?.startsWith('Correo') ? (
                                            <Mail size={16} />
                                          ) : (
                                            <MessageCircle size={16} />
                                          )}
                                          Previsualizar
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

          </div>
        </div>
      </div>

      {/* Modal de previsualización TICI */}
      {showPreviewModal && configRecordatorioAuto && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setShowPreviewModal(false)}
          />
          <div className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-h-[85vh] ${
            configRecordatorioAuto.template?.startsWith('Correo') ? 'max-w-3xl' : 'max-w-lg'
          }`}>
            <div className="bg-white rounded-2xl shadow-2xl mx-4 overflow-hidden flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  {configRecordatorioAuto.template?.startsWith('Correo') ? (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Mail size={20} className="text-blue-600" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <MessageCircle size={20} className="text-green-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-800">Previsualización</h3>
                    <p className="text-xs text-slate-500">{configRecordatorioAuto.template}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-400" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {configRecordatorioAuto.template?.startsWith('Correo') ? (
                  <div 
                    className="email-preview-container"
                    style={{
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    <style>
                      {`
                        .email-preview-container table {
                          max-width: 100% !important;
                          width: 100% !important;
                        }
                        .email-preview-container img {
                          max-width: 100% !important;
                          height: auto !important;
                        }
                        .email-preview-container td,
                        .email-preview-container th {
                          word-break: break-word !important;
                        }
                        .email-preview-container .r0-o,
                        .email-preview-container [class*="r0-o"] {
                          width: 100% !important;
                          max-width: 100% !important;
                        }
                      `}
                    </style>
                    <div 
                      dangerouslySetInnerHTML={{ __html: configRecordatorioAuto.preview || '<p>Sin contenido disponible</p>' }}
                    />
                  </div>
                ) : (
                  <div className="bg-[#E5DDD5] rounded-xl p-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%]">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">
                        {configRecordatorioAuto.preview || 'Sin contenido disponible'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0">
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de previsualización TICI Historial */}
      {previewHistorialTici && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setPreviewHistorialTici(null)}
          />
          <div className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-h-[85vh] ${
            previewHistorialTici.template?.startsWith('Correo') ? 'max-w-3xl' : 'max-w-lg'
          }`}>
            <div className="bg-white rounded-2xl shadow-2xl mx-4 overflow-hidden flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  {previewHistorialTici.template?.startsWith('Correo') ? (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Mail size={20} className="text-blue-600" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <MessageCircle size={20} className="text-green-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-800">Previsualización</h3>
                    <p className="text-xs text-slate-500">{previewHistorialTici.template}</p>
                  </div>
                </div>
                <button
                  onClick={() => setPreviewHistorialTici(null)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-slate-400" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {previewHistorialTici.template?.startsWith('Correo') ? (
                  <div 
                    className="email-preview-container"
                    style={{
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    <style>
                      {`
                        .email-preview-container table {
                          max-width: 100% !important;
                          width: 100% !important;
                        }
                        .email-preview-container img {
                          max-width: 100% !important;
                          height: auto !important;
                        }
                        .email-preview-container td,
                        .email-preview-container th {
                          word-break: break-word !important;
                        }
                        .email-preview-container .r0-o,
                        .email-preview-container [class*="r0-o"] {
                          width: 100% !important;
                          max-width: 100% !important;
                        }
                      `}
                    </style>
                    <div 
                      dangerouslySetInnerHTML={{ __html: previewHistorialTici.preview || '<p>Sin contenido disponible</p>' }}
                    />
                  </div>
                ) : (
                  <div className="bg-[#E5DDD5] rounded-xl p-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%]">
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">
                        {previewHistorialTici.preview || 'Sin contenido disponible'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0">
                <button
                  onClick={() => setPreviewHistorialTici(null)}
                  className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de confirmación de recordatorio */}
      {mostrarConfirmacion && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setMostrarConfirmacion(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-sm">
            <div className="bg-white rounded-3xl shadow-2xl p-8 text-center mx-4">
              {/* Ícono de éxito */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-200">
                <CheckCircle2 size={40} className="text-white" />
              </div>
              
              {/* Título */}
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                ¡Felicitaciones!
              </h3>
              
              {/* Mensaje */}
              <p className="text-slate-600 mb-6">
                Tu recordatorio fue creado de forma exitosa para el{' '}
                <span className="font-semibold text-slate-800">
                  {fechaRecordatorioCreado && (() => {
                    const fecha = new Date(fechaRecordatorioCreado);
                    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                    const dia = fecha.getDate();
                    const mes = meses[fecha.getMonth()];
                    const año = fecha.getFullYear();
                    let horas = fecha.getHours();
                    const minutos = fecha.getMinutes().toString().padStart(2, '0');
                    const ampm = horas >= 12 ? 'pm' : 'am';
                    horas = horas % 12 || 12;
                    return `${dia} de ${mes} de ${año} a las ${horas}:${minutos} ${ampm}`;
                  })()}
                </span>
              </p>
              
              {/* Botón */}
              <button
                onClick={() => setMostrarConfirmacion(false)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg shadow-emerald-200"
              >
                ¡Entendido!
              </button>
            </div>
          </div>
        </>
      )}

      {/* Modal de confirmación para cancelar recordatorio */}
      {confirmarCancelacion && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setConfirmarCancelacion(null)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-sm">
            <div className="bg-white rounded-3xl shadow-2xl p-8 text-center mx-4">
              {/* Ícono de advertencia */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-200">
                <XCircle size={40} className="text-white" />
              </div>
              
              {/* Título */}
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                ¿Cancelar recordatorio?
              </h3>
              
              {/* Mensaje */}
              <p className="text-slate-600 mb-2">
                Estás a punto de cancelar el recordatorio programado para:
              </p>
              <p className="font-semibold text-slate-800 mb-6">
                {confirmarCancelacion.fecha_programada && (() => {
                  const fecha = new Date(confirmarCancelacion.fecha_programada);
                  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                  const dia = fecha.getDate();
                  const mes = meses[fecha.getMonth()];
                  const año = fecha.getFullYear();
                  let horas = fecha.getHours();
                  const minutos = fecha.getMinutes().toString().padStart(2, '0');
                  const ampm = horas >= 12 ? 'pm' : 'am';
                  horas = horas % 12 || 12;
                  return `${dia} de ${mes} de ${año} a las ${horas}:${minutos} ${ampm}`;
                })()}
              </p>
              
              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmarCancelacion(null)}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-all duration-200"
                >
                  No, volver
                </button>
                <button
                  onClick={() => handleCancelarRecordatorio(confirmarCancelacion.id)}
                  disabled={cancelandoRecordatorio === confirmarCancelacion.id}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-red-600 text-white font-semibold hover:from-rose-600 hover:to-red-700 transition-all duration-200 shadow-lg shadow-rose-200 flex items-center justify-center gap-2"
                >
                  {cancelandoRecordatorio === confirmarCancelacion.id ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Cancelando...
                    </>
                  ) : (
                    'Sí, cancelar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal de confirmación para reasignar lead */}
      {mostrarModalReasignar && comercialSeleccionado && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => {
              setMostrarModalReasignar(false);
              setComercialSeleccionado(null);
            }}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-md">
            <div className="bg-white rounded-3xl shadow-2xl p-8 text-center mx-4">
              {/* Ícono */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#1717AF] to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-200">
                <Users size={40} className="text-white" />
              </div>
              
              {/* Título */}
              <h3 className="text-xl font-bold text-slate-800 mb-3">
                ¿Reasignar lead?
              </h3>
              
              {/* Mensaje */}
              <p className="text-slate-600 mb-6">
                ¿Estás seguro que quieres reasignar el lead de{' '}
                <span className="font-semibold text-slate-800">{nombreComercialActual}</span>{' '}
                a{' '}
                <span className="font-semibold text-[#1717AF]">{comercialSeleccionado.nombre}</span>?
              </p>
              
              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setMostrarModalReasignar(false);
                    setComercialSeleccionado(null);
                  }}
                  disabled={reasignando}
                  className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarReasignacion}
                  disabled={reasignando}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#1717AF] to-indigo-600 text-white font-semibold hover:from-[#1414A0] hover:to-indigo-700 transition-all duration-200 shadow-lg shadow-indigo-200 flex items-center justify-center gap-2"
                >
                  {reasignando ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Reasignando...
                    </>
                  ) : (
                    'Sí, reasignar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal para ver texto completo */}
      {modalTextoCompleto && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[60]"
            onClick={() => setModalTextoCompleto(null)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white rounded-2xl shadow-2xl z-[61] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">{modalTextoCompleto.label}</h3>
              <button
                onClick={() => setModalTextoCompleto(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                {modalTextoCompleto.value}
              </p>
            </div>
            <button
              onClick={() => setModalTextoCompleto(null)}
              className="w-full mt-4 py-3 rounded-xl bg-[#1717AF] text-white font-medium hover:bg-[#1717AF]/90 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </>
      )}

      {/* Toast de confirmación */}
      {toastMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] animate-fade-in">
          <div className={`px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 ${
            toastMessage.includes('Error') 
              ? 'bg-rose-600 text-white' 
              : 'bg-emerald-600 text-white'
          }`}>
            {toastMessage.includes('Error') ? (
              <AlertCircle size={18} />
            ) : (
              <Check size={18} />
            )}
            <span className="text-sm font-medium">{toastMessage}</span>
          </div>
        </div>
      )}
      
      {/* Modal para crear lead en Respond.io */}
      <CrearRespondModal
        isOpen={crearRespondModalOpen}
        onClose={() => setCrearRespondModalOpen(false)}
        lead={lead}
        onSuccess={handleRespondSuccess}
      />
    </>
  );
};

export default LeadSidebar;


